import React, { useEffect, useMemo, useRef, useState } from 'react'
import { supabase } from '../../supabaseClient'
import Swal from 'sweetalert2'
import { Helmet } from 'react-helmet'
import { getAuthItem } from '../../utils/auth'
import { useBarista } from '../../contexts/BaristaContext'
import { authHeaders } from '../../utils/authFetch'
import { offlineDB } from '../../offline/db'
import { useNetworkStatus } from '../../offline/useNetworkStatus'
import {
    getBranchStaffContext,
    submitStaffCountOffline,
    getPendingStockCountTypesOffline,
    submitAdjustmentRequestOffline,
    getPendingAdjustmentRequestsOffline,
    cancelPendingAdjustmentRequestOffline,
} from '../../offline/posOfflineHelpers'

import { CashDrawerLogsCard, RemakeActivityCard } from './ShiftAttendance'

if (typeof document !== 'undefined' && !document.getElementById('shimmer-keyframes')) {
    const style = document.createElement('style')
    style.id = 'shimmer-keyframes'
    style.innerHTML = `
        @keyframes shimmer {
            0% { background-position: -400px 0; }
            100% { background-position: 400px 0; }
        }
    `
    document.head.appendChild(style)
}

if (typeof document !== 'undefined' && !document.getElementById('scrollbar-hide-styles')) {
    const style = document.createElement('style')
    style.id = 'scrollbar-hide-styles'
    style.innerHTML = `
            .scrollbar-hide::-webkit-scrollbar { display: none; }
            .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }
        `
    document.head.appendChild(style)
}

// ─── Design tokens ────────────────────────────────────────────────────
const C = {
    espresso: 'transparent',
    dark: '#F9FAFB',
    card: '#FFFFFF',
    border: '#E5E7EB',
    muted: '#9CA3AF',
    text: '#111827',
    textSoft: '#6B7280',
    amber: '#D97706',
    gold: '#B45309',
    teal: '#0D9488',
    coral: '#DC2626',
    blue: '#2563EB',
    green: '#16A34A',
    purple: '#7C3AED',
    indigo: '#4F46E5',
    nude: '#A0826D',
    midgrey: '#6B7280',
}

const SkeletonBlock = ({ className = '', style = {} }) => (
    <div
        className={`rounded ${className}`}
        style={{
            backgroundImage: 'linear-gradient(90deg, #e5e7eb 0px, #f3f4f6 40px, #e5e7eb 80px)',
            backgroundSize: '400px 100%',
            animation: 'shimmer 1.4s ease-in-out infinite',
            ...style,
        }}
    />
)

export default function InventoryManagement() {

    const isOnline = useNetworkStatus()

    const [loading, setLoading] = useState(true)
    const [activeTab, setActiveTab] = useState('overview')
    const [posSubTab, setPosSubTab] = useState('history')
    const [staffRecordId, setStaffRecordId] = useState(null)

    const { selectedBarista } = useBarista()
    const [staffRole, setStaffRole] = useState(null)

    const [showStaffCountModal, setShowStaffCountModal] = useState(false)
    const [staffCountItem, setStaffCountItem] = useState(null)
    const [staffCountInput, setStaffCountInput] = useState('')
    const [staffCountResult, setStaffCountResult] = useState(null)
    const [isSubmittingCount, setIsSubmittingCount] = useState(false)
    const [staffCountHistory, setStaffCountHistory] = useState([])
    const [staffCountType, setStaffCountType] = useState('beginning')
    const [showStockCountRecordsModal, setShowStockCountRecordsModal] = useState(false)
    const [stockCountRecordsItem, setStockCountRecordsItem] = useState(null)
    const [stockCountRecords, setStockCountRecords] = useState([])
    const [stockCountRecordsLoading, setStockCountRecordsLoading] = useState(false)
    const [todayCountTypes, setTodayCountTypes] = useState({})

    const [branchId, setBranchId] = useState(null)
    const [branchName, setBranchName] = useState('')

    const [branchMenuItems, setBranchMenuItems] = useState([])
    const [branchInventory, setBranchInventory] = useState([])
    const [archivedInventory, setArchivedInventory] = useState([])
    const [showArchivedModal, setShowArchivedModal] = useState(false)
    const [recipeReference, setRecipeReference] = useState([])
    const [inventorySearch, setInventorySearch] = useState('')
    const [inventoryStockStatusFilter, setInventoryStockStatusFilter] = useState('all')
    const [archivedInventorySearch, setArchivedInventorySearch] = useState('')
    const [archivedInventoryCategoryFilter, setArchivedInventoryCategoryFilter] = useState('All')
    const [archivedInventoryPage, setArchivedInventoryPage] = useState(1)

    // Pagination
    const [inventoryPage, setInventoryPage] = useState(1)
    const [recipesPage, setRecipesPage] = useState(1)
    const [transactionsPage, setTransactionsPage] = useState(1)
    const [posPage, setPosPage] = useState(1)
    const [menuItemsSearch, setMenuItemsSearch] = useState('')
    const [menuItemsPage, setMenuItemsPage] = useState(1)
    const [adjustmentRequestsPage, setAdjustmentRequestsPage] = useState(1)
    const [stockCountReviewPage, setStockCountReviewPage] = useState(1)
    const [restockRequestPage, setRestockRequestPage] = useState(1)
    const PAGE_SIZE = 10

    // Transactions
    const [inventoryTransactions, setInventoryTransactions] = useState([])
    const [posTransactions, setPosTransactions] = useState([])

    const [showAddStockModal, setShowAddStockModal] = useState(false)
    const [availableIngredients, setAvailableIngredients] = useState([])
    const [ingredientSearchQuery, setIngredientSearchQuery] = useState('')
    const [showIngredientDropdown, setShowIngredientDropdown] = useState(false)
    const ingredientDropdownRef = useRef(null)

    const [restockRequests, setRestockRequests] = useState([])
    const [restockRequestsLoading, setRestockRequestsLoading] = useState(false)
    const [restockQuantityInputs, setRestockQuantityInputs] = useState({})
    const [submittingRestockId, setSubmittingRestockId] = useState(null)
    const [restockFilter, setRestockFilter] = useState('all')
    const [showDownloadMenu, setShowDownloadMenu] = useState(false)
    const [pdfLoading, setPdfLoading] = useState(false)
    const [adjustmentPdfLoading, setAdjustmentPdfLoading] = useState(false)
    const [stockCountPdfLoading, setStockCountPdfLoading] = useState(false)
    const [restockPdfLoading, setRestockPdfLoading] = useState(false)
    const downloadMenuRef = useRef(null)
    const inventoryTableRef = useRef(null)
    const tabsScrollRef = useRef(null)
    const [isDraggingTabs, setIsDraggingTabs] = useState(false)
    const tabsDragStartXRef = useRef(0)
    const tabsScrollStartRef = useRef(0)

    const [showStockCountDownloadMenu, setShowStockCountDownloadMenu] = useState(false)
    const [showRestockDownloadMenu, setShowRestockDownloadMenu] = useState(false)
    const [showAdjustmentDownloadMenu, setShowAdjustmentDownloadMenu] = useState(false)
    const stockCountDownloadMenuRef = useRef(null)
    const restockDownloadMenuRef = useRef(null)
    const adjustmentDownloadMenuRef = useRef(null)

    const [posTransactionSearch, setPosTransactionSearch] = useState('')
    const [recipeSearch, setRecipeSearch] = useState('')
    const [recipeCategoryFilter, setRecipeCategoryFilter] = useState('All')
    const [recipeClassificationFilter, setRecipeClassificationFilter] = useState('All')
    const [availableRecipeClassifications, setAvailableRecipeClassifications] = useState([])
    const [transactionSearch, setTransactionSearch] = useState('')

    const [inventoryDateFilter, setInventoryDateFilter] = useState('all')
    const [posDateFilter, setPosDateFilter] = useState('all')
    const [posOrderStatusFilter, setPosOrderStatusFilter] = useState('all')
    const [inventoryCustomStart, setInventoryCustomStart] = useState('')
    const [inventoryCustomEnd, setInventoryCustomEnd] = useState('')
    const [posCustomStart, setPosCustomStart] = useState('')
    const [posCustomEnd, setPosCustomEnd] = useState('')

    const [initialAddStockFormData, setInitialAddStockFormData] = useState(null)

    const [addStockFormData, setAddStockFormData] = useState({
        ingredient_id: '',
        input_mode: 'manual',
        current_stock: '',
        package_count: '',
        reorder_level: ''
    })

    const [showAddIngredientModal, setShowAddIngredientModal] = useState(false)
    const [isAddingNewIngredient, setIsAddingNewIngredient] = useState(false)
    const [newIngredientFormData, setNewIngredientFormData] = useState({
        name: '', unit: '', category: '', package_label: '',
        stock_per_package: '', package_price: '', current_stock: '', reorder_level: ''
    })

    const NEW_INGREDIENT_FIELD_LIMITS = { name: 100, unit: 20, category: 50, package_label: 50 }

    const [inventorySubTab, setInventorySubTab] = useState('stock_list') // 'stock_list' | 'stock_count_review'
    const [stockCountReview, setStockCountReview] = useState([])
    const [stockCountReviewLoading, setStockCountReviewLoading] = useState(false)
    const [stockCountReviewDate, setStockCountReviewDate] = useState(
        new Date().toISOString().split('T')[0]
    )

    const [showAdjustStockModal, setShowAdjustStockModal] = useState(false)
    const [showEODModal, setShowEODModal] = useState(false)
    const [eodTime, setEodTime] = useState('')
    const [isSavingEOD, setIsSavingEOD] = useState(false)
    const [selectedInventoryItem, setSelectedInventoryItem] = useState(null)
    const [initialStockFormData, setInitialStockFormData] = useState(null)

    const [showAdjustmentRequestModal, setShowAdjustmentRequestModal] = useState(false)
    const [adjustmentRequestItem, setAdjustmentRequestItem] = useState(null)
    const [adjustmentRequests, setAdjustmentRequests] = useState([])
    const [adjustmentRequestsLoading, setAdjustmentRequestsLoading] = useState(false)
    const [adjustmentRequestForm, setAdjustmentRequestForm] = useState({
        adjustment_type: 'deduct',
        input_mode: 'manual',
        quantity: '',
        amount_per_package: '',
        package_count: '',
        reason: 'spillage',
        notes: ''
    })
    const [isSubmittingAdjustmentRequest, setIsSubmittingAdjustmentRequest] = useState(false)
    const [editingAdjustmentRequestId, setEditingAdjustmentRequestId] = useState(null)

    const [stockFormData, setStockFormData] = useState({
        mode: 'add',
        input_mode: 'manual',
        quantity: '',
        amount_per_package: '',
        package_count: '',
        reorder_level: ''
    })

    const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080'
    const API = `${BASE_URL}/api/inventory`

    useEffect(() => {
        initializeBranchData()
    }, [isOnline])

    useEffect(() => {
        setInventoryPage(1)
    }, [inventorySearch, inventoryStockStatusFilter])

    useEffect(() => {
        setMenuItemsPage(1)
    }, [menuItemsSearch])

    useEffect(() => {
        if (inventorySubTab === 'stock_count_review' && staffRole === 'Admin' && branchId) {
            fetchStockCountReview(stockCountReviewDate)
        }
    }, [inventorySubTab, stockCountReviewDate, branchId, staffRole])

    useEffect(() => {
        if (inventorySubTab === 'restock_request' && staffRole === 'Admin' && branchId) {
            fetchRestockRequests()
        }
        if (inventorySubTab === 'adjustment_requests' && (staffRole === 'Admin' || staffRole === 'Barista') && branchId) {
            fetchAdjustmentRequests()
        }
    }, [inventorySubTab, branchId, staffRole])

    useEffect(() => {
        const handleSyncComplete = () => { if (branchId) fetchAdjustmentRequests() }
        window.addEventListener('kaperk:sync-complete', handleSyncComplete)
        return () => window.removeEventListener('kaperk:sync-complete', handleSyncComplete)
    }, [branchId])

    useEffect(() => {
        if (!branchId) return
        const refreshPOSTransactions = () => fetchPOSTransactions(branchId)
        window.addEventListener('kaperk:orders-changed', refreshPOSTransactions)
        window.addEventListener('kaperk:sync-complete', refreshPOSTransactions)
        window.addEventListener('kaperk:order-created', refreshPOSTransactions)
        return () => {
            window.removeEventListener('kaperk:orders-changed', refreshPOSTransactions)
            window.removeEventListener('kaperk:sync-complete', refreshPOSTransactions)
            window.removeEventListener('kaperk:order-created', refreshPOSTransactions)
        }
    }, [branchId])

    useEffect(() => {
        setAdjustmentRequestsPage(1)
    }, [inventorySubTab, adjustmentRequests.length])

    useEffect(() => {
        setStockCountReviewPage(1)
    }, [stockCountReviewDate, stockCountReview.length])

    useEffect(() => {
        setRestockRequestPage(1)
    }, [restockFilter])

    useEffect(() => {
        setRecipesPage(1)
    }, [recipeSearch, recipeCategoryFilter, recipeClassificationFilter])

    useEffect(() => {
        setTransactionsPage(1)
    }, [transactionSearch, inventoryDateFilter, inventoryCustomStart, inventoryCustomEnd])

    useEffect(() => {
        setPosPage(1)
    }, [posTransactionSearch, posDateFilter, posOrderStatusFilter, posCustomStart, posCustomEnd])

    useEffect(() => {
        setArchivedInventoryPage(1)
    }, [archivedInventorySearch, archivedInventoryCategoryFilter])

    useEffect(() => {
        if (!branchId) return

        const channel = supabase
            .channel(`branch-inventory-${branchId}`)
            .on(
                'postgres_changes',
                {
                    event: '*',
                    schema: 'inventory',
                    table: 'stock_reservations',
                    filter: `branch_id=eq.${branchId}`
                },
                () => {
                    fetchBranchInventory(branchId)
                }
            )
            .on(
                'postgres_changes',
                {
                    event: '*',
                    schema: 'inventory',
                    table: 'branch_inventory',
                    filter: `branch_id=eq.${branchId}`
                },
                () => {
                    fetchBranchInventory(branchId)
                }
            )
            .subscribe()

        return () => {
            supabase.removeChannel(channel)
        }
    }, [branchId])

    useEffect(() => {
        const pending = sessionStorage.getItem('pendingInventoryTab')
        if (pending) {
            if (pending === 'remake_activity') {
                setActiveTab('remake_activity')
            } else {
                setActiveTab('inventory')
                setInventorySubTab(pending)
            }
            sessionStorage.removeItem('pendingInventoryTab')
        }

        const handleNavigateToSubTab = (e) => {
            const tab = e.detail?.tab
            if (tab === 'remake_activity') {
                setActiveTab('remake_activity')
            } else {
                setActiveTab('inventory')
                if (tab) setInventorySubTab(tab)
            }
            sessionStorage.removeItem('pendingInventoryTab')
        }
        window.addEventListener('kaperk:navigate-inventory', handleNavigateToSubTab)
        return () => window.removeEventListener('kaperk:navigate-inventory', handleNavigateToSubTab)
    }, [])

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                ingredientDropdownRef.current &&
                !ingredientDropdownRef.current.contains(event.target)
            ) {
                setShowIngredientDropdown(false)
            }
            if (
                downloadMenuRef.current &&
                !downloadMenuRef.current.contains(event.target)
            ) {
                setShowDownloadMenu(false)
            }
            if (
                stockCountDownloadMenuRef.current &&
                !stockCountDownloadMenuRef.current.contains(event.target)
            ) {
                setShowStockCountDownloadMenu(false)
            }
            if (
                restockDownloadMenuRef.current &&
                !restockDownloadMenuRef.current.contains(event.target)
            ) {
                setShowRestockDownloadMenu(false)
            }
            if (
                adjustmentDownloadMenuRef.current &&
                !adjustmentDownloadMenuRef.current.contains(event.target)
            ) {
                setShowAdjustmentDownloadMenu(false)
            }
        }
        document.addEventListener('mousedown', handleClickOutside)
        return () => {
            document.removeEventListener('mousedown', handleClickOutside)
        }
    }, [])

    const initializeBranchData = async () => {
        try {
            setLoading(true)

            const ctx = await getBranchStaffContext(isOnline)

            const currentBranchId = ctx.branch_id
            const currentBranchName = ctx.branch_name || 'Assigned Branch'

            setBranchId(currentBranchId)
            setStaffRole(ctx.role || null)
            setBranchName(currentBranchName)
            setStaffRecordId(ctx.staff_record_id ?? ctx.id) // getBranchStaffContext returns `id`, not `staff_record_id`
            const savedEodTime = ctx.eod_report_time || localStorage.getItem(`eodTime_${currentBranchId}`)
            if (savedEodTime) setEodTime(savedEodTime)

            // Instant paint from cache (unchanged)
            const [cachedStock, cachedRecipes] = await Promise.all([
                offlineDB.invStockListCache.where('branch_id').equals(currentBranchId).toArray(),
                offlineDB.invRecipeRefCache.where('branch_id').equals(currentBranchId).toArray(),
            ])
            if (cachedStock.length) setBranchInventory(cachedStock)
            if (cachedRecipes.length) setRecipeReference(cachedRecipes)
            if (cachedStock.length || cachedRecipes.length) setLoading(false)

            if (isOnline) {
                await Promise.all([
                    fetchBranchMenuItems(currentBranchId),
                    fetchBranchInventory(currentBranchId),
                    fetchArchivedInventory(currentBranchId),
                    fetchRecipeReference(currentBranchId),
                    fetchInventoryTransactions(currentBranchId, ctx.role || null),
                    fetchPOSTransactions(currentBranchId)
                ])
                await fetchTodayCountTypes(currentBranchId)
                if (ctx.role === 'Admin') await fetchAdjustmentRequests(currentBranchId)
            } else {
                await fetchBranchInventory(currentBranchId)
                await fetchRecipeReference(currentBranchId)
                await fetchBranchMenuItems(currentBranchId)
                await fetchInventoryTransactions(currentBranchId, ctx.role || null)
                await fetchPOSTransactions(currentBranchId)
            }

        } catch (err) {
            console.error('Error initializing branch inventory:', err)
            Swal.fire({
                icon: 'error',
                title: 'Failed to Load Branch Inventory',
                text: err.message || 'An error occurred while loading branch data.',
                confirmButtonColor: '#A0826D'
            })
        } finally {
            setLoading(false)
        }
    }

    const fetchBranchMenuItems = async (currentBranchId) => {
        if (!isOnline) {
            const cached = await offlineDB.invMenuItemsCache.where('branch_id').equals(currentBranchId).toArray()
            setBranchMenuItems(cached.filter(item => !item.is_archived))
            return
        }
        const res = await fetch(`${API}/${currentBranchId}/menu-items`, { headers: authHeaders() })
        if (!res.ok) throw new Error('Failed to fetch branch menu items')
        const data = await res.json()
        const activeData = (data || []).filter(item => !item.is_archived)
        setBranchMenuItems(activeData)
        offlineDB.invMenuItemsCache.where('branch_id').equals(currentBranchId).delete()
            .then(() => offlineDB.invMenuItemsCache.bulkPut(data.map(i => ({ ...i, branch_id: currentBranchId }))))
            .catch(err => console.error('Cache write failed:', err))
    }

    // const fetchBranchInventory = async (currentBranchId) => {
    //     try {
    //         const res = await fetch(`${API}/${currentBranchId}/stock`, { headers: authHeaders() })
    //         if (!res.ok) throw new Error('Failed to fetch branch inventory')
    //         setBranchInventory(await res.json())
    //     } catch (err) {
    //         console.error('Error fetching branch inventory:', err)
    //         setBranchInventory([])
    //     }
    // }

    const fetchBranchInventory = async (currentBranchId) => {
        if (!isOnline) {
            const cached = await offlineDB.invStockListCache.where('branch_id').equals(currentBranchId).toArray()
            setBranchInventory(cached)
            return
        }
        const res = await fetch(`${API}/${currentBranchId}/stock`, { headers: authHeaders() })
        if (!res.ok) throw new Error('Failed to fetch branch inventory')
        const data = await res.json()
        setBranchInventory(data)
        offlineDB.invStockListCache.where('branch_id').equals(currentBranchId).delete()
            .then(() => offlineDB.invStockListCache.bulkPut(data.map(i => ({ ...i, branch_id: currentBranchId }))))
            .catch(err => console.error('Cache write failed:', err))
    }

    const fetchArchivedInventory = async (currentBranchId) => {
        try {
            const res = await fetch(`${API}/${currentBranchId}/stock/archived`, { headers: authHeaders() })
            if (!res.ok) throw new Error('Failed to fetch archived inventory')
            setArchivedInventory(await res.json())
        } catch (err) {
            console.error('Error fetching archived inventory:', err)
            setArchivedInventory([])
        }
    }

    const fetchAvailableIngredients = async (currentBranchId) => {
        try {
            const res = await fetch(`${API}/${currentBranchId}/ingredients/available`, { headers: authHeaders() })
            if (!res.ok) throw new Error('Failed to fetch available ingredients')
            setAvailableIngredients(await res.json())
        } catch (err) {
            console.error('Error fetching available ingredients:', err)
            Swal.fire({
                icon: 'error',
                title: 'Failed to Load Ingredients',
                text: err.message || 'Unable to load ingredients for adding stock.',
                confirmButtonColor: '#A0826D'
            })
        }
    }

    const fetchStockCountReview = async (reviewDate) => {
        if (!branchId) return
        setStockCountReviewLoading(true)
        try {
            const res = await fetch(`${API}/${branchId}/stock-count/review?date=${reviewDate}`, { headers: authHeaders() })
            if (!res.ok) throw new Error('Failed to fetch stock count review')
            const review = await res.json()
            setStockCountReview(review)
            return review
        } catch (err) {
            console.error('Error fetching stock count review:', err)
            Swal.fire({ icon: 'error', title: 'Failed to Load Review', text: err.message, confirmButtonColor: '#A0826D' })
        } finally {
            setStockCountReviewLoading(false)
        }
    }

    const fetchAdjustmentRequests = async (targetBranchId = branchId) => {
        if (!targetBranchId) return
        setAdjustmentRequestsLoading(true)
        try {
            const offlinePending = await getPendingAdjustmentRequestsOffline(targetBranchId, branchInventory)
            if (!isOnline) {
                setAdjustmentRequests(prev => [...offlinePending, ...prev.filter(r => !r.isOffline)])
                return
            }
            const res = await fetch(`${API}/${targetBranchId}/adjustment-requests`, { headers: authHeaders() })
            if (!res.ok) throw new Error('Failed to fetch adjustment requests')
            const serverRequests = await res.json()
            setAdjustmentRequests([...offlinePending, ...serverRequests])
        } catch (err) {
            console.error('Error fetching adjustment requests:', err)
        } finally {
            setAdjustmentRequestsLoading(false)
        }
    }

    const handleSubmitAdjustmentRequest = async () => {
        if (!adjustmentRequestItem) return

        if (staffRole === 'Barista' && !selectedBarista) {
            Swal.fire({
                icon: 'warning',
                title: 'Barista Identity Required',
                text: 'Please select your barista profile before submitting a request.',
                confirmButtonColor: '#A0826D'
            })
            return
        }

        const qty = adjustmentRequestForm.input_mode === 'package'
            ? parseFloat(adjustmentRequestForm.amount_per_package || 0) * parseFloat(adjustmentRequestForm.package_count || 0)
            : parseFloat(adjustmentRequestForm.quantity)

        if (isNaN(qty) || qty <= 0) {
            Swal.fire({ icon: 'warning', title: 'Invalid Quantity', text: 'Please enter a valid quantity greater than 0.', confirmButtonColor: '#A0826D' })
            return
        }

        setIsSubmittingAdjustmentRequest(true)
        try {

            if (!isOnline) {
                if (editingAdjustmentRequestId && String(editingAdjustmentRequestId).startsWith('local_')) {
                    await cancelPendingAdjustmentRequestOffline(Number(String(editingAdjustmentRequestId).replace('local_', '')))
                }
                await submitAdjustmentRequestOffline({
                    branchId, ingredientId: adjustmentRequestItem.ingredient_id,
                    adjustmentType: adjustmentRequestForm.adjustment_type,
                    quantity: qty, reason: adjustmentRequestForm.reason,
                    notes: adjustmentRequestForm.notes || null,
                    staffRecordId, baristaId: selectedBarista?.barista_id ?? null,
                })

                setShowAdjustmentRequestModal(false)
                setAdjustmentRequestItem(null)
                setEditingAdjustmentRequestId(null)
                setAdjustmentRequestForm({ adjustment_type: 'deduct', input_mode: 'manual', quantity: '', amount_per_package: '', package_count: '', reason: 'spillage', notes: '' })
                await fetchAdjustmentRequests()
                Swal.fire({ icon: 'info', title: 'Request Queued Offline', text: 'No connection — this will be sent to the admin once wifi returns.', confirmButtonColor: '#A0826D', timer: 2500 })
                return
            }

            if (editingAdjustmentRequestId) {
                const res = await fetch(`${API}/adjustment-requests/${editingAdjustmentRequestId}`, {
                    method: 'PATCH',
                    headers: { 'Content-Type': 'application/json', ...authHeaders() },
                    body: JSON.stringify({
                        adjustmentType: adjustmentRequestForm.adjustment_type,
                        quantity: qty,
                        reason: adjustmentRequestForm.reason,
                        notes: adjustmentRequestForm.notes || null
                    })
                })
                const body = await res.json()
                if (!res.ok) throw new Error(body.error || 'Failed to update')

                setShowAdjustmentRequestModal(false)
                setAdjustmentRequestItem(null)
                setEditingAdjustmentRequestId(null)
                setAdjustmentRequestForm({ adjustment_type: 'deduct', input_mode: 'manual', quantity: '', amount_per_package: '', package_count: '', reason: 'spillage', notes: '' })

                await fetchAdjustmentRequests()

                Swal.fire({
                    icon: 'success',
                    title: 'Request Updated',
                    text: 'Your adjustment request has been updated.',
                    confirmButtonColor: '#A0826D',
                    timer: 2000
                })
                return
            }

            const res = await fetch(`${API}/${branchId}/adjustment-requests`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...authHeaders() },
                body: JSON.stringify({
                    ingredientId: adjustmentRequestItem.ingredient_id,
                    adjustmentType: adjustmentRequestForm.adjustment_type,
                    quantity: qty,
                    reason: adjustmentRequestForm.reason,
                    notes: adjustmentRequestForm.notes || null,
                    staffRecordId,
                    baristaId: selectedBarista?.barista_id ?? null,
                }),
            })
            const body = await res.json()
            if (!res.ok) throw new Error(body.error || 'Failed to submit')

            setShowAdjustmentRequestModal(false)
            setAdjustmentRequestItem(null)
            setAdjustmentRequestForm({ adjustment_type: 'deduct', input_mode: 'manual', quantity: '', amount_per_package: '', package_count: '', reason: 'spillage', notes: '' })

            await fetchAdjustmentRequests()

            Swal.fire({
                icon: 'success',
                title: 'Request Submitted',
                text: 'Your adjustment request has been sent to the admin for review.',
                confirmButtonColor: '#A0826D',
                timer: 2000
            })
        } catch (err) {
            Swal.fire({ icon: 'error', title: 'Failed to Submit', text: err.message, confirmButtonColor: '#A0826D' })
        } finally {
            setIsSubmittingAdjustmentRequest(false)
        }
    }

    const handleApproveAdjustmentRequest = async (req) => {
        const result = await Swal.fire({
            icon: 'question',
            title: 'Approve Adjustment?',
            html: `<b>${req.adjustment_type === 'add' ? 'Add' : 'Deduct'} ${req.quantity} ${req.ingredient_unit}</b> from <b>${req.ingredient_name}</b>?<br/><span class="text-sm text-gray-500">Reason: ${req.reason}</span>`,
            showCancelButton: true,
            confirmButtonText: 'Approve & Apply',
            cancelButtonText: 'Cancel',
            confirmButtonColor: '#A0826D',
            cancelButtonColor: '#6b7280'
        })
        if (!result.isConfirmed) return

        try {
            const res = await fetch(`${API}/adjustment-requests/${req.id}/approve`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...authHeaders() },
                body: JSON.stringify({
                    branchId,
                    staffRecordId,
                    createdByAuthId: getAuthItem('staffId')
                })
            })
            const body = await res.json()
            if (!res.ok) {
                if (res.status === 400) {
                    Swal.fire({ icon: 'warning', title: 'Insufficient Stock', text: body.error, confirmButtonColor: '#A0826D' })
                    return
                }
                throw new Error(body.error || 'Failed to approve request')
            }

            await Promise.all([fetchBranchInventory(branchId), fetchAdjustmentRequests(), fetchInventoryTransactions(branchId)])
            Swal.fire({ icon: 'success', title: 'Adjustment Applied', confirmButtonColor: '#A0826D', timer: 2000 })
        } catch (err) {
            Swal.fire({ icon: 'error', title: 'Failed', text: err.message, confirmButtonColor: '#A0826D' })
        }
    }

    const handleRejectAdjustmentRequest = async (req) => {
        const result = await Swal.fire({
            icon: 'warning',
            title: 'Reject Request?',
            text: `Reject the adjustment request for "${req.ingredient_name}"?`,
            showCancelButton: true,
            confirmButtonText: 'Yes, Reject',
            cancelButtonText: 'Keep It',
            confirmButtonColor: '#dc2626',
            cancelButtonColor: '#A0826D'
        })
        if (!result.isConfirmed) return

        try {
            const res = await fetch(`${API}/adjustment-requests/${req.id}/reject`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...authHeaders() },
                body: JSON.stringify({ staffRecordId })
            })
            const body = await res.json()
            if (!res.ok) throw new Error(body.error || 'Failed to reject request')

            await fetchAdjustmentRequests()
            Swal.fire({ icon: 'success', title: 'Request Rejected', confirmButtonColor: '#A0826D', timer: 1500 })
        } catch (err) {
            Swal.fire({ icon: 'error', title: 'Failed', text: err.message, confirmButtonColor: '#A0826D' })
        }
    }

    const handleEditAdjustmentRequest = (req) => {
        const inventoryItem = branchInventory.find(i => i.ingredient_id === req.ingredient_id)
        setAdjustmentRequestItem(inventoryItem || {
            ingredient_id: req.ingredient_id,
            name: req.ingredient_name,
            unit: req.ingredient_unit
        })
        setAdjustmentRequestForm({
            adjustment_type: req.adjustment_type,
            input_mode: 'manual',
            quantity: String(req.quantity),
            amount_per_package: '',
            package_count: '',
            reason: req.reason,
            notes: req.notes || ''
        })
        setEditingAdjustmentRequestId(req.id)
        setShowAdjustmentRequestModal(true)
    }

    const handleCancelMyAdjustmentRequest = async (req) => {
        const result = await Swal.fire({
            icon: 'warning',
            title: 'Cancel Request?',
            text: `Cancel your adjustment request for "${req.ingredient_name}"?`,
            showCancelButton: true,
            confirmButtonText: 'Yes, Cancel',
            cancelButtonText: 'Keep It',
            confirmButtonColor: '#dc2626',
            cancelButtonColor: '#A0826D'
        })
        if (!result.isConfirmed) return

        if (req.isOffline) {
            await cancelPendingAdjustmentRequestOffline(req.localId)
            await fetchAdjustmentRequests()
            Swal.fire({ icon: 'success', title: 'Request Cancelled', confirmButtonColor: '#A0826D', timer: 1500 })
            return
        }

        try {
            const res = await fetch(`${API}/adjustment-requests/${req.id}`, {
                method: 'DELETE',
                headers: authHeaders()
            })
            const body = await res.json()
            if (!res.ok) throw new Error(body.error || 'Failed to cancel')

            await fetchAdjustmentRequests()
            Swal.fire({ icon: 'success', title: 'Request Cancelled', confirmButtonColor: '#A0826D', timer: 1500 })
        } catch (err) {
            Swal.fire({ icon: 'error', title: 'Failed to Cancel', text: err.message, confirmButtonColor: '#A0826D' })
        }
    }

    const fetchRestockRequests = async () => {
        if (!branchId) return
        setRestockRequestsLoading(true)
        try {
            const res = await fetch(`${API}/${branchId}/restock-requests`, { headers: authHeaders() })
            if (!res.ok) throw new Error('Failed to fetch restock requests')
            setRestockRequests(await res.json())
        } catch (err) {
            console.error('Error fetching restock requests:', err)
        } finally {
            setRestockRequestsLoading(false)
        }
    }

    const fetchStockCountRecords = async (ingredientId, reviewDate) => {
        setStockCountRecordsLoading(true)
        try {
            const res = await fetch(
                `${API}/${branchId}/stock-count/records?ingredientId=${ingredientId}&date=${reviewDate}`,
                { headers: authHeaders() }
            )
            if (!res.ok) throw new Error('Failed to fetch stock count records')
            setStockCountRecords(await res.json())
        } catch (err) {
            console.error('Error fetching stock count records:', err)
        } finally {
            setStockCountRecordsLoading(false)
        }
    }

    const handleSubmitRestockRequest = async (item) => {
        const qty = parseFloat(restockQuantityInputs[item.ingredient_id] || 0)
        if (!qty || qty < item.reorder_level) {
            Swal.fire({
                icon: 'warning',
                title: 'Invalid Quantity',
                text: `Requested quantity must be at least the reorder level (${item.reorder_level} ${item.unit}).`,
                confirmButtonColor: '#A0826D'
            })
            return
        }

        setSubmittingRestockId(item.ingredient_id)
        try {
            const res = await fetch(`${API}/${branchId}/restock-requests`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...authHeaders() },
                body: JSON.stringify({
                    ingredientId: item.ingredient_id,
                    requestedQuantity: qty,
                    reorderLevel: item.reorder_level,
                    currentStock: item.current_stock,
                    staffRecordId
                })
            })
            const body = await res.json()
            if (!res.ok) throw new Error(body.error || 'Failed to submit')

            await fetchRestockRequests()
            setRestockQuantityInputs(prev => ({ ...prev, [item.ingredient_id]: '' }))

            Swal.fire({
                icon: 'success',
                title: 'Restock Request Submitted',
                text: `Your request for ${item.name} has been sent to the super admin.`,
                confirmButtonColor: '#A0826D',
                timer: 2000
            })
        } catch (err) {
            Swal.fire({ icon: 'error', title: 'Failed to Submit Request', text: err.message, confirmButtonColor: '#A0826D' })
        } finally {
            setSubmittingRestockId(null)
        }
    }

    const handleCancelRestockRequest = async (requestId, ingredientName) => {
        const result = await Swal.fire({
            icon: 'warning',
            title: 'Cancel Request?',
            text: `Cancel the pending restock request for "${ingredientName}"?`,
            showCancelButton: true,
            confirmButtonText: 'Yes, Cancel',
            cancelButtonText: 'Keep It',
            confirmButtonColor: '#dc2626',
            cancelButtonColor: '#A0826D'
        })
        if (!result.isConfirmed) return

        try {
            const res = await fetch(`${API}/restock-requests/${requestId}`, {
                method: 'DELETE',
                headers: authHeaders()
            })
            const body = await res.json()
            if (!res.ok) throw new Error(body.error || 'Failed to cancel')

            await fetchRestockRequests()
            Swal.fire({ icon: 'success', title: 'Request Cancelled', confirmButtonColor: '#A0826D', timer: 1500 })
        } catch (err) {
            Swal.fire({ icon: 'error', title: 'Failed to Cancel', text: err.message, confirmButtonColor: '#A0826D' })
        }
    }

    const handleMarkFulfilled = async (requestId, ingredientName) => {
        const result = await Swal.fire({
            icon: 'question',
            title: 'Confirm Stock Received?',
            text: `Mark the restock for "${ingredientName}" as received? Make sure to manually adjust your stock levels.`,
            showCancelButton: true,
            confirmButtonText: 'Yes, Mark as Received',
            cancelButtonText: 'Cancel',
            confirmButtonColor: '#A0826D',
            cancelButtonColor: '#6b7280'
        })
        if (!result.isConfirmed) return

        try {
            const res = await fetch(`${API}/restock-requests/${requestId}/fulfill`, {
                method: 'POST',
                headers: authHeaders()
            })
            const body = await res.json()
            if (!res.ok) throw new Error(body.error || 'Failed to mark fulfilled')

            await fetchRestockRequests()
            Swal.fire({
                icon: 'success',
                title: 'Marked as Received',
                text: 'Remember to adjust your stock levels through the Stock List tab.',
                confirmButtonColor: '#A0826D',
                timer: 2500
            })
        } catch (err) {
            Swal.fire({ icon: 'error', title: 'Failed', text: err.message, confirmButtonColor: '#A0826D' })
        }
    }

    // const fetchRecipeReference = async (currentBranchId) => {
    //     const res = await fetch(`${API}/${currentBranchId}/recipes`, { headers: authHeaders() })
    //     if (!res.ok) throw new Error('Failed to fetch recipe reference')
    //     setRecipeReference(await res.json())
    // }

    const fetchRecipeReference = async (currentBranchId) => {
        if (!isOnline) {
            const cached = await offlineDB.invRecipeRefCache.where('branch_id').equals(currentBranchId).toArray()
            setRecipeReference(cached.filter(item => !item.is_archived))
            return
        }
        const res = await fetch(`${API}/${currentBranchId}/recipes`, { headers: authHeaders() })
        if (!res.ok) throw new Error('Failed to fetch recipe reference')
        const data = await res.json()
        const activeData = (data || []).filter(item => !item.is_archived)
        setRecipeReference(activeData)
        offlineDB.invRecipeRefCache.where('branch_id').equals(currentBranchId).delete()
            .then(() => offlineDB.invRecipeRefCache.bulkPut(data.map(i => ({ ...i, branch_id: currentBranchId }))))
            .catch(err => console.error('Cache write failed:', err))
    }

    const fetchInventoryTransactions = async (currentBranchId, role = staffRole) => {
        if (!isOnline) {
            const cached = await offlineDB.invTransactionsCache.where('branch_id').equals(currentBranchId).toArray()
            setInventoryTransactions(cached.sort((a, b) => new Date(b.created_at) - new Date(a.created_at)))
            return
        }
        const params = new URLSearchParams()
        if (role) params.set('role', role)
        const res = await fetch(`${API}/${currentBranchId}/transactions?${params.toString()}`, { headers: authHeaders() })
        if (!res.ok) throw new Error('Failed to fetch inventory transactions')
        const data = await res.json()
        setInventoryTransactions(data)
        offlineDB.invTransactionsCache.where('branch_id').equals(currentBranchId).delete()
            .then(() => offlineDB.invTransactionsCache.bulkPut(data.map(t => ({ ...t, branch_id: currentBranchId }))))
            .catch(err => console.error('Cache write failed:', err))
    }

    const fetchPOSTransactions = async (currentBranchId) => {
        if (!isOnline) {
            // Rows from the last successful online fetch (covers older orders)
            const cachedFromApi = await offlineDB.posTransactionsCache.where('branch_id').equals(currentBranchId).toArray()

            // Orders sitting in ordersCache (incl. ones just created offline) —
            // same source OrderManagement/TransactionPrinter read from, so build
            // the same shape here instead of waiting for posTransactionsCache.
            const localOrders = (await offlineDB.ordersCache.toArray())
                .filter(o => o.branch_id === currentBranchId && o.order_source === 'pos')

            const localRows = []
            for (const order of localOrders) {
                const txCache = await offlineDB.orderTransactionsCache.get(order.order_id)
                const transactions = txCache?.transactions || []
                for (const t of transactions) {
                    localRows.push({
                        order_transaction_id: `local_${order.order_id}_${t.payment_method}_${t.created_at || order.created_at}`,
                        branch_id: order.branch_id,
                        reference_number: order.reference_number || 'N/A',
                        payment_method: t.payment_method || 'N/A',
                        transaction_amount: Number(t.transaction_amount || 0),
                        payment_status: t.status || 'N/A',
                        order_status: order.order_status || 'N/A',
                        created_at: t.created_at || order.created_at,
                    })
                }
            }

            // Prefer local rows (fresher / reflects latest status); fill in the
            // rest from the API cache for anything not currently in ordersCache.
            const localRefs = new Set(localRows.map(r => r.reference_number))
            const merged = [...localRows, ...cachedFromApi.filter(r => !localRefs.has(r.reference_number))]

            setPosTransactions(merged.sort((a, b) => new Date(b.created_at) - new Date(a.created_at)))
            return
        }
        try {
            const res = await fetch(`${API}/${currentBranchId}/pos-transactions`, { headers: authHeaders() })
            if (!res.ok) throw new Error('Failed to fetch POS transactions')
            const data = await res.json()
            setPosTransactions(data)
            offlineDB.posTransactionsCache.where('branch_id').equals(currentBranchId).delete()
                .then(() => offlineDB.posTransactionsCache.bulkPut(data.map(t => ({ ...t, branch_id: currentBranchId }))))
                .catch(err => console.error('Cache write failed:', err))
        } catch (err) {
            console.error('Error fetching POS transactions:', err)
            const cached = await offlineDB.posTransactionsCache.where('branch_id').equals(currentBranchId).toArray()
            setPosTransactions(cached.sort((a, b) => new Date(b.created_at) - new Date(a.created_at)))
        }
    }

    // const fetchReservedStockMap = async (currentBranchId) => {
    //     const { data, error } = await supabase
    //         .schema('inventory')
    //         .from('stock_reservations')
    //         .select('ingredient_id, reserved_quantity, status')
    //         .eq('branch_id', currentBranchId)
    //         .eq('status', 'reserved')

    //     if (error) throw error

    //     const reservationMap = new Map()

    //     for (const row of data || []) {
    //         const ingredientId = row.ingredient_id
    //         const currentReserved = reservationMap.get(ingredientId) || 0
    //         reservationMap.set(
    //             ingredientId,
    //             currentReserved + Number(row.reserved_quantity || 0)
    //         )
    //     }

    //     return reservationMap
    // }

    // === CRUD ─────────────────────────────────────────────────────────────────

    const handleAddNewIngredient = async () => {
        if (!branchId) return
        const name = newIngredientFormData.name.trim()
        const unit = newIngredientFormData.unit.trim()

        if (!name || !unit) {
            Swal.fire({ icon: 'warning', title: 'Missing Information', text: 'Please fill in ingredient name and unit.', confirmButtonColor: '#A0826D' })
            return
        }

        const stockPerPackage = parseFloat(newIngredientFormData.stock_per_package)
        if (newIngredientFormData.stock_per_package !== '' && (isNaN(stockPerPackage) || stockPerPackage < 0)) {
            Swal.fire({ icon: 'warning', title: 'Invalid Quantity per Package', text: 'Must be 0 or greater.', confirmButtonColor: '#A0826D' })
            return
        }

        const packagePrice = parseFloat(newIngredientFormData.package_price)
        if (newIngredientFormData.package_price !== '' && (isNaN(packagePrice) || packagePrice < 0)) {
            Swal.fire({ icon: 'warning', title: 'Invalid Package Price', text: 'Must be 0 or greater.', confirmButtonColor: '#A0826D' })
            return
        }

        const currentStock = parseFloat(newIngredientFormData.current_stock || 0)
        if (isNaN(currentStock) || currentStock < 0) {
            Swal.fire({ icon: 'warning', title: 'Invalid Starting Stock', text: 'Must be 0 or greater.', confirmButtonColor: '#A0826D' })
            return
        }

        const reorderLevel = parseFloat(newIngredientFormData.reorder_level || 0)
        if (isNaN(reorderLevel) || reorderLevel < 0) {
            Swal.fire({ icon: 'warning', title: 'Invalid Reorder Level', text: 'Must be 0 or greater.', confirmButtonColor: '#A0826D' })
            return
        }

        setIsAddingNewIngredient(true)
        try {
            const res = await fetch(`${API}/${branchId}/ingredients`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...authHeaders() },
                body: JSON.stringify({
                    name,
                    unit,
                    category: newIngredientFormData.category.trim() || null,
                    package_label: newIngredientFormData.package_label.trim() || null,
                    stock_per_package: newIngredientFormData.stock_per_package !== '' ? stockPerPackage : null,
                    package_price: newIngredientFormData.package_price !== '' ? packagePrice : null,
                    current_stock: currentStock,
                    reorder_level: reorderLevel,
                    staffRecordId,
                    baristaId: selectedBarista?.barista_id ?? null,
                    createdByAuthId: getAuthItem('staffId')
                })
            })
            const body = await res.json()
            if (!res.ok) {
                if (res.status === 409) {
                    Swal.fire({ icon: 'warning', title: 'Ingredient Already Exists', text: body.error, confirmButtonColor: '#A0826D' })
                    return
                }
                throw new Error(body.error || 'Failed to create ingredient')
            }

            setShowAddIngredientModal(false)
            setNewIngredientFormData({ name: '', unit: '', category: '', package_label: '', stock_per_package: '', package_price: '', current_stock: '', reorder_level: '' })

            await fetchBranchInventory(branchId)
            await fetchInventoryTransactions(branchId)

            Swal.fire({
                icon: 'success',
                title: 'Ingredient Created',
                text: `"${body.name || name}" has been created and added to your branch inventory.`,
                confirmButtonColor: '#A0826D',
                timer: 2500
            })
        } catch (err) {
            console.error('Error creating new ingredient:', err)
            Swal.fire({ icon: 'error', title: 'Failed to Create Ingredient', text: err.message, confirmButtonColor: '#A0826D' })
        } finally {
            setIsAddingNewIngredient(false)
        }
    }

    const handleAddStockItem = async () => {
        try {
            if (!branchId) return
            if (!hasAddStockChanges) return

            const parsedReorderLevel = parseFloat(addStockFormData.reorder_level)
            const selectedIngredientForCalc = availableIngredients.find(
                i => String(i.ingredient_id) === String(addStockFormData.ingredient_id)
            )
            const parsedCurrentStock =
                addStockFormData.input_mode === 'package'
                    ? parseFloat(addStockFormData.package_count || 0) * Number(selectedIngredientForCalc?.stock_per_package || 0)
                    : parseFloat(addStockFormData.current_stock)

            if (!addStockFormData.ingredient_id) {
                Swal.fire({ icon: 'warning', title: 'Ingredient Required', text: 'Please select an ingredient to add.', confirmButtonColor: '#A0826D' })
                return
            }

            const alreadyExists = branchInventory.some(
                item => String(item.ingredient_id) === String(addStockFormData.ingredient_id)
            )
            if (alreadyExists) {
                Swal.fire({ icon: 'warning', title: 'Duplicate Ingredient', text: 'This ingredient is already being tracked in this branch inventory.', confirmButtonColor: '#A0826D' })
                return
            }

            if (isNaN(parsedCurrentStock) || parsedCurrentStock < 0) {
                Swal.fire({ icon: 'warning', title: 'Invalid Stock', text: 'Current stock must be 0 or greater.', confirmButtonColor: '#A0826D' })
                return
            }

            if (isNaN(parsedReorderLevel) || parsedReorderLevel < 0) {
                Swal.fire({ icon: 'warning', title: 'Invalid Reorder Level', text: 'Reorder level must be 0 or greater.', confirmButtonColor: '#A0826D' })
                return
            }

            const selectedIngredient = availableIngredients.find(
                ingredient => String(ingredient.ingredient_id) === String(addStockFormData.ingredient_id)
            )

            const res = await fetch(`${API}/${branchId}/stock`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...authHeaders() },
                body: JSON.stringify({
                    ingredientId: addStockFormData.ingredient_id,
                    currentStock: parsedCurrentStock,
                    reorderLevel: parsedReorderLevel,
                    unit: selectedIngredient?.unit || '',
                    staffRecordId,
                    baristaId: selectedBarista?.barista_id ?? null,
                    createdByAuthId: getAuthItem('staffId')
                })
            })
            const body = await res.json()
            if (!res.ok) {
                if (res.status === 409) {
                    Swal.fire({ icon: 'warning', title: 'Duplicate Ingredient', text: body.error, confirmButtonColor: '#A0826D' })
                    return
                }
                throw new Error(body.error || 'Failed to add ingredient')
            }

            setShowAddStockModal(false)
            setAddStockFormData({ ingredient_id: '', input_mode: 'manual', current_stock: '', package_count: '', reorder_level: '' })

            await fetchBranchInventory(branchId)
            await fetchArchivedInventory(branchId)
            await fetchInventoryTransactions(branchId)

            Swal.fire({
                icon: 'success',
                title: `${selectedIngredient?.name || 'Ingredient'} Added`,
                text: `${selectedIngredient?.name || 'Ingredient'} is now tracked in this branch inventory.`,
                confirmButtonColor: '#A0826D',
                timer: 2000
            })
        } catch (err) {
            console.error('Error adding branch stock item:', err)
            Swal.fire({
                icon: 'error',
                title: 'Failed to Add Ingredient',
                text: err.message || 'An error occurred while adding the ingredient.',
                confirmButtonColor: '#A0826D'
            })
        }
    }

    // Archive instead of hard delete
    const handleArchiveStockItem = async (item) => {
        const result = await Swal.fire({
            icon: 'warning',
            title: 'Archive Stock Item?',
            text: `Are you sure you want to archive "${item.name}" from this branch inventory? You can reinstate it later.`,
            showCancelButton: true,
            confirmButtonText: 'Archive',
            cancelButtonText: 'Cancel',
            confirmButtonColor: '#dc2626',
            cancelButtonColor: '#A0826D'
        })
        if (!result.isConfirmed) return

        try {
            const res = await fetch(`${API}/${branchId}/stock/${item.id}/archive`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json', ...authHeaders() },
                body: JSON.stringify({
                    staffRecordId,
                    baristaId: selectedBarista?.barista_id ?? null,
                    createdByAuthId: getAuthItem('staffId')
                })
            })
            const body = await res.json()
            if (!res.ok) throw new Error(body.error || 'Failed to archive')

            await fetchBranchInventory(branchId)
            await fetchArchivedInventory(branchId)
            await fetchInventoryTransactions(branchId)

            Swal.fire({
                icon: 'success',
                title: `${item.name} Archived`,
                text: `${item.name} has been archived. You can reinstate it from the archived list.`,
                confirmButtonColor: '#A0826D',
                timer: 2500
            })
        } catch (err) {
            console.error('Error archiving stock item:', err)
            Swal.fire({ icon: 'error', title: 'Failed to Archive', text: err.message || 'An error occurred while archiving the stock item.', confirmButtonColor: '#A0826D' })
        }
    }

    const handleReinstateStockItem = async (item) => {
        const result = await Swal.fire({
            icon: 'question',
            title: 'Reinstate Stock Item?',
            text: `Reinstate "${item.name}" back to the active branch inventory?`,
            showCancelButton: true,
            confirmButtonText: 'Reinstate',
            cancelButtonText: 'Cancel',
            confirmButtonColor: '#A0826D',
            cancelButtonColor: '#6b7280'
        })
        if (!result.isConfirmed) return

        try {
            const res = await fetch(`${API}/${branchId}/stock/${item.id}/reinstate`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json', ...authHeaders() },
                body: JSON.stringify({
                    staffRecordId,
                    baristaId: selectedBarista?.barista_id ?? null,
                    createdByAuthId: getAuthItem('staffId')
                })
            })
            const body = await res.json()
            if (!res.ok) throw new Error(body.error || 'Failed to reinstate')

            await fetchBranchInventory(branchId)
            await fetchArchivedInventory(branchId)
            await fetchInventoryTransactions(branchId)

            Swal.fire({
                icon: 'success',
                title: `${item.name} Reinstated`,
                text: `${item.name} is now active in the branch inventory.`,
                confirmButtonColor: '#A0826D',
                timer: 2000
            })
        } catch (err) {
            console.error('Error reinstating stock item:', err)
            Swal.fire({ icon: 'error', title: 'Failed to Reinstate', text: err.message || 'An error occurred while reinstating the stock item.', confirmButtonColor: '#A0826D' })
        }
    }

    const downloadInventoryExcel = async (reviewDataOverride = null) => {
        let reviewData = reviewDataOverride || stockCountReview
        if (!reviewDataOverride && stockCountReview.length === 0 && branchId) {
            reviewData = await fetchStockCountReview(stockCountReviewDate)
        }

        if (!window.ExcelJS) {
            await new Promise((resolve, reject) => {
                const script = document.createElement('script')
                script.src = 'https://cdnjs.cloudflare.com/ajax/libs/exceljs/4.4.0/exceljs.min.js'
                script.onload = resolve
                script.onerror = reject
                document.head.appendChild(script)
            })
        }

        const wb = new window.ExcelJS.Workbook()
        wb.creator = 'Coffee Gus·to'
        wb.created = new Date()

        // ── Brand palette (matches Coffee Gus·to nude theme) ──
        const BRAND_NUDE = 'FFA0826D'
        const BRAND_GREEN = 'FF16A34A'
        const BRAND_AMBER = 'FFD97706'
        const BRAND_RED = 'FFDC2626'
        const BRAND_BLUE = 'FF2563EB'
        const BRAND_PURPLE = 'FF7C3AED'
        const HEADER_BG = 'FF111827'
        const ALT_ROW_BG = 'FFF9FAFB'
        const TOTAL_BG = 'FFF3E8E1'
        const WHITE = 'FFFFFFFF'

        const headerStyle = (bgColor = HEADER_BG) => ({
            font: { name: 'Arial', bold: true, color: { argb: WHITE }, size: 10 },
            fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: bgColor } },
            alignment: { horizontal: 'center', vertical: 'middle', wrapText: true },
            border: { bottom: { style: 'medium', color: { argb: BRAND_NUDE } } },
        })

        const dataStyle = (numFmt, color = 'FF111827', bold = false, align = 'right') => ({
            font: { name: 'Arial', size: 10, color: { argb: color }, bold },
            alignment: { horizontal: align, vertical: 'middle' },
            numFmt,
        })

        const labelStyle = {
            font: { name: 'Arial', size: 10, color: { argb: 'FF374151' } },
            alignment: { horizontal: 'left', vertical: 'middle' },
        }

        const totalRowStyle = (numFmt) => ({
            font: { name: 'Arial', bold: true, size: 10, color: { argb: 'FF8C705B' } },
            fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: TOTAL_BG } },
            alignment: { horizontal: 'right', vertical: 'middle' },
            border: { top: { style: 'thin', color: { argb: BRAND_NUDE } } },
            numFmt,
        })

        const addSheetTitle = (ws, title, subtitle, colCount) => {
            ws.mergeCells(1, 1, 1, colCount)
            const titleCell = ws.getCell('A1')
            titleCell.value = `Coffee Gus·to — ${title}`
            titleCell.font = { name: 'Arial', bold: true, size: 14, color: { argb: WHITE } }
            titleCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: HEADER_BG } }
            titleCell.alignment = { horizontal: 'left', vertical: 'middle', indent: 1 }
            ws.getRow(1).height = 32

            ws.mergeCells(2, 1, 2, colCount)
            const subCell = ws.getCell('A2')
            subCell.value = subtitle
            subCell.font = { name: 'Arial', italic: true, size: 9, color: { argb: 'FF6B7280' } }
            subCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF3F4F6' } }
            subCell.alignment = { horizontal: 'left', vertical: 'middle', indent: 1 }
            ws.getRow(2).height = 18
        }


        const now = new Date().toLocaleString('en-PH', { dateStyle: 'long', timeStyle: 'short' })
        const esc = (v) => {
            const s = String(v ?? '')
            return s.includes(',') || s.includes('"') || s.includes('\n')
                ? `"${s.replace(/"/g, '""')}"` : s
        }

        const applyAltRows = (ws, startRow, endRow, colCount, skipCols = []) => {
            for (let r = startRow; r <= endRow; r++) {
                if (r % 2 === 0) {
                    for (let c = 1; c <= colCount; c++) {
                        if (skipCols.includes(c)) continue
                        ws.getCell(r, c).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: ALT_ROW_BG } }
                    }
                }
            }
        }

        const exportDate = new Date().toISOString().split('T')[0]

        // ── INVENTORY SHEET ──
        const ws = wb.addWorksheet('Inventory')
        ws.columns = [
            { width: 24 }, { width: 14 }, { width: 12 }, { width: 12 }, { width: 12 },
            { width: 13 }, { width: 13 }, { width: 20 },
            { width: 13 }, { width: 11 }, { width: 11 }, { width: 12 }, { width: 12 }, { width: 12 }, { width: 18 },
        ]
        addSheetTitle(ws, 'Inventory Report', `Branch: ${branchName} · Generated ${now}`, 15)

        const headerLabels = [
            'Ingredient', 'Category', 'On Hand', 'Reserved', 'Available', 'Status', 'Reorder Level', 'Last Updated',
            'Beg. Stock', 'Added', 'Deducted', 'System Qty', 'Staff Count', 'Variance', 'Counted By'
        ]
        const hr = ws.getRow(4)
        hr.values = headerLabels
        hr.height = 26
        headerLabels.forEach((_, i) => Object.assign(hr.getCell(i + 1), headerStyle()))

        branchInventory.forEach((item, i) => {
            const r = ws.getRow(5 + i)
            r.height = 18

            const isOut = item.available_stock <= 0
            const isLow = item.available_stock > 0 && item.available_stock <= item.reorder_level
            const statusLabel = isOut ? 'Out of Stock' : isLow ? 'Low Stock' : 'In Stock'
            const statusColor = isOut ? BRAND_RED : isLow ? BRAND_AMBER : BRAND_GREEN

            const countRow = reviewData.find(row => row.ingredient_id === item.ingredient_id)
            const variance = countRow?.variance ?? null
            const varianceColor = variance == null ? 'FF9CA3AF' : variance === 0 ? BRAND_GREEN : variance > 0 ? BRAND_BLUE : BRAND_RED

            r.getCell(1).value = item.name; Object.assign(r.getCell(1), labelStyle)
            r.getCell(2).value = item.category; Object.assign(r.getCell(2), labelStyle)
            r.getCell(3).value = `${item.current_stock} ${item.unit}`; Object.assign(r.getCell(3), dataStyle('@', 'FF111827'))
            r.getCell(4).value = `${item.reserved_stock || 0} ${item.unit}`; Object.assign(r.getCell(4), dataStyle('@', 'FF9CA3AF'))
            r.getCell(5).value = `${item.available_stock} ${item.unit}`; Object.assign(r.getCell(5), dataStyle('@', statusColor, true))
            r.getCell(6).value = statusLabel; Object.assign(r.getCell(6), dataStyle('@', statusColor, true, 'center'))
            r.getCell(7).value = `${item.reorder_level} ${item.unit}`; Object.assign(r.getCell(7), dataStyle('@', 'FF6B7280'))
            r.getCell(8).value = item.last_updated
                ? new Date(item.last_updated).toLocaleString('en-PH', { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
                : 'N/A'
            Object.assign(r.getCell(8), { ...labelStyle, font: { ...labelStyle.font, size: 9, color: { argb: 'FF9CA3AF' } } })

            r.getCell(9).value = countRow != null ? `${countRow.beginning_stock} ${item.unit}` : '—'
            Object.assign(r.getCell(9), dataStyle('@', 'FF6B7280'))
            r.getCell(10).value = countRow != null ? `+${countRow.added_stock}` : '—'
            Object.assign(r.getCell(10), dataStyle('@', BRAND_GREEN))
            r.getCell(11).value = countRow != null ? `-${countRow.deducted_stock}` : '—'
            Object.assign(r.getCell(11), dataStyle('@', BRAND_AMBER))
            r.getCell(12).value = countRow != null ? `${countRow.system_quantity} ${item.unit}` : '—'
            Object.assign(r.getCell(12), dataStyle('@', BRAND_BLUE))
            r.getCell(13).value = countRow?.staff_count != null ? `${countRow.staff_count} ${item.unit}` : '—'
            Object.assign(r.getCell(13), dataStyle('@', BRAND_PURPLE, true))
            r.getCell(14).value = variance != null ? `${variance > 0 ? '+' : ''}${variance} ${item.unit}` : '—'
            Object.assign(r.getCell(14), dataStyle('@', varianceColor, true))
            r.getCell(15).value = countRow?.counted_by || '—'
            Object.assign(r.getCell(15), { ...labelStyle, font: { ...labelStyle.font, size: 9 } })
        })

        const lastRow = 4 + branchInventory.length
        applyAltRows(ws, 5, lastRow, 15)

        // ── Summary block ──
        const inStock = branchInventory.filter(i => i.available_stock > i.reorder_level).length
        const lowStock = branchInventory.filter(i => i.available_stock > 0 && i.available_stock <= i.reorder_level).length
        const outOfStock = branchInventory.filter(i => i.available_stock <= 0).length
        const withVariance = branchInventory.filter(i => {
            const c = reviewData.find(r => r.ingredient_id === i.ingredient_id)
            return c?.variance != null && c.variance !== 0
        }).length

        const summaryStart = lastRow + 3
        ws.mergeCells(summaryStart, 1, summaryStart, 3)
        const miniTitle = ws.getCell(summaryStart, 1)
        miniTitle.value = 'SUMMARY'
        miniTitle.font = { name: 'Arial', bold: true, size: 9, color: { argb: WHITE } }
        miniTitle.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: BRAND_NUDE } }
        miniTitle.alignment = { horizontal: 'left', vertical: 'middle', indent: 1 }
        ws.getRow(summaryStart).height = 18

        const summaryRows = [
            ['In Stock', inStock, BRAND_GREEN],
            ['Low Stock', lowStock, BRAND_AMBER],
            ['Out of Stock', outOfStock, BRAND_RED],
            ['Total Tracked', branchInventory.length, 'FF374151'],
            ['Items With Variance', withVariance, BRAND_PURPLE],
        ]
        summaryRows.forEach(([label, value, color], i) => {
            const r = ws.getRow(summaryStart + 1 + i)
            r.height = 17
            r.getCell(1).value = label; Object.assign(r.getCell(1), labelStyle)
            r.getCell(2).value = value; Object.assign(r.getCell(2), dataStyle('#,##0', color, true))
        })

        const buffer = await wb.xlsx.writeBuffer()
        const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `Inventory_${branchName.replace(/\s+/g, '_')}_${exportDate}.xlsx`
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(url)

    }

    const handleRemoveStockItem = async (item) => {
        const result = await Swal.fire({
            icon: 'warning',
            title: 'Remove Stock Item?',
            html: `Are you sure you want to permanently remove <b>"${item.name}"</b> from this branch inventory?<br/><span class="text-sm text-red-600">This cannot be undone — all stock tracking for this branch will be lost. Use Archive instead if you might need it again.</span>`,
            showCancelButton: true,
            confirmButtonText: 'Remove Permanently',
            cancelButtonText: 'Cancel',
            confirmButtonColor: '#dc2626',
            cancelButtonColor: '#A0826D'
        })
        if (!result.isConfirmed) return

        try {
            const res = await fetch(`${API}/${branchId}/stock/${item.id}`, {
                method: 'DELETE',
                headers: authHeaders()
            })
            const body = await res.json()
            if (!res.ok) throw new Error(body.error || 'Failed to remove')

            await fetchBranchInventory(branchId)
            await fetchArchivedInventory(branchId)

            Swal.fire({
                icon: 'success',
                title: `${item.name} Removed`,
                text: `${item.name} has been permanently removed from this branch inventory.`,
                confirmButtonColor: '#A0826D',
                timer: 2000
            })
        } catch (err) {
            console.error('Error removing stock item:', err)
            Swal.fire({ icon: 'error', title: 'Failed to Remove', text: err.message || 'An error occurred while removing the stock item.', confirmButtonColor: '#A0826D' })
        }
    }

    const handleDownloadInventoryPDF = async () => {
        setPdfLoading(true)
        try {
            let reviewData = stockCountReview
            if (stockCountReview.length === 0 && branchId) {
                reviewData = await fetchStockCountReview(stockCountReviewDate)
            }

            const loadScript = (src) => new Promise((resolve, reject) => {
                if (document.querySelector(`script[src="${src}"]`)) { resolve(); return }
                const s = document.createElement('script')
                s.src = src
                s.onload = resolve
                s.onerror = reject
                document.head.appendChild(s)
            })

            await loadScript('https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js')
            await loadScript('https://cdnjs.cloudflare.com/ajax/libs/jspdf-autotable/3.8.2/jspdf.plugin.autotable.min.js')

            const { jsPDF } = window.jspdf
            const pdf = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a3' })
            const pageW = pdf.internal.pageSize.getWidth()
            const pageH = pdf.internal.pageSize.getHeight()
            const margin = 12

            const NUDE = [160, 130, 109]
            const DARK = [17, 24, 39]
            const GREEN = [22, 163, 74]
            const AMBER = [217, 119, 6]
            const RED = [220, 38, 38]
            const BLUE = [37, 99, 235]
            const PURPLE = [124, 58, 237]
            const GREY = [107, 114, 128]
            const ALT_ROW = [249, 250, 251]

            const drawHeader = (isFirst) => {
                pdf.setFillColor(...NUDE)
                pdf.rect(margin, margin, pageW - margin * 2, 1, 'F')
                pdf.setFont('helvetica', 'bold')
                pdf.setFontSize(14)
                pdf.setTextColor(...NUDE)
                pdf.text("Coffee Gus\u00B7to", margin, margin + 8)
                pdf.setFont('helvetica', 'normal')
                pdf.setFontSize(10)
                pdf.setTextColor(...GREY)
                pdf.text(isFirst ? "Inventory Report" : "Inventory Report (cont.)", pageW - margin, margin + 8, { align: "right" })
                pdf.setFontSize(8)
                pdf.setTextColor(156, 163, 175)
                const sub = [
                    branchName ? branchName : "All Branches",
                    "Generated " + new Date().toLocaleString('en-PH', { dateStyle: 'medium', timeStyle: 'short' })
                ].join("   \u00B7   ")
                pdf.text(sub, margin, margin + 13)
            }

            const head = [[
                'Ingredient', 'Category', 'On Hand', 'Reserved', 'Available', 'Status', 'Reorder',
                'Last Updated', 'Beg. Stock', 'Added', 'Deducted', 'Sys. Qty', 'Staff Count', 'Variance', 'Counted By'
            ]]

            const body = branchInventory.map(item => {
                const isOut = item.available_stock <= 0
                const isLow = item.available_stock > 0 && item.available_stock <= item.reorder_level
                const statusLabel = isOut ? 'Out of Stock' : isLow ? 'Low Stock' : 'In Stock'
                const countRow = reviewData.find(r => r.ingredient_id === item.ingredient_id)
                const variance = countRow?.variance ?? null

                return [
                    `${item.name}\n${item.unit}`,
                    item.category,
                    `${item.current_stock} ${item.unit}`,
                    `${item.reserved_stock || 0} ${item.unit}`,
                    `${item.available_stock} ${item.unit}`,
                    statusLabel,
                    `${item.reorder_level} ${item.unit}`,
                    item.last_updated
                        ? new Date(item.last_updated).toLocaleString('en-PH', { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
                        : 'N/A',
                    countRow != null ? `${countRow.beginning_stock} ${item.unit}` : '\u2014',
                    countRow != null ? `+${countRow.added_stock}` : '\u2014',
                    countRow != null ? `-${countRow.deducted_stock}` : '\u2014',
                    countRow != null ? `${countRow.system_quantity} ${item.unit}` : '\u2014',
                    countRow?.staff_count != null ? `${countRow.staff_count} ${item.unit}` : '\u2014',
                    variance != null ? `${variance > 0 ? '+' : ''}${variance} ${item.unit}` : '\u2014',
                    countRow?.counted_by || '\u2014',
                ]
            })

            pdf.autoTable({
                head,
                body,
                startY: margin + 20,
                theme: 'grid',
                styles: { font: 'helvetica', fontSize: 8, cellPadding: 2.5, textColor: DARK, lineColor: [229, 231, 235], lineWidth: 0.1, valign: 'middle' },
                headStyles: { fillColor: DARK, textColor: 255, fontStyle: 'bold', halign: 'center', fontSize: 8 },
                alternateRowStyles: { fillColor: ALT_ROW },
                margin: { top: margin + 20, bottom: 16, left: margin, right: margin },
                didDrawPage: (data) => drawHeader(data.pageNumber === 1),
                didParseCell: (data) => {
                    if (data.section !== 'body') return
                    const col = data.column.index
                    const raw = data.cell.raw

                    if (col === 5) { // Status
                        if (raw === 'Out of Stock') { data.cell.styles.textColor = RED; data.cell.styles.fontStyle = 'bold' }
                        else if (raw === 'Low Stock') { data.cell.styles.textColor = AMBER; data.cell.styles.fontStyle = 'bold' }
                        else { data.cell.styles.textColor = GREEN; data.cell.styles.fontStyle = 'bold' }
                    }
                    if (col === 9) data.cell.styles.textColor = GREEN   // Added
                    if (col === 10) data.cell.styles.textColor = AMBER  // Deducted
                    if (col === 11) data.cell.styles.textColor = BLUE   // Sys. Qty
                    if (col === 12) { data.cell.styles.textColor = PURPLE; data.cell.styles.fontStyle = 'bold' } // Staff Count
                    if (col === 13) { // Variance
                        const str = String(raw)
                        if (str.startsWith('+')) data.cell.styles.textColor = BLUE
                        else if (str.startsWith('-')) data.cell.styles.textColor = RED
                        else if (str !== '\u2014') data.cell.styles.textColor = GREEN
                        data.cell.styles.fontStyle = 'bold'
                    }
                },
            })

            const totalPages = pdf.internal.getNumberOfPages()
            for (let i = 1; i <= totalPages; i++) {
                pdf.setPage(i)
                const y = pageH - 8
                pdf.setFont('helvetica', 'normal')
                pdf.setFontSize(8)
                pdf.setTextColor(...GREY)
                pdf.text("Coffee Gus\u00B7to \u2014 Confidential", margin, y)
                pdf.text(`Page ${i} of ${totalPages}`, pageW - margin, y, { align: "right" })
                pdf.setDrawColor(229, 231, 235)
                pdf.setLineWidth(0.2)
                pdf.line(margin, pageH - 13, pageW - margin, pageH - 13)
            }

            const dateStr = new Date().toISOString().split('T')[0]
            const branchSlug = branchName ? `_${branchName.replace(/\s+/g, '_')}` : ""
            pdf.save(`CoffeeGusto_Inventory${branchSlug}_${dateStr}.pdf`)

        } catch (err) {
            console.error('PDF export failed:', err)
            Swal.fire({ icon: 'error', title: 'PDF Export Failed', text: err.message, confirmButtonColor: '#A0826D' })
        } finally {
            setPdfLoading(false)
        }
    }

    // ─── Generic branded export engine (reused by Adjustment Requests, ────────
    // ─── Stock Count Review, and Restock Request — same look as Inventory) ────
    const exportRowsAsExcel = async ({ sheetName, reportTitle, columns, rows, filenamePrefix }) => {
        if (!window.ExcelJS) {
            await new Promise((resolve, reject) => {
                const script = document.createElement('script')
                script.src = 'https://cdnjs.cloudflare.com/ajax/libs/exceljs/4.4.0/exceljs.min.js'
                script.onload = resolve
                script.onerror = reject
                document.head.appendChild(script)
            })
        }

        const wb = new window.ExcelJS.Workbook()
        wb.creator = 'Coffee Gus·to'
        wb.created = new Date()

        const HEADER_BG = 'FF111827'
        const ALT_ROW_BG = 'FFF9FAFB'
        const WHITE = 'FFFFFFFF'
        const BRAND_NUDE = 'FFA0826D'

        const headerStyle = {
            font: { name: 'Arial', bold: true, color: { argb: WHITE }, size: 10 },
            fill: { type: 'pattern', pattern: 'solid', fgColor: { argb: HEADER_BG } },
            alignment: { horizontal: 'center', vertical: 'middle', wrapText: true },
            border: { bottom: { style: 'medium', color: { argb: BRAND_NUDE } } },
        }

        const ws = wb.addWorksheet(sheetName)
        ws.columns = columns.map(c => ({ width: c.width || 16 }))

        ws.mergeCells(1, 1, 1, columns.length)
        const titleCell = ws.getCell('A1')
        titleCell.value = `Coffee Gus·to — ${reportTitle}`
        titleCell.font = { name: 'Arial', bold: true, size: 14, color: { argb: WHITE } }
        titleCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: HEADER_BG } }
        titleCell.alignment = { horizontal: 'left', vertical: 'middle', indent: 1 }
        ws.getRow(1).height = 32

        ws.mergeCells(2, 1, 2, columns.length)
        const subCell = ws.getCell('A2')
        subCell.value = `Branch: ${branchName} · Generated ${new Date().toLocaleString('en-PH', { dateStyle: 'long', timeStyle: 'short' })}`
        subCell.font = { name: 'Arial', italic: true, size: 9, color: { argb: 'FF6B7280' } }
        subCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFF3F4F6' } }
        subCell.alignment = { horizontal: 'left', vertical: 'middle', indent: 1 }
        ws.getRow(2).height = 18

        const hr = ws.getRow(4)
        hr.values = columns.map(c => c.header)
        hr.height = 26
        columns.forEach((_, i) => Object.assign(hr.getCell(i + 1), headerStyle))

        rows.forEach((row, i) => {
            const r = ws.getRow(5 + i)
            r.height = 18
            columns.forEach((col, ci) => {
                const cellData = col.excelValue(row)
                const cell = r.getCell(ci + 1)
                cell.value = cellData.value
                cell.font = { name: 'Arial', size: 10, bold: !!cellData.bold, color: { argb: cellData.color || 'FF111827' } }
                cell.alignment = { horizontal: cellData.align || col.align || 'left', vertical: 'middle' }
            })
        })

        const lastRow = 4 + rows.length
        for (let r = 5; r <= lastRow; r++) {
            if (r % 2 === 0) {
                for (let c = 1; c <= columns.length; c++) {
                    ws.getCell(r, c).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: ALT_ROW_BG } }
                }
            }
        }

        const buffer = await wb.xlsx.writeBuffer()
        const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `${filenamePrefix}_${branchName.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.xlsx`
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(url)
    }

    const exportRowsAsPDF = async ({ reportTitle, columns, rows, filenamePrefix, setLoadingFn }) => {
        setLoadingFn(true)
        try {
            const loadScript = (src) => new Promise((resolve, reject) => {
                if (document.querySelector(`script[src="${src}"]`)) { resolve(); return }
                const s = document.createElement('script')
                s.src = src
                s.onload = resolve
                s.onerror = reject
                document.head.appendChild(s)
            })
            await loadScript('https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js')
            await loadScript('https://cdnjs.cloudflare.com/ajax/libs/jspdf-autotable/3.8.2/jspdf.plugin.autotable.min.js')

            const { jsPDF } = window.jspdf
            const pdf = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a3' })
            const pageW = pdf.internal.pageSize.getWidth()
            const pageH = pdf.internal.pageSize.getHeight()
            const margin = 12

            const NUDE = [160, 130, 109]
            const DARK = [17, 24, 39]
            const GREY = [107, 114, 128]
            const ALT_ROW = [249, 250, 251]

            const argbToRgb = (argb) => {
                if (!argb) return DARK
                const hex = argb.length === 8 ? argb.slice(2) : argb
                return [parseInt(hex.slice(0, 2), 16), parseInt(hex.slice(2, 4), 16), parseInt(hex.slice(4, 6), 16)]
            }

            const drawHeader = (isFirst) => {
                pdf.setFillColor(...NUDE)
                pdf.rect(margin, margin, pageW - margin * 2, 1, 'F')
                pdf.setFont('helvetica', 'bold')
                pdf.setFontSize(14)
                pdf.setTextColor(...NUDE)
                pdf.text("Coffee Gus\u00B7to", margin, margin + 8)
                pdf.setFont('helvetica', 'normal')
                pdf.setFontSize(10)
                pdf.setTextColor(...GREY)
                pdf.text(isFirst ? reportTitle : `${reportTitle} (cont.)`, pageW - margin, margin + 8, { align: "right" })
                pdf.setFontSize(8)
                pdf.setTextColor(156, 163, 175)
                const sub = [
                    branchName ? branchName : "All Branches",
                    "Generated " + new Date().toLocaleString('en-PH', { dateStyle: 'medium', timeStyle: 'short' })
                ].join("   \u00B7   ")
                pdf.text(sub, margin, margin + 13)
            }

            const head = [columns.map(c => c.header)]
            const cellMeta = rows.map(row => columns.map(col => col.excelValue(row)))
            const body = cellMeta.map(rowMeta => rowMeta.map(cell => String(cell.value)))

            pdf.autoTable({
                head,
                body,
                startY: margin + 20,
                theme: 'grid',
                styles: { font: 'helvetica', fontSize: 8, cellPadding: 2.5, textColor: DARK, lineColor: [229, 231, 235], lineWidth: 0.1, valign: 'middle' },
                headStyles: { fillColor: DARK, textColor: 255, fontStyle: 'bold', halign: 'center', fontSize: 8 },
                alternateRowStyles: { fillColor: ALT_ROW },
                margin: { top: margin + 20, bottom: 16, left: margin, right: margin },
                didDrawPage: (data) => drawHeader(data.pageNumber === 1),
                didParseCell: (data) => {
                    if (data.section !== 'body') return
                    const meta = cellMeta[data.row.index]?.[data.column.index]
                    if (!meta) return
                    if (meta.color) data.cell.styles.textColor = argbToRgb(meta.color)
                    if (meta.bold) data.cell.styles.fontStyle = 'bold'
                    if (meta.align) data.cell.styles.halign = meta.align
                },
            })

            const totalPages = pdf.internal.getNumberOfPages()
            for (let i = 1; i <= totalPages; i++) {
                pdf.setPage(i)
                const y = pageH - 8
                pdf.setFont('helvetica', 'normal')
                pdf.setFontSize(8)
                pdf.setTextColor(...GREY)
                pdf.text("Coffee Gus\u00B7to \u2014 Confidential", margin, y)
                pdf.text(`Page ${i} of ${totalPages}`, pageW - margin, y, { align: "right" })
                pdf.setDrawColor(229, 231, 235)
                pdf.setLineWidth(0.2)
                pdf.line(margin, pageH - 13, pageW - margin, pageH - 13)
            }

            const dateStr = new Date().toISOString().split('T')[0]
            const branchSlug = branchName ? `_${branchName.replace(/\s+/g, '_')}` : ""
            pdf.save(`CoffeeGusto_${filenamePrefix}${branchSlug}_${dateStr}.pdf`)
        } catch (err) {
            console.error('PDF export failed:', err)
            Swal.fire({ icon: 'error', title: 'PDF Export Failed', text: err.message, confirmButtonColor: '#A0826D' })
        } finally {
            setLoadingFn(false)
        }
    }

    // ─── Adjustment Requests export ─────────────────────────────────────────
    const adjustmentRequestColumns = [
        {
            header: 'Ingredient', width: 24,
            excelValue: (r) => ({ value: r.ingredient_name }),
            pdfValue: (r) => ({ html: `<div style="font-weight:600;color:#111827;font-size:13px;">${r.ingredient_name}</div><div style="font-size:11px;color:#6b7280;margin-top:2px;">${r.ingredient_unit}</div>` })
        },
        {
            header: 'Type', width: 12, align: 'center',
            excelValue: (r) => ({ value: r.adjustment_type === 'add' ? 'Add' : 'Deduct', color: r.adjustment_type === 'add' ? 'FF16A34A' : 'FFDC2626', bold: true, align: 'center' }),
            pdfValue: (r) => {
                const isAdd = r.adjustment_type === 'add'
                return { html: `<span style="display:inline-block;padding:4px 10px;border-radius:9999px;font-size:11px;font-weight:600;background:${isAdd ? '#f0fdf4' : '#fef2f2'};color:${isAdd ? '#16a34a' : '#dc2626'};">${isAdd ? '+ Add' : '\u2212 Deduct'}</span>` }
            }
        },
        {
            header: 'Quantity', width: 14, align: 'right',
            excelValue: (r) => ({ value: `${r.quantity} ${r.ingredient_unit}`, align: 'right' }),
            pdfValue: (r) => ({ html: `<span style="color:#111827;font-weight:500;">${r.quantity} ${r.ingredient_unit}</span>` })
        },
        {
            header: 'Reason', width: 26,
            excelValue: (r) => ({ value: `${r.reason}${r.notes ? ' — ' + r.notes : ''}`, color: 'FF4B5563' }),
            pdfValue: (r) => ({ html: `<div style="color:#374151;text-transform:capitalize;">${r.reason}</div>${r.notes ? `<div style="font-size:11px;color:#9ca3af;margin-top:2px;">${r.notes}</div>` : ''}` })
        },
        {
            header: 'Requested By', width: 18,
            excelValue: (r) => ({ value: r.requested_by }),
            pdfValue: (r) => ({ html: `<span style="color:#374151;">${r.requested_by}</span>` })
        },
        {
            header: 'Status', width: 14, align: 'center',
            excelValue: (r) => ({ value: r.status.charAt(0).toUpperCase() + r.status.slice(1), color: r.status === 'approved' ? 'FF16A34A' : r.status === 'rejected' ? 'FFDC2626' : 'FFD97706', bold: true, align: 'center' }),
            pdfValue: (r) => {
                const color = r.status === 'approved' ? '#16a34a' : r.status === 'rejected' ? '#dc2626' : '#d97706'
                const bg = r.status === 'approved' ? '#f0fdf4' : r.status === 'rejected' ? '#fef2f2' : '#fffbeb'
                return { html: `<span style="display:inline-block;padding:4px 10px;border-radius:9999px;font-size:11px;font-weight:600;background:${bg};color:${color};">${r.status.charAt(0).toUpperCase() + r.status.slice(1)}</span>` }
            }
        },
        {
            header: 'Date', width: 20,
            excelValue: (r) => ({ value: formatDateTime(r.created_at), color: 'FF9CA3AF' }),
            pdfValue: (r) => ({ html: `<span style="font-size:11px;color:#9ca3af;">${formatDateTime(r.created_at)}</span>` })
        },
    ]

    const downloadAdjustmentRequestsExcel = () => exportRowsAsExcel({
        sheetName: 'Adjustment Requests',
        reportTitle: 'Adjustment Requests Report',
        columns: adjustmentRequestColumns,
        rows: adjustmentRequests,
        filenamePrefix: 'AdjustmentRequests'
    })

    const downloadAdjustmentRequestsPDF = () => exportRowsAsPDF({
        reportTitle: 'Adjustment Requests Report',
        columns: adjustmentRequestColumns,
        rows: adjustmentRequests,
        filenamePrefix: 'AdjustmentRequests',
        setLoadingFn: setAdjustmentPdfLoading
    })

    // ─── Stock Count Review export ──────────────────────────────────────────
    const stockCountReviewColumns = [
        {
            header: 'Ingredient', width: 24,
            excelValue: (r) => ({ value: r.name }),
            pdfValue: (r) => ({ html: `<div style="font-weight:600;color:#111827;font-size:13px;">${r.name}</div><div style="font-size:11px;color:#6b7280;margin-top:2px;">${r.unit} \u00B7 ${r.category}</div>` })
        },
        {
            header: 'Beginning Stock', width: 16,
            excelValue: (r) => ({ value: `${r.beginning_stock} ${r.unit}`, color: 'FF6B7280' }),
            pdfValue: (r) => ({ html: `<span style="color:#4b5563;">${r.beginning_stock} ${r.unit}</span>` })
        },
        {
            header: 'Added', width: 14,
            excelValue: (r) => ({ value: `+${r.added_stock} ${r.unit}`, color: 'FF16A34A', bold: true }),
            pdfValue: (r) => ({ html: `<span style="color:#16a34a;font-weight:500;">+${r.added_stock} ${r.unit}</span>` })
        },
        {
            header: 'Deducted', width: 14,
            excelValue: (r) => ({ value: `-${r.deducted_stock} ${r.unit}`, color: 'FFD97706', bold: true }),
            pdfValue: (r) => ({ html: `<span style="color:#d97706;font-weight:500;">-${r.deducted_stock} ${r.unit}</span>` })
        },
        {
            header: 'System Qty', width: 14,
            excelValue: (r) => ({ value: `${r.system_quantity} ${r.unit}`, color: 'FF2563EB', bold: true }),
            pdfValue: (r) => ({ html: `<span style="color:#2563eb;font-weight:500;">${r.system_quantity} ${r.unit}</span>` })
        },
        {
            header: 'Staff Count', width: 14,
            excelValue: (r) => ({ value: r.staff_count != null ? `${r.staff_count} ${r.unit}` : '\u2014', color: 'FF7C3AED', bold: true }),
            pdfValue: (r) => ({ html: `<span style="color:#7c3aed;font-weight:600;">${r.staff_count != null ? `${r.staff_count} ${r.unit}` : '\u2014'}</span>` })
        },
        {
            header: 'Variance', width: 14, align: 'center',
            excelValue: (r) => {
                const v = r.variance
                const color = v == null ? 'FF9CA3AF' : v === 0 ? 'FF16A34A' : v > 0 ? 'FF2563EB' : 'FFDC2626'
                return { value: v != null ? `${v > 0 ? '+' : ''}${v} ${r.unit}` : '\u2014', color, bold: true, align: 'center' }
            },
            pdfValue: (r) => {
                const v = r.variance
                const color = v == null ? '#6b7280' : v === 0 ? '#16a34a' : v > 0 ? '#2563eb' : '#dc2626'
                const bg = v == null ? '#f3f4f6' : v === 0 ? '#f0fdf4' : v > 0 ? '#eff6ff' : '#fef2f2'
                return { html: `<span style="display:inline-block;padding:4px 10px;border-radius:6px;font-size:11px;font-weight:600;background:${bg};color:${color};">${v != null ? `${v > 0 ? '+' : ''}${v} ${r.unit}` : '\u2014'}</span>` }
            }
        },
        {
            header: 'Counted By', width: 20,
            excelValue: (r) => ({ value: r.counted_by || '\u2014' }),
            pdfValue: (r) => ({ html: `<div style="color:#374151;font-weight:500;">${r.counted_by || '\u2014'}</div>${r.count_time ? `<div style="color:#9ca3af;font-size:10px;margin-top:2px;">${new Date(r.count_time).toLocaleString('en-PH', { hour: 'numeric', minute: '2-digit' })}</div>` : ''}` })
        },
    ]

    const downloadStockCountReviewExcel = async () => {
        let data = stockCountReview
        if (data.length === 0 && branchId) data = await fetchStockCountReview(stockCountReviewDate)
        exportRowsAsExcel({
            sheetName: 'Stock Count Review',
            reportTitle: `Stock Count Review — ${stockCountReviewDate}`,
            columns: stockCountReviewColumns,
            rows: data || [],
            filenamePrefix: 'StockCountReview'
        })
    }

    const downloadStockCountReviewPDF = async () => {
        let data = stockCountReview
        if (data.length === 0 && branchId) data = await fetchStockCountReview(stockCountReviewDate)
        exportRowsAsPDF({
            reportTitle: `Stock Count Review — ${stockCountReviewDate}`,
            columns: stockCountReviewColumns,
            rows: data || [],
            filenamePrefix: 'StockCountReview',
            setLoadingFn: setStockCountPdfLoading
        })
    }

    // ─── Restock Requests export ────────────────────────────────────────────
    const restockRequestColumns = [
        {
            header: 'Ingredient', width: 24,
            excelValue: (r) => ({ value: r.ingredient_name }),
            pdfValue: (r) => ({ html: `<div style="font-weight:600;color:#111827;font-size:13px;">${r.ingredient_name}</div><div style="font-size:11px;color:#6b7280;margin-top:2px;">${r.ingredient_unit}</div>` })
        },
        {
            header: 'Stock at Request', width: 16,
            excelValue: (r) => ({ value: `${r.current_stock} ${r.ingredient_unit}`, color: 'FF6B7280' }),
            pdfValue: (r) => ({ html: `<span style="color:#4b5563;">${r.current_stock} ${r.ingredient_unit}</span>` })
        },
        {
            header: 'Reorder Level', width: 16,
            excelValue: (r) => ({ value: `${r.reorder_level} ${r.ingredient_unit}`, color: 'FF6B7280' }),
            pdfValue: (r) => ({ html: `<span style="color:#4b5563;">${r.reorder_level} ${r.ingredient_unit}</span>` })
        },
        {
            header: 'Requested Qty', width: 16,
            excelValue: (r) => ({ value: `${r.requested_quantity} ${r.ingredient_unit}`, bold: true }),
            pdfValue: (r) => ({ html: `<span style="color:#111827;font-weight:600;">${r.requested_quantity} ${r.ingredient_unit}</span>` })
        },
        {
            header: 'Status', width: 14, align: 'center',
            excelValue: (r) => {
                const color = r.status === 'fulfilled' ? 'FF16A34A' : r.status === 'approved' ? 'FF2563EB' : r.status === 'rejected' ? 'FFDC2626' : 'FFD97706'
                return { value: r.status.charAt(0).toUpperCase() + r.status.slice(1), color, bold: true, align: 'center' }
            },
            pdfValue: (r) => {
                const color = r.status === 'fulfilled' ? '#16a34a' : r.status === 'approved' ? '#2563eb' : r.status === 'rejected' ? '#dc2626' : '#d97706'
                const bg = r.status === 'fulfilled' ? '#f0fdf4' : r.status === 'approved' ? '#eff6ff' : r.status === 'rejected' ? '#fef2f2' : '#fffbeb'
                return { html: `<span style="display:inline-block;padding:4px 10px;border-radius:9999px;font-size:11px;font-weight:600;background:${bg};color:${color};">${r.status.charAt(0).toUpperCase() + r.status.slice(1)}</span>` }
            }
        },
        {
            header: 'Requested', width: 20,
            excelValue: (r) => ({ value: formatDateTime(r.created_at), color: 'FF9CA3AF' }),
            pdfValue: (r) => ({ html: `<span style="font-size:11px;color:#9ca3af;">${formatDateTime(r.created_at)}</span>` })
        },
    ]

    const downloadRestockRequestsExcel = async () => {
        if (!window.ExcelJS) {
            await new Promise((resolve, reject) => {
                const script = document.createElement('script')
                script.src = 'https://cdnjs.cloudflare.com/ajax/libs/exceljs/4.4.0/exceljs.min.js'
                script.onload = resolve
                script.onerror = reject
                document.head.appendChild(script)
            })
        }

        const wb = new window.ExcelJS.Workbook()
        wb.creator = 'Coffee Gus·to'
        wb.created = new Date()

        const ORANGE = 'FFE8601C'
        const WHITE = 'FFFFFFFF'
        const GREY_BORDER = 'FFBFBFBF'
        const thinBorder = {
            top: { style: 'thin', color: { argb: GREY_BORDER } },
            bottom: { style: 'thin', color: { argb: GREY_BORDER } },
            left: { style: 'thin', color: { argb: GREY_BORDER } },
            right: { style: 'thin', color: { argb: GREY_BORDER } },
        }

        const ws = wb.addWorksheet('Restock Request')
        ws.columns = [
            { width: 14 }, // ORDER QTY
            { width: 46 }, // DESCRIPTION
            { width: 14 }, // TOTAL QTY
            { width: 12 }, // UOM
        ]

        // ── Company header ──
        ws.mergeCells('A1:D1')
        ws.getCell('A1').value = 'Coffee Gus·to'
        ws.getCell('A1').font = { name: 'Arial', bold: true, size: 16, color: { argb: 'FFA0826D' } }

        ws.mergeCells('A2:D2')
        ws.getCell('A2').value = branchName
        ws.getCell('A2').font = { name: 'Arial', size: 11 }

        ws.mergeCells('A3:D3')
        ws.getCell('A3').value = `Generated ${new Date().toLocaleString('en-PH', { dateStyle: 'long', timeStyle: 'short' })}`
        ws.getCell('A3').font = { name: 'Arial', italic: true, size: 9, color: { argb: 'FF6B7280' } }

        // ── Order form title bar ──
        ws.mergeCells('A5:D5')
        const titleCell = ws.getCell('A5')
        titleCell.value = 'RESTOCK REQUEST FORM'
        titleCell.font = { name: 'Arial', bold: true, size: 14, color: { argb: WHITE } }
        titleCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: ORANGE } }
        titleCell.alignment = { horizontal: 'center', vertical: 'middle' }
        ws.getRow(5).height = 26

        // ── Order code box ──
        const orderCode = `RSR-${branchName.replace(/\s+/g, '').toUpperCase().slice(0, 4)}-${new Date().toISOString().split('T')[0].replace(/-/g, '')}`
        ws.mergeCells('A6:D6')
        ws.getCell('A6').value = 'ORDER CODE'
        ws.getCell('A6').font = { name: 'Arial', bold: true, size: 10, color: { argb: ORANGE } }
        ws.getCell('A6').alignment = { horizontal: 'center' }
        ws.getCell('A6').border = thinBorder

        ws.mergeCells('A7:D7')
        ws.getCell('A7').value = orderCode
        ws.getCell('A7').font = { name: 'Arial', bold: true, size: 12 }
        ws.getCell('A7').alignment = { horizontal: 'center' }
        ws.getCell('A7').border = thinBorder

        // ── Requesting office / order date ──
        ws.getCell('A9').value = 'Requesting Office:'
        ws.getCell('A9').font = { name: 'Arial', bold: true, color: { argb: ORANGE } }
        ws.mergeCells('B9:D9')
        ws.getCell('B9').value = branchName
        ws.getCell('B9').font = { name: 'Arial', size: 11 }

        ws.getCell('A10').value = 'Order Date:'
        ws.getCell('A10').font = { name: 'Arial', bold: true, color: { argb: ORANGE } }
        ws.mergeCells('B10:D10')
        ws.getCell('B10').value = new Date().toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })
        ws.getCell('B10').font = { name: 'Arial', size: 11 }

        // ── Table header ──
        const headerRowIdx = 12
        const hr = ws.getRow(headerRowIdx)
        hr.values = ['ORDER QTY', 'DESCRIPTION', 'TOTAL QTY', 'UOM']
        hr.eachCell(cell => {
            cell.font = { name: 'Arial', bold: true, size: 11, color: { argb: WHITE } }
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: ORANGE } }
            cell.alignment = { horizontal: 'center', vertical: 'middle' }
            cell.border = {
                top: { style: 'thin', color: { argb: ORANGE } },
                bottom: { style: 'thin', color: { argb: ORANGE } },
                left: { style: 'thin', color: { argb: ORANGE } },
                right: { style: 'thin', color: { argb: ORANGE } },
            }
        })
        hr.height = 22

        // ── Section label ──
        const sectionRowIdx = headerRowIdx + 1
        ws.mergeCells(sectionRowIdx, 1, sectionRowIdx, 4)
        const sectionCell = ws.getCell(sectionRowIdx, 1)
        sectionCell.value = 'RESTOCK REQUEST'
        sectionCell.font = { name: 'Arial', bold: true, size: 10, color: { argb: WHITE } }
        sectionCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: ORANGE } }
        sectionCell.alignment = { horizontal: 'center', vertical: 'middle' }

        // ── Data rows: only pending requests + items not yet requested ──
        const csvOrderRows = [
            ...restockRequestRows
                .filter(r => r.status === 'pending')
                .map(r => ({ ingredient_name: r.ingredient_name, requested_quantity: r.requested_quantity, ingredient_unit: r.ingredient_unit })),
            ...notYetRequestedItems
                .map(item => ({ ingredient_name: item.name, requested_quantity: item.reorder_level, ingredient_unit: item.unit }))
        ]

        csvOrderRows.forEach((r, i) => {
            const rowIdx = sectionRowIdx + 1 + i
            const row = ws.getRow(rowIdx)
            row.values = [1, r.ingredient_name, r.requested_quantity, r.ingredient_unit]
            row.getCell(1).alignment = { horizontal: 'center' }
            row.getCell(3).alignment = { horizontal: 'right' }
            row.getCell(4).alignment = { horizontal: 'center' }
            row.eachCell(cell => {
                cell.border = thinBorder
                cell.font = { name: 'Arial', size: 10 }
            })
        })

        const buffer = await wb.xlsx.writeBuffer()
        const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = `RestockRequest_${branchName.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.xlsx`
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(url)
    }

    const downloadRestockRequestsPDF = () => exportRowsAsPDF({
        reportTitle: 'Restock Requests Report',
        columns: restockRequestColumns,
        rows: restockRequestRows.filter(r => r.status !== 'rejected' && r.status !== 'approved'),
        filenamePrefix: 'RestockRequests',
        setLoadingFn: setRestockPdfLoading
    })

    // ─── Dispatch: which export runs depends on the active inventory sub-tab ──
    const getActiveDownloadHandlers = () => {
        switch (inventorySubTab) {
            case 'adjustment_requests':
                return { pdf: downloadAdjustmentRequestsPDF, csv: downloadAdjustmentRequestsExcel }
            case 'stock_count_review':
                return { pdf: downloadStockCountReviewPDF, csv: downloadStockCountReviewExcel }
            case 'restock_request':
                return { pdf: downloadRestockRequestsPDF, csv: downloadRestockRequestsExcel }
            case 'stock_list':
            default:
                return { pdf: handleDownloadInventoryPDF, csv: downloadInventoryExcel }
        }
    }

    const fetchTodayCountTypes = async (currentBranchId) => {
        if (!isOnline) return

        try {
            const params = new URLSearchParams()
            if (selectedBarista) params.set('baristaId', selectedBarista.barista_id)
            else params.set('staffId', staffRecordId || '')

            const res = await fetch(`${API}/${currentBranchId}/stock-count/today-types?${params.toString()}`, { headers: authHeaders() })
            if (!res.ok) return
            const data = await res.json()

            const countMap = {}
            Object.entries(data).forEach(([ingredientId, types]) => {
                countMap[ingredientId] = new Set(types)
            })
            setTodayCountTypes(countMap)
        } catch (err) {
            console.error('Error fetching today count types:', err)
        }
    }

    const openStaffCountModal = async (item) => {
        setStaffCountItem(item)
        setStaffCountInput('')
        setStaffCountResult(null)
        setStaffCountType('beginning')

        if (!isOnline) {
            const pendingTypes = await getPendingStockCountTypesOffline(branchId, item.ingredient_id)
            const usedTypes = new Set(pendingTypes)
            setTodayCountTypes(prev => ({ ...prev, [item.ingredient_id]: usedTypes }))
            setStaffCountType(!usedTypes.has('beginning') ? 'beginning' : !usedTypes.has('end_of_shift') ? 'end_of_shift' : 'general')
            setStaffCountHistory([])
            setShowStaffCountModal(true)
            return
        }
        const today = new Date().toISOString().split('T')[0]

        try {
            const res = await fetch(
                `${API}/${branchId}/stock-count/records?ingredientId=${item.ingredient_id}&date=${today}`,
                { headers: authHeaders() }
            )
            if (res.ok) {
                const data = await res.json()
                setStaffCountHistory(data)

                const myRecords = data.filter(r =>
                    selectedBarista
                        ? r.counted_by_barista_id === selectedBarista.barista_id
                        : r.counted_by_staff_id === staffRecordId
                )
                const usedTypes = new Set(myRecords.map(r => r.count_type).filter(Boolean))
                setTodayCountTypes(prev => ({ ...prev, [item.ingredient_id]: usedTypes }))

                if (!usedTypes.has('beginning')) {
                    setStaffCountType('beginning')
                } else if (!usedTypes.has('end_of_shift')) {
                    setStaffCountType('end_of_shift')
                } else {
                    setStaffCountType('general')
                }
            }
        } catch (err) {
            console.error('Error loading staff count history:', err)
        }

        setShowStaffCountModal(true)
    }

    const handleSubmitStaffCount = async () => {
        if (!staffCountItem || staffCountInput === '') return

        const counted = parseFloat(staffCountInput)
        if (isNaN(counted) || counted < 0) {
            Swal.fire({ icon: 'warning', title: 'Invalid Count', text: 'Please enter a valid number.', confirmButtonColor: '#A0826D' })
            return
        }

        setIsSubmittingCount(true)
        
        try {

            if (!isOnline) {
                const result = await submitStaffCountOffline({
                    branchId, ingredientId: staffCountItem.ingredient_id,
                    countedQuantity: counted, countType: staffCountType,
                    staffRecordId, baristaId: selectedBarista?.barista_id ?? null,
                    unit: staffCountItem.unit,
                })
                setStaffCountResult({ ...result, unit: staffCountItem.unit })

                const updatedTypes = new Set(todayCountTypes[staffCountItem.ingredient_id] || [])
                updatedTypes.add(staffCountType)
                setTodayCountTypes(prev => ({ ...prev, [staffCountItem.ingredient_id]: updatedTypes }))
                setStaffCountType(!updatedTypes.has('beginning') ? 'beginning' : !updatedTypes.has('end_of_shift') ? 'end_of_shift' : 'general')

                setTimeout(() => { setStaffCountResult(null); setStaffCountInput('') }, 1800)
                return
            }

            const res = await fetch(`${API}/${branchId}/stock-count`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...authHeaders() },
                body: JSON.stringify({
                    ingredientId: staffCountItem.ingredient_id,
                    countedQuantity: counted,
                    countType: staffCountType,
                    staffRecordId,
                    baristaId: selectedBarista?.barista_id ?? null,
                    unit: staffCountItem.unit
                })
            })
            const body = await res.json()
            if (!res.ok) throw new Error(body.error || 'Failed to submit count')

            setStaffCountResult({
                match: body.match,
                discrepancy: body.discrepancy,
                counted: body.counted,
                actual: body.actual,
                unit: staffCountItem.unit
            })

            const updatedTypes = new Set(todayCountTypes[staffCountItem.ingredient_id] || [])
            updatedTypes.add(staffCountType)
            setTodayCountTypes(prev => ({ ...prev, [staffCountItem.ingredient_id]: updatedTypes }))

            if (!updatedTypes.has('beginning')) {
                setStaffCountType('beginning')
            } else if (!updatedTypes.has('end_of_shift')) {
                setStaffCountType('end_of_shift')
            } else {
                setStaffCountType('general')
            }

            setTimeout(() => {
                setStaffCountResult(null)
                setStaffCountInput('')
            }, 1800)

            const today = new Date().toISOString().split('T')[0]
            const histRes = await fetch(
                `${API}/${branchId}/stock-count/records?ingredientId=${staffCountItem.ingredient_id}&date=${today}`,
                { headers: authHeaders() }
            )
            if (histRes.ok) {
                const histData = await histRes.json()
                const myHist = histData
                    .filter(r => selectedBarista
                        ? r.counted_by_barista_id === selectedBarista.barista_id
                        : r.counted_by_staff_id === staffRecordId)
                    .slice(0, 10)
                setStaffCountHistory(myHist)
            }
        } catch (err) {
            Swal.fire({ icon: 'error', title: 'Failed to Submit Count', text: err.message, confirmButtonColor: '#A0826D' })
        } finally {
            setIsSubmittingCount(false)
        }
    }

    const openAddStockModal = async () => {
        await fetchAvailableIngredients(branchId)

        const emptyAddStockData = {
            ingredient_id: '',
            input_mode: 'manual',
            current_stock: '',
            package_count: '',
            reorder_level: ''
        }

        setIngredientSearchQuery('')
        setAddStockFormData(emptyAddStockData)
        setInitialAddStockFormData(emptyAddStockData)
        setShowAddStockModal(true)
    }

    const openAdjustStockModal = (item) => {
        const initialAdjustData = {
            mode: 'add',
            input_mode: 'manual',
            quantity: '',
            amount_per_package: '',
            package_count: '',
            reorder_level: item.reorder_level ?? ''
        }

        setSelectedInventoryItem(item)
        setStockFormData(initialAdjustData)
        setInitialStockFormData(initialAdjustData)
        setShowAdjustStockModal(true)
    }

    const handleUpdateStock = async () => {
        if (!selectedInventoryItem) return
        if (!hasAdjustStockChanges) return

        try {
            const parsedReorderLevel = parseFloat(stockFormData.reorder_level)
            const parsedQuantity = parseFloat(stockFormData.quantity || 0)
            const parsedAmountPerPackage = parseFloat(stockFormData.amount_per_package || 0)
            const parsedPackageCount = parseFloat(stockFormData.package_count || 0)

            if (stockFormData.input_mode === 'manual' && parsedQuantity < 0) {
                Swal.fire({ icon: 'warning', title: 'Invalid Stock Input', text: 'Quantity cannot be negative.', confirmButtonColor: '#A0826D' })
                return
            }

            if (stockFormData.input_mode === 'package') {
                if (parsedAmountPerPackage <= 0) {
                    Swal.fire({ icon: 'warning', title: 'Invalid Stock Input', text: 'Amount per package must be greater than 0.', confirmButtonColor: '#A0826D' })
                    return
                }
                if (parsedPackageCount <= 0) {
                    Swal.fire({ icon: 'warning', title: 'Invalid Stock Input', text: 'Number of packages must be greater than 0.', confirmButtonColor: '#A0826D' })
                    return
                }
            }

            const totalAdjustment =
                stockFormData.input_mode === 'package'
                    ? parsedAmountPerPackage * parsedPackageCount
                    : parsedQuantity

            const res = await fetch(`${API}/${branchId}/stock/${selectedInventoryItem.id}`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json', ...authHeaders() },
                body: JSON.stringify({
                    mode: stockFormData.mode,
                    totalAdjustment,
                    reorderLevel: parsedReorderLevel,
                    unit: selectedInventoryItem.unit || '',
                    staffRecordId,
                    baristaId: selectedBarista?.barista_id ?? null,
                    createdByAuthId: getAuthItem('staffId')
                })
            })
            const body = await res.json()
            if (!res.ok) {
                if (res.status === 400) {
                    Swal.fire({ icon: 'warning', title: 'Insufficient Stock', text: body.error, confirmButtonColor: '#A0826D' })
                    return
                }
                throw new Error(body.error || 'Failed to update stock')
            }

            setShowAdjustStockModal(false)
            setSelectedInventoryItem(null)
            setStockFormData({ mode: 'add', input_mode: 'manual', quantity: '', amount_per_package: '', package_count: '', reorder_level: '' })

            await fetchBranchInventory(branchId)
            await fetchInventoryTransactions(branchId)

            Swal.fire({
                icon: 'success',
                title: `${selectedInventoryItem?.name || 'Ingredient'} Updated`,
                text: `${selectedInventoryItem?.name || 'Ingredient'} stock has been updated successfully.`,
                confirmButtonColor: '#A0826D',
                timer: 2000
            })

        } catch (err) {
            console.error('Error updating stock:', err)
            Swal.fire({ icon: 'error', title: 'Failed to Update Stock', text: err.message, confirmButtonColor: '#A0826D' })
        }
    }

    // === Helpers ───────────────────────────────────────────────────────────────

    const formatCurrency = (amount) => {
        return `₱${parseFloat(amount || 0).toLocaleString('en-PH', {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        })}`
    }
    const handleSaveEODTime = async () => {
        if (!eodTime || !branchId || !staffRecordId) return
        setIsSavingEOD(true)
        try {
            const eodRes = await fetch(`${API}/${branchId}/eod-time`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', ...authHeaders() },
                body: JSON.stringify({ eodTime, staffRecordId })
            })
            const eodBody = await eodRes.json()
            if (!eodRes.ok) throw new Error(eodBody.error || 'Failed to save EOD time.')

            localStorage.setItem(`eodTime_${branchId}`, eodTime)

            const staffEmail = eodBody.email
            if (!staffEmail) throw new Error('No email found for this staff account.')

            const today = new Date().toISOString().split('T')[0]
            const dayStart = `${today}T00:00:00+00:00`
            const dayEnd = `${today}T23:59:59+00:00`

            // NOTE: this direct supabase read for countRows is fine to keep, OR
            // replace with: const countRows = await fetch(`${API}/${branchId}/stock-count/records?...`)
            // per ingredient if you want to fully remove supabase reads from this file.
            const { data: countRows } = await supabase
                .schema('inventory')
                .from('staff_counts')
                .select('ingredient_id, counted_quantity, discrepancy, created_at')
                .eq('branch_id', branchId)
                .gte('created_at', dayStart)
                .lte('created_at', dayEnd)
                .order('created_at', { ascending: false })

            const countMap = new Map()
                ; (countRows || []).forEach(r => {
                    if (!countMap.has(r.ingredient_id)) countMap.set(r.ingredient_id, r)
                })

            const reviewData = await fetchStockCountReview(today)

            const inventoryPayload = branchInventory.map(item => {
                const isOut = item.available_stock <= 0
                const isLow = item.available_stock > 0 && item.available_stock <= item.reorder_level
                const countRow = countMap.get(item.ingredient_id)
                const reviewRow = (reviewData).find(r => r.ingredient_id === item.ingredient_id)
                return {
                    name: item.name,
                    category: item.category,
                    unit: item.unit,
                    current_stock: item.current_stock,
                    reserved_stock: item.reserved_stock || 0,
                    available_stock: item.available_stock,
                    reorder_level: item.reorder_level,
                    status: isOut ? 'Out of Stock' : isLow ? 'Low Stock' : 'In Stock',
                    beginning_stock: reviewRow?.beginning_stock ?? null,
                    added_stock: reviewRow?.added_stock ?? null,
                    deducted_stock: reviewRow?.deducted_stock ?? null,
                    system_quantity: reviewRow?.system_quantity ?? null,
                    staff_count: countRow?.counted_quantity ?? null,
                    variance: countRow?.discrepancy ?? null,
                    counted_by: reviewRow?.counted_by ?? null,
                    last_updated: item.last_updated ?? null,
                }
            })

            // Summary stats
            const summary = {
                total: branchInventory.length,
                in_stock: branchInventory.filter(i => i.available_stock > i.reorder_level).length,
                low_stock: branchInventory.filter(i => i.available_stock > 0 && i.available_stock <= i.reorder_level).length,
                out_of_stock: branchInventory.filter(i => i.available_stock <= 0).length,
                with_variance: branchInventory.filter(i => {
                    const c = countMap.get(i.ingredient_id)
                    return c?.discrepancy != null && c.discrepancy !== 0
                }).length,
            }

            // Invoke Edge Function with full EOD payload
            const [hours, minutes] = eodTime.split(':').map(Number)
            const now = new Date()
            const sendAt = new Date(now.getFullYear(), now.getMonth(), now.getDate(), hours, minutes, 0, 0)
            if (sendAt <= now) sendAt.setDate(sendAt.getDate() + 1)
            const sendAtISO = sendAt.toISOString()

            // Generate PDF base64 to attach to email
            let pdfBase64 = null
            try {
                const loadScript = (src) => new Promise((resolve, reject) => {
                    if (document.querySelector(`script[src="${src}"]`)) { resolve(); return }
                    const s = document.createElement('script')
                    s.src = src
                    s.onload = resolve
                    s.onerror = reject
                    document.head.appendChild(s)
                })
                await loadScript('https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js')
                await loadScript('https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js')



                const wrapper = document.createElement('div')
                wrapper.style.cssText = 'position:fixed;top:-99999px;left:-99999px;width:1200px;background:#fff;padding:16px;font-family:sans-serif;font-size:13px;'

                const table = document.createElement('table')
                table.style.cssText = 'width:100%;border-collapse:collapse;'

                const thead = document.createElement('thead')
                thead.innerHTML = `
                    <tr style="background:#f9fafb;border-bottom:2px solid #e5e7eb;">
                        <th style="padding:10px 14px;text-align:left;font-size:11px;text-transform:uppercase;color:#6b7280;">Ingredient</th>
                        <th style="padding:10px 14px;text-align:left;font-size:11px;text-transform:uppercase;color:#6b7280;">Category</th>
                        <th style="padding:10px 14px;text-align:left;font-size:11px;text-transform:uppercase;color:#6b7280;">On Hand</th>
                        <th style="padding:10px 14px;text-align:left;font-size:11px;text-transform:uppercase;color:#6b7280;">Reserved</th>
                        <th style="padding:10px 14px;text-align:left;font-size:11px;text-transform:uppercase;color:#6b7280;">Available</th>
                        <th style="padding:10px 14px;text-align:left;font-size:11px;text-transform:uppercase;color:#6b7280;">Status</th>
                        <th style="padding:10px 14px;text-align:left;font-size:11px;text-transform:uppercase;color:#6b7280;">Reorder Level</th>
                        <th style="padding:10px 14px;text-align:left;font-size:11px;text-transform:uppercase;color:#6b7280;">Last Updated</th>
                        <th style="padding:10px 14px;text-align:left;font-size:11px;text-transform:uppercase;color:#6b7280;border-left:2px solid #e5e7eb;">Beg. Stock</th>
                        <th style="padding:10px 14px;text-align:left;font-size:11px;text-transform:uppercase;color:#6b7280;">Added</th>
                        <th style="padding:10px 14px;text-align:left;font-size:11px;text-transform:uppercase;color:#6b7280;">Deducted</th>
                        <th style="padding:10px 14px;text-align:left;font-size:11px;text-transform:uppercase;color:#6b7280;">System Qty</th>
                        <th style="padding:10px 14px;text-align:left;font-size:11px;text-transform:uppercase;color:#6b7280;">Staff Count</th>
                        <th style="padding:10px 14px;text-align:left;font-size:11px;text-transform:uppercase;color:#6b7280;">Variance</th>
                        <th style="padding:10px 14px;text-align:left;font-size:11px;text-transform:uppercase;color:#6b7280;">Counted By</th>
                    </tr>`
                table.appendChild(thead)

                const tbody = document.createElement('tbody')
                branchInventory.forEach((item, idx) => {
                    const isOut = item.available_stock <= 0
                    const isLow = item.available_stock > 0 && item.available_stock <= item.reorder_level
                    const statusLabel = isOut ? 'Out of Stock' : isLow ? 'Low Stock' : 'In Stock'
                    const statusColor = isOut ? '#dc2626' : isLow ? '#d97706' : '#16a34a'
                    const statusBg = isOut ? '#fee2e2' : isLow ? '#ffedd5' : '#dcfce7'
                    const rowBg = idx % 2 === 0 ? '#ffffff' : '#f9fafb'
                    const countReviewRow = (reviewData).find(r => r.ingredient_id === item.ingredient_id)
                    const begStock = countReviewRow != null ? `${countReviewRow.beginning_stock} ${item.unit}` : '—'
                    const addedStock = countReviewRow != null ? `+${countReviewRow.added_stock} ${item.unit}` : '—'
                    const deductedStock = countReviewRow != null ? `-${countReviewRow.deducted_stock} ${item.unit}` : '—'
                    const systemQty = countReviewRow != null ? `${countReviewRow.system_quantity} ${item.unit}` : '—'
                    const staffCountLabel = countReviewRow?.staff_count != null ? `${countReviewRow.staff_count} ${item.unit}` : '—'
                    const varianceVal = countReviewRow?.variance != null ? countReviewRow.variance : null
                    const varianceLabel = varianceVal != null ? `${varianceVal > 0 ? '+' : ''}${varianceVal} ${item.unit}` : '—'
                    const varianceColor = varianceVal == null ? '#6b7280' : varianceVal === 0 ? '#16a34a' : varianceVal > 0 ? '#2563eb' : '#dc2626'
                    const varianceBg = varianceVal == null ? '#f3f4f6' : varianceVal === 0 ? '#dcfce7' : varianceVal > 0 ? '#dbeafe' : '#fee2e2'
                    const countedBy = countReviewRow?.counted_by || '—'
                    const countTime = countReviewRow?.count_time
                        ? new Date(countReviewRow.count_time).toLocaleString('en-PH', { hour: 'numeric', minute: '2-digit' })
                        : ''
                    const tr = document.createElement('tr')
                    tr.style.background = rowBg
                    tr.style.borderBottom = '1px solid #e5e7eb'
                    tr.innerHTML = `
                        <td style="padding:10px 14px;"><div style="font-weight:600;color:#1f2937;">${item.name}</div><div style="font-size:11px;color:#9ca3af;">${item.unit}</div></td>
                        <td style="padding:10px 14px;color:#4b5563;">${item.category}</td>
                        <td style="padding:10px 14px;color:#1f2937;font-weight:500;">${item.current_stock} ${item.unit}</td>
                        <td style="padding:10px 14px;color:#1f2937;font-weight:500;">${item.reserved_stock || 0} ${item.unit}</td>
                        <td style="padding:10px 14px;"><span style="display:inline-block;padding:3px 10px;border-radius:9999px;font-size:11px;font-weight:600;background:${item.available_stock <= 0 ? '#fee2e2' : item.available_stock <= item.reorder_level ? '#ffedd5' : '#dcfce7'};color:${item.available_stock <= 0 ? '#dc2626' : item.available_stock <= item.reorder_level ? '#d97706' : '#16a34a'};">${item.available_stock} ${item.unit}</span></td>
                        <td style="padding:10px 14px;"><span style="display:inline-block;padding:3px 10px;border-radius:9999px;font-size:11px;font-weight:600;background:${statusBg};color:${statusColor};">${statusLabel}</span></td>
                        <td style="padding:10px 14px;color:#4b5563;">${item.reorder_level} ${item.unit}</td>
                        <td style="padding:10px 14px;color:#4b5563;font-size:12px;">${item.last_updated ? new Date(item.last_updated).toLocaleString('en-PH', { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) : 'N/A'}</td>
                        <td style="padding:10px 14px;color:#4b5563;font-size:12px;border-left:2px solid #e5e7eb;">${begStock}</td>
                        <td style="padding:10px 14px;color:#16a34a;font-weight:500;font-size:12px;">${addedStock}</td>
                        <td style="padding:10px 14px;color:#d97706;font-weight:500;font-size:12px;">${deductedStock}</td>
                        <td style="padding:10px 14px;color:#2563eb;font-weight:500;font-size:12px;">${systemQty}</td>
                        <td style="padding:10px 14px;color:#7c3aed;font-weight:500;font-size:12px;">${staffCountLabel}</td>
                        <td style="padding:10px 14px;"><span style="display:inline-block;padding:3px 10px;border-radius:9999px;font-size:11px;font-weight:600;background:${varianceBg};color:${varianceColor};">${varianceLabel}</span></td>
                        <td style="padding:10px 14px;font-size:12px;"><div style="color:#374151;font-weight:500;">${countedBy}</div><div style="color:#9ca3af;font-size:10px;">${countTime}</div></td>`
                    tbody.appendChild(tr)
                })
                table.appendChild(tbody)
                wrapper.appendChild(table)
                document.body.appendChild(wrapper)

                const { jsPDF } = window.jspdf
                const pdf = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a3' })
                const pageW = pdf.internal.pageSize.getWidth()
                const pageH = pdf.internal.pageSize.getHeight()
                const margin = 10
                const contentW = pageW - margin * 2
                const headerH = 14

                const canvas = await window.html2canvas(wrapper, {
                    scale: 1.8, useCORS: true, backgroundColor: '#ffffff', logging: false,
                    width: wrapper.scrollWidth, height: wrapper.scrollHeight,
                    windowWidth: wrapper.scrollWidth, scrollX: 0, scrollY: 0,
                })
                document.body.removeChild(wrapper)

                const imgW = contentW
                const imgH = (canvas.height / canvas.width) * imgW
                pdf.setFillColor(160, 130, 109)
                pdf.rect(margin, margin, contentW, 0.5, 'F')
                pdf.setFont('helvetica', 'bold')
                pdf.setFontSize(13)
                pdf.setTextColor(160, 130, 109)
                pdf.text(`Inventory Report — ${branchName}`, margin, margin + 7)
                pdf.setFont('helvetica', 'normal')
                pdf.setFontSize(8)
                pdf.setTextColor(107, 114, 128)
                pdf.text(`Generated ${new Date().toLocaleString('en-PH', { dateStyle: 'medium', timeStyle: 'short' })}`, pageW - margin, margin + 7, { align: 'right' })

                const usableH = pageH - margin * 2 - headerH
                let yOffset = 0
                let firstPage = true
                while (yOffset < imgH) {
                    if (!firstPage) pdf.addPage()
                    const sliceH = Math.min(usableH, imgH - yOffset)
                    const srcY = (yOffset / imgH) * canvas.height
                    const srcH = (sliceH / imgH) * canvas.height
                    const slice = document.createElement('canvas')
                    slice.width = canvas.width
                    slice.height = srcH
                    slice.getContext('2d').drawImage(canvas, 0, srcY, canvas.width, srcH, 0, 0, canvas.width, srcH)
                    pdf.addImage(slice.toDataURL('image/jpeg', 0.92), 'JPEG', margin, firstPage ? margin + headerH + 2 : margin, contentW, sliceH)
                    yOffset += sliceH
                    firstPage = false
                }

                pdfBase64 = pdf.output('datauristring').split(',')[1]
            } catch (pdfErr) {
                console.warn('PDF generation for email failed:', pdfErr)
            }

            const { error: fnError } = await supabase.functions.invoke('schedule-eod-report', {
                body: {
                    branch_id: branchId,
                    branch_name: branchName,
                    email: staffEmail,
                    send_time: eodTime,
                    send_at: sendAtISO,
                    report_date: today,
                    inventory: inventoryPayload,
                    summary,
                    pdf_base64: pdfBase64,
                    pdf_filename: `Inventory_${branchName.replace(/\s+/g, '_')}_${today}.pdf`,
                }
            })

            if (fnError) {
                console.warn('Edge Function warning:', fnError)
            }

            setShowEODModal(false)
            Swal.fire({
                icon: 'success',
                title: 'EOD Report Scheduled',
                text: `Your end-of-day report will be sent daily at ${eodTime}. The email will include download links for both PDF and CSV exports.`,
                confirmButtonColor: '#A0826D',
                timer: 3000
            })
        } catch (err) {
            console.error('EOD save error:', err)
            Swal.fire({ icon: 'error', title: 'Failed to Save', text: err.message, confirmButtonColor: '#A0826D' })
        } finally {
            setIsSavingEOD(false)
        }
    }


    const formatDateTime = (value) => {
        if (!value) return 'N/A'
        return new Date(value).toLocaleString('en-PH', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit'
        })
    }

    const handleTabsMouseDown = (e) => {
        if (!tabsScrollRef.current) return
        setIsDraggingTabs(true)
        tabsDragStartXRef.current = e.pageX - tabsScrollRef.current.offsetLeft
        tabsScrollStartRef.current = tabsScrollRef.current.scrollLeft
    }

    const handleTabsMouseMove = (e) => {
        if (!isDraggingTabs || !tabsScrollRef.current) return
        e.preventDefault()
        const x = e.pageX - tabsScrollRef.current.offsetLeft
        const walk = x - tabsDragStartXRef.current
        tabsScrollRef.current.scrollLeft = tabsScrollStartRef.current - walk
    }

    const handleTabsMouseUp = () => setIsDraggingTabs(false)
    const handleTabsMouseLeave = () => setIsDraggingTabs(false)

    const handleTabsWheel = (e) => {
        if (!tabsScrollRef.current || e.deltaY === 0) return
        e.preventDefault()
        tabsScrollRef.current.scrollLeft += e.deltaY
    }

    const isWithinDateFilter = (dateValue, filter, customStart = '', customEnd = '') => {
        if (!dateValue || filter === 'all') return true

        const now = new Date()
        const targetDate = new Date(dateValue)
        if (Number.isNaN(targetDate.getTime())) return false

        const startOfToday = new Date(now)
        startOfToday.setHours(0, 0, 0, 0)

        const startOfWeek = new Date(startOfToday)
        const day = startOfWeek.getDay()
        startOfWeek.setDate(startOfWeek.getDate() + (day === 0 ? -6 : 1 - day))

        const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1)
        const startOfYear = new Date(now.getFullYear(), 0, 1)

        switch (filter) {
            case 'day': return targetDate >= startOfToday
            case 'week': return targetDate >= startOfWeek
            case 'month': return targetDate >= startOfMonth
            case 'year': return targetDate >= startOfYear
            case 'custom': {
                if (!customStart || !customEnd) return true
                const from = new Date(customStart)
                const to = new Date(customEnd)
                to.setHours(23, 59, 59, 999)
                return targetDate >= from && targetDate <= to
            }
            default: return true
        }
    }

    const summary = useMemo(() => {
        const totalTrackedIngredients = branchInventory.length

        const lowStockItems = branchInventory.filter(item =>
            item.available_stock > 0 && item.available_stock <= item.reorder_level
        ).length

        const outOfStockItems = branchInventory.filter(item =>
            item.available_stock <= 0
        ).length

        const drinkKeywords = ['beverage', 'coffee', 'drink', 'drinks', 'tea', 'juice', 'shake', 'smoothie']
        const foodKeywords = ['food', 'snack', 'meal', 'pastry', 'bread', 'rice', 'sandwich', 'cake', 'dessert']

        const isDrink = (item) => {
            const cat = String(item.category || '').toLowerCase()
            const cls = String(item.classification || '').toLowerCase()
            return drinkKeywords.some(k => cat.includes(k) || cls.includes(k))
        }

        const isFood = (item) => {
            const cat = String(item.category || '').toLowerCase()
            const cls = String(item.classification || '').toLowerCase()
            return foodKeywords.some(k => cat.includes(k) || cls.includes(k))
        }

        const drinkItems = branchMenuItems.filter(isDrink).length
        const foodItems = branchMenuItems.filter(isFood).length

        return { lowStockItems, outOfStockItems, totalTrackedIngredients, drinkItems, foodItems }
    }, [branchMenuItems, branchInventory])

    const restockRequestRows = useMemo(() => {
        return restockRequests.map(r => {
            const inv = branchInventory.find(i => i.ingredient_id === r.ingredient_id)
                || archivedInventory.find(i => i.ingredient_id === r.ingredient_id)
            return {
                ...r,
                ingredient_name: inv?.name || 'Unknown Ingredient',
                ingredient_unit: inv?.unit || ''
            }
        })
    }, [restockRequests, branchInventory, archivedInventory])


    const pendingOrApprovedRestockRequests = useMemo(() => {
        return restockRequestRows
            .filter(r => r.status === 'pending')
            .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
    }, [restockRequestRows])

    const fulfilledRestockRequests = useMemo(() => {
        return restockRequestRows
            .filter(r => r.status === 'fulfilled')
            .sort((a, b) => new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at))
    }, [restockRequestRows])

    const notYetRequestedItems = useMemo(() => {
        const activeIngredientIds = new Set(pendingOrApprovedRestockRequests.map(r => r.ingredient_id))
        return branchInventory
            .filter(item =>
                (item.available_stock <= 0 || item.available_stock <= item.reorder_level) &&
                !activeIngredientIds.has(item.ingredient_id)
            )
            .sort((a, b) => new Date(b.last_updated || 0) - new Date(a.last_updated || 0))
    }, [branchInventory, pendingOrApprovedRestockRequests])


    const allRestockEntries = useMemo(() => {
        const notReq = notYetRequestedItems.map(item => ({ type: 'not_requested', item, sortKey: item.last_updated || '' }))
        const active = pendingOrApprovedRestockRequests.map(req => ({ type: 'active', req, sortKey: req.created_at }))
        const fulfilled = fulfilledRestockRequests.map(req => ({ type: 'fulfilled', req, sortKey: req.updated_at || req.created_at }))
        return [...notReq, ...active, ...fulfilled].sort((a, b) => new Date(b.sortKey || 0) - new Date(a.sortKey || 0))
    }, [notYetRequestedItems, pendingOrApprovedRestockRequests, fulfilledRestockRequests])

    const filteredInventory = branchInventory.filter(item => {
        const query = inventorySearch.toLowerCase()
        const matchesSearch = (
            item.name.toLowerCase().includes(query) ||
            item.category.toLowerCase().includes(query) ||
            item.unit.toLowerCase().includes(query)
        )
        const itemStatus = item.available_stock <= 0
            ? 'out_of_stock'
            : item.available_stock <= item.reorder_level
                ? 'low_stock'
                : 'in_stock'
        const matchesStatus = inventoryStockStatusFilter === 'all' || itemStatus === inventoryStockStatusFilter
        return matchesSearch && matchesStatus
    })

    const filteredMenuItems = branchMenuItems.filter(item => {
        const query = menuItemsSearch.toLowerCase()
        return (
            item.name.toLowerCase().includes(query) ||
            item.category.toLowerCase().includes(query) ||
            item.classification.toLowerCase().includes(query)
        )
    })

    const paginatedMenuItems = [...filteredMenuItems]
        .sort((a, b) => a.name.localeCompare(b.name))
        .slice((menuItemsPage - 1) * PAGE_SIZE, menuItemsPage * PAGE_SIZE)

    const filteredRecipes = recipeReference.filter(item => {
        const query = recipeSearch.toLowerCase()
        const matchesSearch = (
            item.name.toLowerCase().includes(query) ||
            item.category.toLowerCase().includes(query) ||
            item.classification.toLowerCase().includes(query) ||
            item.ingredients.some(ing => ing.name.toLowerCase().includes(query))
        )
        const matchesCategory = recipeCategoryFilter === 'All' || item.category === recipeCategoryFilter
        const matchesClassification = recipeClassificationFilter === 'All' || item.classification === recipeClassificationFilter
        return matchesSearch && matchesCategory && matchesClassification
    })

    const filteredTransactions = inventoryTransactions.filter(transaction => {
        const query = transactionSearch.toLowerCase()
        const matchesSearch =
            String(transaction.ingredient_name || '').toLowerCase().includes(query) ||
            String(transaction.transaction_type || '').toLowerCase().includes(query) ||
            String(transaction.reference_type || '').toLowerCase().includes(query) ||
            String(transaction.notes || '').toLowerCase().includes(query)
        const matchesDate = isWithinDateFilter(transaction.created_at, inventoryDateFilter, inventoryCustomStart, inventoryCustomEnd)
        return matchesSearch && matchesDate
    })

    const filteredPOSTransactions = posTransactions.filter(transaction => {
        const query = posTransactionSearch.toLowerCase()
        const matchesSearch =
            String(transaction.reference_number || '').toLowerCase().includes(query) ||
            String(transaction.payment_method || '').toLowerCase().includes(query) ||
            String(transaction.payment_status || '').toLowerCase().includes(query) ||
            String(transaction.order_status || '').toLowerCase().includes(query)
        const matchesDate = isWithinDateFilter(transaction.created_at, posDateFilter, posCustomStart, posCustomEnd)
        const matchesOrderStatus = posOrderStatusFilter === 'all' || transaction.order_status === posOrderStatusFilter
        return matchesSearch && matchesDate && matchesOrderStatus
    })
    const paginatedInventory = filteredInventory.slice(
        (inventoryPage - 1) * PAGE_SIZE, inventoryPage * PAGE_SIZE
    )
    const paginatedRecipes = filteredRecipes.slice(
        (recipesPage - 1) * PAGE_SIZE, recipesPage * PAGE_SIZE
    )
    const paginatedTransactions = filteredTransactions.slice(
        (transactionsPage - 1) * PAGE_SIZE, transactionsPage * PAGE_SIZE
    )
    const paginatedPOS = filteredPOSTransactions.slice(
        (posPage - 1) * PAGE_SIZE, posPage * PAGE_SIZE
    )
    const paginatedAdjustmentRequests = adjustmentRequests.slice(
        (adjustmentRequestsPage - 1) * PAGE_SIZE, adjustmentRequestsPage * PAGE_SIZE
    )
    const paginatedStockCountReview = stockCountReview.slice(
        (stockCountReviewPage - 1) * PAGE_SIZE, stockCountReviewPage * PAGE_SIZE
    )

    const filteredAvailableIngredients = [...availableIngredients]
        .filter(ingredient => {
            const query = ingredientSearchQuery.toLowerCase()
            return (
                ingredient.name.toLowerCase().includes(query) ||
                String(ingredient.unit || '').toLowerCase().includes(query) ||
                String(ingredient.category || '').toLowerCase().includes(query)
            )
        })
        .sort((a, b) => a.name.localeCompare(b.name))

    const normalizeAddStockFormData = (data = {}) => ({
        ingredient_id: String(data.ingredient_id || '').trim(),
        input_mode: String(data.input_mode || 'manual').trim(),
        current_stock: String(data.current_stock || '').trim(),
        package_count: String(data.package_count || '').trim(),
        reorder_level: String(data.reorder_level || '').trim()
    })

    const normalizeAdjustStockFormData = (data = {}) => ({
        mode: String(data.mode || 'add').trim(),
        input_mode: String(data.input_mode || 'manual').trim(),
        quantity: String(data.quantity || '').trim(),
        amount_per_package: String(data.amount_per_package || '').trim(),
        package_count: String(data.package_count || '').trim(),
        reorder_level: String(data.reorder_level || '').trim()
    })

    const hasAddStockChanges =
        JSON.stringify(normalizeAddStockFormData(addStockFormData)) !==
        JSON.stringify(normalizeAddStockFormData(initialAddStockFormData || { ingredient_id: '', current_stock: '', reorder_level: '' }))

    const hasAdjustStockChanges =
        JSON.stringify(normalizeAdjustStockFormData(stockFormData)) !==
        JSON.stringify(normalizeAdjustStockFormData(initialStockFormData || { mode: 'add', input_mode: 'manual', quantity: '', amount_per_package: '', package_count: '', reorder_level: '' }))

    const selectedAddIngredient = availableIngredients.find(
        ingredient => String(ingredient.ingredient_id) === String(addStockFormData.ingredient_id)
    )

    // ─── Pagination  ─────────────────────────────────────────────
    function Pagination({ page, totalItems, pageSize, onPageChange }) {
        const totalPages = Math.ceil(totalItems / pageSize)
        if (totalPages <= 1) return null

        return (
            <div className="flex items-center justify-between px-4 py-2 rounded-b-xl"
            >
                <span style={{ fontSize: 14, color: C.midgrey }}>
                    Showing <span className="font-medium text-gray-700">{Math.min((page - 1) * pageSize + 1, totalItems)}–{Math.min(page * pageSize, totalItems)}</span> of <span className="font-medium text-gray-700">{totalItems}</span> items
                </span>
                <div className="flex items-center gap-1">
                    <button
                        onClick={() => onPageChange(Math.max(1, page - 1))}
                        disabled={page === 1}
                        style={{
                            padding: "3px 10px", borderRadius: 8,
                            border: `1px solid ${C.border}`, background: C.card,
                            color: page === 1 ? C.border : C.textSoft,
                            fontSize: 14, cursor: page === 1 ? "not-allowed" : "pointer",
                        }}>
                        ← Prev
                    </button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1)
                        .filter(n => n === 1 || n === totalPages || Math.abs(n - page) <= 1)
                        .reduce((acc, n, idx, arr) => {
                            if (idx > 0 && n - arr[idx - 1] > 1) acc.push("…")
                            acc.push(n)
                            return acc
                        }, [])
                        .map((n, idx) =>
                            n === "…" ? (
                                <span key={`e-${idx}`} style={{ fontSize: 16, color: C.midgrey, padding: "0 2px" }}>…</span>
                            ) : (
                                <button key={n} onClick={() => onPageChange(n)}
                                    style={{
                                        width: 28, height: 28, borderRadius: 8,
                                        border: `1px solid ${n === page ? C.nude : C.border}`,
                                        background: n === page ? C.nude : C.card,
                                        color: n === page ? "#fff" : C.textSoft,
                                        fontSize: 14, fontWeight: n === page ? 600 : 400,
                                        cursor: "pointer",
                                    }}>
                                    {n}
                                </button>
                            )
                        )
                    }
                    <button
                        onClick={() => onPageChange(Math.min(totalPages, page + 1))}
                        disabled={page === totalPages}
                        style={{
                            padding: "3px 10px", borderRadius: 8,
                            border: `1px solid ${C.border}`, background: C.card,
                            color: page === totalPages ? C.border : C.textSoft,
                            fontSize: 14, cursor: page === totalPages ? "not-allowed" : "pointer",
                        }}>
                        Next →
                    </button>
                </div>
            </div>
        )
    }

    return (
        <div className="bg-gray-50 min-h-screen">
            <Helmet>
                <title>KaPerk</title>
            </Helmet>
            <div className="p-3 sm:p-6">
                {/* Header Card */}
                <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
                    <div className="px-6 py-5 border-b border-gray-200">
                        <div className="flex items-center justify-between gap-4 flex-wrap">
                            <div>
                                <h2 className="text-xl font-bold text-gray-800">Branch Inventory Management</h2>
                                <p className="text-sm text-gray-500 mt-1">
                                    {loading ? 'Loading assigned branch...' : `Assigned Branch: ${branchName || 'Unassigned'}`}
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="p-6">
                        {loading ? (
                            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                                {[...Array(6)].map((_, i) => (
                                    <div key={i} className="h-24 bg-gray-100 rounded-lg animate-pulse"></div>
                                ))}
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                                <div className="p-4 bg-orange-50 rounded-lg border border-orange-100">
                                    <p className="text-sm text-gray-600">Low Stock Ingredients</p>
                                    <p className="text-2xl font-bold text-gray-800 mt-2">{summary.lowStockItems}</p>
                                    <p className="text-xs text-gray-500 mt-1">Available stock is at or below reorder level</p>
                                </div>
                                <div className="p-4 bg-red-50 rounded-lg border border-red-100">
                                    <p className="text-sm text-gray-600">Out of Stock</p>
                                    <p className="text-2xl font-bold text-gray-800 mt-2">{summary.outOfStockItems}</p>
                                    <p className="text-xs text-gray-500 mt-1">No available stock after reservations</p>
                                </div>
                                <div className="p-4 bg-blue-50 rounded-lg border border-blue-100">
                                    <p className="text-sm text-gray-600">Tracked Ingredients</p>
                                    <p className="text-2xl font-bold text-gray-800 mt-2">{summary.totalTrackedIngredients}</p>
                                    <p className="text-xs text-gray-500 mt-1">Inventory rows for this branch</p>
                                </div>
                                <div className="p-4 bg-green-50 rounded-lg border border-green-100">
                                    <p className="text-sm text-gray-600">Drink Items</p>
                                    <p className="text-2xl font-bold text-gray-800 mt-2">{summary.drinkItems}</p>
                                    <p className="text-xs text-gray-500 mt-1">Coffee, tea, and beverage items</p>
                                </div>
                                <div className="p-4 bg-purple-50 rounded-lg border border-purple-100">
                                    <p className="text-sm text-gray-600">Food / Snack Items</p>
                                    <p className="text-2xl font-bold text-gray-800 mt-2">{summary.foodItems}</p>
                                    <p className="text-xs text-gray-500 mt-1">Classification-based branch count</p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Tabs */}
                <div
                    ref={tabsScrollRef}
                    onMouseDown={handleTabsMouseDown}
                    onMouseMove={handleTabsMouseMove}
                    onMouseUp={handleTabsMouseUp}
                    onMouseLeave={handleTabsMouseLeave}
                    onWheel={handleTabsWheel}
                    className={`flex items-center gap-2 mb-6 overflow-x-auto scrollbar-hide select-none ${isDraggingTabs ? 'cursor-grabbing' : 'cursor-grab'}`}
                >
                    {['overview', 'inventory', 'recipes', 'transactions', 'pos_transactions', 'remake_activity']
                        .filter((tab) => tab !== 'remake_activity' || staffRole === 'Admin')
                        .map((tab) => (
                            <button
                                key={tab}
                                onClick={() => setActiveTab(tab)}
                                className={`px-5 py-2.5 rounded-lg font-medium transition-colors flex-shrink-0 whitespace-nowrap ${activeTab === tab
                                    ? 'bg-[#A0826D] text-white'
                                    : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
                                    }`}
                            >
                                {tab === 'overview' && 'Overview'}
                                {tab === 'inventory' && 'Branch Inventory'}
                                {tab === 'recipes' && 'Menu Item Recipes'}
                                {tab === 'transactions' && 'Inventory Transactions'}
                                {tab === 'pos_transactions' && 'POS Transactions'}
                                {tab === 'remake_activity' && 'Remake Activity'}
                            </button>
                        ))}

                </div>

                {/* Overview Tab */}
                {activeTab === 'overview' && (
                    <>
                    <div className={`grid grid-cols-1 ${staffRole === 'Barista' ? 'lg:grid-cols-2' : ''} gap-6`}>
                        {staffRole === 'Barista' && (
                            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                                <div className="px-6 py-4 border-b border-gray-200">
                                    <h3 className="text-lg font-bold text-gray-800">Assigned Menu Items</h3>
                                    <p className="text-sm text-gray-500 mt-1">Branch-visible products only</p>
                                    <div className="relative mt-3">
                                        <input
                                            type="text"
                                            placeholder="Search menu items..."
                                            value={menuItemsSearch}
                                            onChange={(e) => setMenuItemsSearch(e.target.value)}
                                            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#A0826D]"
                                        />
                                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24">
                                                <path fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10a7 7 0 1 0 14 0a7 7 0 1 0-14 0m18 11l-6-6" />
                                            </svg>
                                        </span>
                                    </div>
                                </div>
                                <div className="divide-y divide-gray-200 max-h-[420px] overflow-y-auto">
                                    {filteredMenuItems.length === 0 ? (
                                        <div className="px-6 py-8 text-center text-gray-500">
                                            {menuItemsSearch ? 'No menu items match your search.' : 'No assigned menu items found.'}
                                        </div>
                                    ) : (
                                        paginatedMenuItems.map(item => (
                                            <div key={item.id} className="px-6 py-4 hover:bg-gray-50">
                                                <div className="flex items-center gap-3">
                                                    {item.item_img ? (
                                                        <img src={item.item_img} alt={item.name} className="w-12 h-12 rounded-lg object-cover border border-gray-200" />
                                                    ) : (
                                                        <div className="w-12 h-12 rounded-lg bg-gray-100 border border-gray-200 flex items-center justify-center text-[10px] text-gray-400">N/A</div>
                                                    )}
                                                    <div className="flex-1">
                                                        <p className="font-medium text-gray-800">{item.name}</p>
                                                        <p className="text-sm text-gray-500">{item.category} • {item.classification}</p>
                                                    </div>
                                                    <div className="text-sm font-semibold text-gray-800">{formatCurrency(item.price)}</div>
                                                </div>
                                            </div>
                                        ))
                                    )}
                                </div>
                                <Pagination
                                    page={menuItemsPage}
                                    totalItems={filteredMenuItems.length}
                                    pageSize={PAGE_SIZE}
                                    onPageChange={setMenuItemsPage}
                                />
                            </div>
                        )}

                        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                            <div className="px-6 py-4 border-b border-gray-200">
                                <h3 className="text-lg font-bold text-gray-800">Recent Inventory Transactions</h3>
                                <p className="text-sm text-gray-500 mt-1">Latest stock movement in this branch</p>
                            </div>
                            <div className="divide-y divide-gray-200 max-h-[420px] overflow-y-auto">
                                {inventoryTransactions.length === 0 ? (
                                    <div className="px-6 py-8 text-center text-gray-500">No inventory transactions found.</div>
                                ) : (
                                    inventoryTransactions.slice(0, 10).map(transaction => (
                                        <div key={transaction.inventory_transaction_id} className="px-6 py-4 hover:bg-gray-50">
                                            <div className="flex items-start justify-between gap-4">
                                                <div>
                                                    <p className="font-medium text-gray-800">{transaction.ingredient_name}</p>
                                                    <p className="text-sm text-gray-500 mt-1">{transaction.transaction_type} • {transaction.reference_type}</p>
                                                    <p className="text-xs text-gray-400 mt-1">{formatDateTime(transaction.created_at)}</p>
                                                    {transaction.notes && <p className="text-xs text-gray-400 mt-1">{transaction.notes}</p>}
                                                </div>
                                                <div className="text-right">
                                                    <p className="font-semibold text-gray-800">{transaction.quantity} {transaction.unit}</p>
                                                    <p className="text-xs text-gray-500 mt-1">{transaction.ingredient_category}</p>
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>
                    </div>

                    </>
                )}

                {/* Remake Activity Tab */}
                {activeTab === 'remake_activity' && staffRole === 'Admin' && (
                    <RemakeActivityCard branchId={branchId} baristaId={null} showFilters isAdmin />
                )}

                {/* Inventory Tab */}
                {activeTab === 'inventory' && (
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                        {/* ── Card header (search + buttons) ── */}
                        <div className="px-6 py-4 border-b border-gray-200">
                            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 flex-wrap">
                                <div>
                                    <div className="flex items-center gap-2 flex-wrap">
                                        <h3 className="text-lg font-bold text-gray-800">Branch Inventory</h3>
                                        {staffRole === 'Admin' && (
                                            <button
                                                onClick={() => setShowEODModal(true)}
                                                className="h-8 px-3 py-1.5 bg-white border border-gray-300 text-gray-600 rounded-lg text-xs font-medium hover:bg-gray-50 flex items-center gap-1.5 whitespace-nowrap"
                                                title="Schedule EOD Email Report"
                                            >
                                                <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24">
                                                    <path fill="currentColor" d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10s10-4.48 10-10S17.52 2 12 2m0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8s8 3.59 8 8s-3.59 8-8 8m.5-13H11v6l5.25 3.15l.75-1.23l-4.5-2.67z" />
                                                </svg>
                                                {eodTime ? `EOD: ${eodTime}` : 'Set EOD Time'}
                                            </button>
                                        )}
                                        {staffRole === 'Admin' && inventorySubTab === 'stock_list' && (
                                            <div className="relative" ref={downloadMenuRef}>
                                                <button
                                                    onClick={() => setShowDownloadMenu(prev => !prev)}
                                                    className="h-8 px-3 py-1.5 bg-green-600 text-white rounded-lg text-xs font-medium hover:bg-green-700 flex items-center gap-1.5 whitespace-nowrap"
                                                >
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24">
                                                        <path fill="#fff" d="m12 16l-5-5l1.4-1.45l2.6 2.6V4h2v8.15l2.6-2.6L17 11zm-6 4q-.825 0-1.412-.587T4 18v-3h2v3h12v-3h2v3q0 .825-.587 1.413T18 20z" />
                                                    </svg>
                                                    Download
                                                </button>
                                                {showDownloadMenu && (
                                                    <div className="absolute left-0 mt-2 w-44 rounded-lg shadow-lg border z-50 bg-white border-gray-200">
                                                        <button
                                                            onClick={() => { getActiveDownloadHandlers().pdf(); setShowDownloadMenu(false) }}
                                                            disabled={pdfLoading}
                                                            className="flex items-center gap-2 w-full text-left px-4 py-2.5 text-xs hover:bg-gray-50 rounded-t-lg"
                                                        >
                                                            <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24">
                                                                <path fill="currentColor" d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                                                <polyline points="14 2 14 8 20 8" stroke="#fff" strokeWidth="2" fill="none" />
                                                            </svg>
                                                            {pdfLoading ? 'Exporting…' : 'Download as PDF'}
                                                        </button>
                                                        <button
                                                            onClick={() => { getActiveDownloadHandlers().csv(); setShowDownloadMenu(false) }}
                                                            className="flex items-center gap-2 w-full text-left px-4 py-2.5 text-xs hover:bg-gray-50 rounded-b-lg border-t border-gray-100"
                                                        >
                                                            <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24">
                                                                <path fill="currentColor" d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                                                <polyline points="14 2 14 8 20 8" stroke="#fff" strokeWidth="2" fill="none" />
                                                            </svg>
                                                            Download as CSV
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                    <p className="text-sm text-gray-500 mt-1">
                                        Manage ingredient quantities for your assigned branch
                                    </p>
                                </div>
                                <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-3">
                                    {inventorySubTab === 'stock_list' && (
                                        <>
                                            <div className="relative w-full sm:w-64 lg:w-72">
                                                <input
                                                    type="text"
                                                    placeholder="Search Inventory items..."
                                                    value={inventorySearch}
                                                    onChange={(e) => setInventorySearch(e.target.value)}
                                                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#A0826D]"
                                                />
                                                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24">
                                                        <path fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10a7 7 0 1 0 14 0a7 7 0 1 0-14 0m18 11l-6-6" />
                                                    </svg>
                                                </span>
                                            </div>
                                            {staffRole === 'Admin' && (
                                                <div className="flex items-center gap-3 w-full sm:w-auto">
                                                    <select
                                                        value={inventoryStockStatusFilter}
                                                        onChange={(e) => setInventoryStockStatusFilter(e.target.value)}
                                                        className="flex-1 sm:flex-none px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#A0826D]"
                                                    >
                                                        <option value="all">All Statuses</option>
                                                        <option value="in_stock">In Stock</option>
                                                        <option value="low_stock">Low Stock</option>
                                                        <option value="out_of_stock">Out of Stock</option>
                                                    </select>
                                                    <button
                                                        onClick={() => { fetchArchivedInventory(branchId); setShowArchivedModal(true) }}
                                                        className="p-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 flex-shrink-0"
                                                        title="View Archived Stock Items"
                                                    >
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24">
                                                            <path fill="currentColor" d="M3 3h18a1 1 0 0 1 1 1v4a1 1 0 0 1-1 1h-1v11a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V9H3a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1m3 6v10h12V9zm2 2h8v2H8zm0 4h8v2H8zM5 5v2h14V5z" />
                                                        </svg>
                                                    </button>
                                                </div>
                                            )}
                                            {staffRole === 'Admin' && (
                                                <button
                                                    onClick={() => {
                                                        setNewIngredientFormData({ name: '', unit: '', category: '', package_label: '', stock_per_package: '', package_price: '', current_stock: '', reorder_level: '' })
                                                        setShowAddIngredientModal(true)
                                                    }}
                                                    className="px-4 py-2 bg-gray-700 text-white rounded-lg font-medium hover:bg-gray-800 whitespace-nowrap"
                                                >
                                                    + New Ingredient
                                                </button>
                                            )}
                                            {staffRole === 'Admin' && (
                                                <button
                                                    onClick={openAddStockModal}
                                                    className="px-4 py-2 bg-[#A0826D] text-white rounded-lg font-medium hover:bg-[#8c705b] whitespace-nowrap"
                                                >
                                                    + Add Stock Item
                                                </button>
                                            )}
                                        </>
                                    )}

                                    {inventorySubTab === 'stock_count_review' && (
                                        <div className="flex items-center gap-3">
                                            <input
                                                type="date"
                                                value={stockCountReviewDate}
                                                max={new Date().toISOString().split('T')[0]}
                                                onChange={(e) => setStockCountReviewDate(e.target.value)}
                                                className="h-8 px-3 py-1.5 border border-gray-300 rounded-lg text-xs focus:outline-none focus:ring-2 focus:ring-[#A0826D]"
                                            />
                                            <button
                                                onClick={() => fetchStockCountReview(stockCountReviewDate)}
                                                className="h-8 px-3 py-1.5 bg-[#A0826D] text-white rounded-lg text-xs font-medium hover:bg-[#8c705b]"
                                            >
                                                Refresh
                                            </button>
                                            <div className="relative" ref={stockCountDownloadMenuRef}>
                                                <button
                                                    onClick={() => setShowStockCountDownloadMenu(prev => !prev)}
                                                    className="h-8 px-3 py-1.5 bg-green-600 text-white rounded-lg text-xs font-medium hover:bg-green-700 flex items-center gap-1.5 whitespace-nowrap"
                                                >
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24">
                                                        <path fill="#fff" d="m12 16l-5-5l1.4-1.45l2.6 2.6V4h2v8.15l2.6-2.6L17 11zm-6 4q-.825 0-1.412-.587T4 18v-3h2v3h12v-3h2v3q0 .825-.587 1.413T18 20z" />
                                                    </svg>
                                                    Download
                                                </button>
                                                {showStockCountDownloadMenu && (
                                                    <div className="absolute left-0 mt-2 w-44 rounded-lg shadow-lg border z-50 bg-white border-gray-200">
                                                        <button
                                                            onClick={() => { downloadStockCountReviewPDF(); setShowStockCountDownloadMenu(false) }}
                                                            disabled={stockCountPdfLoading}
                                                            className="flex items-center gap-2 w-full text-left px-4 py-2.5 text-xs hover:bg-gray-50 rounded-t-lg"
                                                        >
                                                            <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24">
                                                                <path fill="currentColor" d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                                                <polyline points="14 2 14 8 20 8" stroke="#fff" strokeWidth="2" fill="none" />
                                                            </svg>
                                                            {stockCountPdfLoading ? 'Exporting…' : 'Download as PDF'}
                                                        </button>
                                                        <button
                                                            onClick={() => { downloadStockCountReviewExcel(); setShowStockCountDownloadMenu(false) }}
                                                            className="flex items-center gap-2 w-full text-left px-4 py-2.5 text-xs hover:bg-gray-50 rounded-b-lg border-t border-gray-100"
                                                        >
                                                            <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24">
                                                                <path fill="currentColor" d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                                                <polyline points="14 2 14 8 20 8" stroke="#fff" strokeWidth="2" fill="none" />
                                                            </svg>
                                                            Download as CSV
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}

                                    {inventorySubTab === 'restock_request' && (
                                        <div className="flex items-center gap-3">
                                            <div className="relative" ref={restockDownloadMenuRef}>
                                                <button
                                                    onClick={() => setShowRestockDownloadMenu(prev => !prev)}
                                                    className="h-8 px-3 py-1.5 bg-green-600 text-white rounded-lg text-xs font-medium hover:bg-green-700 flex items-center gap-1.5 whitespace-nowrap"
                                                >
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24">
                                                        <path fill="#fff" d="m12 16l-5-5l1.4-1.45l2.6 2.6V4h2v8.15l2.6-2.6L17 11zm-6 4q-.825 0-1.412-.587T4 18v-3h2v3h12v-3h2v3q0 .825-.587 1.413T18 20z" />
                                                    </svg>
                                                    Download
                                                </button>
                                                {showRestockDownloadMenu && (
                                                    <div className="absolute left-0 mt-2 w-56 rounded-lg shadow-lg border z-50 bg-white border-gray-200">
                                                        <button
                                                            onClick={() => { downloadRestockRequestsPDF(); setShowRestockDownloadMenu(false) }}
                                                            disabled={restockPdfLoading}
                                                            className="flex items-center gap-2 w-full text-left px-4 py-2.5 text-xs hover:bg-gray-50 rounded-t-lg"
                                                            title="Exports the full request history as a table"
                                                        >
                                                            <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24">
                                                                <path fill="currentColor" d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                                                <polyline points="14 2 14 8 20 8" stroke="#fff" strokeWidth="2" fill="none" />
                                                            </svg>
                                                            {restockPdfLoading ? 'Exporting…' : 'Request History (PDF)'}
                                                        </button>
                                                        <button
                                                            onClick={() => { downloadRestockRequestsExcel(); setShowRestockDownloadMenu(false) }}
                                                            className="flex items-center gap-2 w-full text-left px-4 py-2.5 text-xs hover:bg-gray-50 rounded-b-lg border-t border-gray-100"
                                                            title="Exports a printable restock order form"
                                                        >
                                                            <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24">
                                                                <path fill="currentColor" d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                                                <polyline points="14 2 14 8 20 8" stroke="#fff" strokeWidth="2" fill="none" />
                                                            </svg>
                                                            Request Form (CSV)
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}

                                    {inventorySubTab === 'adjustment_requests' && staffRole === 'Admin' && (
                                        <div className="flex items-center gap-3">
                                            <div className="relative" ref={adjustmentDownloadMenuRef}>
                                                <button
                                                    onClick={() => setShowAdjustmentDownloadMenu(prev => !prev)}
                                                    className="h-8 px-3 py-1.5 bg-green-600 text-white rounded-lg text-xs font-medium hover:bg-green-700 flex items-center gap-1.5 whitespace-nowrap"
                                                >
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24">
                                                        <path fill="#fff" d="m12 16l-5-5l1.4-1.45l2.6 2.6V4h2v8.15l2.6-2.6L17 11zm-6 4q-.825 0-1.412-.587T4 18v-3h2v3h12v-3h2v3q0 .825-.587 1.413T18 20z" />
                                                    </svg>
                                                    Download
                                                </button>
                                                {showAdjustmentDownloadMenu && (
                                                    <div className="absolute left-0 mt-2 w-44 rounded-lg shadow-lg border z-50 bg-white border-gray-200">
                                                        <button
                                                            onClick={() => { downloadAdjustmentRequestsPDF(); setShowAdjustmentDownloadMenu(false) }}
                                                            disabled={adjustmentPdfLoading}
                                                            className="flex items-center gap-2 w-full text-left px-4 py-2.5 text-xs hover:bg-gray-50 rounded-t-lg"
                                                        >
                                                            <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24">
                                                                <path fill="currentColor" d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                                                <polyline points="14 2 14 8 20 8" stroke="#fff" strokeWidth="2" fill="none" />
                                                            </svg>
                                                            {adjustmentPdfLoading ? 'Exporting…' : 'Download as PDF'}
                                                        </button>
                                                        <button
                                                            onClick={() => { downloadAdjustmentRequestsExcel(); setShowAdjustmentDownloadMenu(false) }}
                                                            className="flex items-center gap-2 w-full text-left px-4 py-2.5 text-xs hover:bg-gray-50 rounded-b-lg border-t border-gray-100"
                                                        >
                                                            <svg xmlns="http://www.w3.org/2000/svg" width="13" height="13" viewBox="0 0 24 24">
                                                                <path fill="currentColor" d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                                                                <polyline points="14 2 14 8 20 8" stroke="#fff" strokeWidth="2" fill="none" />
                                                            </svg>
                                                            Download as CSV
                                                        </button>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* ── Sub-tabs (Admin only) ── */}
                            {staffRole === 'Admin' && (
                                <div className="flex gap-2 mt-4 overflow-x-auto pb-1">
                                    <button
                                        onClick={() => setInventorySubTab('stock_list')}
                                        className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors flex-shrink-0 whitespace-nowrap ${inventorySubTab === 'stock_list' ? 'bg-[#A0826D] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                            }`}
                                    >
                                        Stock List
                                    </button>
                                    <button
                                        onClick={() => setInventorySubTab('adjustment_requests')}
                                        className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors flex-shrink-0 whitespace-nowrap ${inventorySubTab === 'adjustment_requests' ? 'bg-[#A0826D] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                                    >
                                        Adjustment Requests
                                        {adjustmentRequests.filter(r => r.status === 'pending').length > 0 && (
                                            <span className="ml-1.5 px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-red-500 text-white">
                                                {adjustmentRequests.filter(r => r.status === 'pending').length}
                                            </span>
                                        )}
                                    </button>
                                    <button
                                        onClick={() => setInventorySubTab('stock_count_review')}
                                        className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors flex-shrink-0 whitespace-nowrap ${inventorySubTab === 'stock_count_review' ? 'bg-[#A0826D] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                            }`}
                                    >
                                        Stock Count Review
                                    </button>
                                    <button
                                        onClick={() => setInventorySubTab('restock_request')}
                                        className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors flex-shrink-0 whitespace-nowrap ${inventorySubTab === 'restock_request' ? 'bg-[#A0826D] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                            }`}
                                    >
                                        Restock Requests
                                        {branchInventory.filter(i => i.available_stock <= i.reorder_level).length > 0 && (
                                            <span className="ml-1.5 px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-red-500 text-white">
                                                {branchInventory.filter(i => i.available_stock <= i.reorder_level).length}
                                            </span>
                                        )}
                                    </button>
                                </div>
                            )}

                            {inventorySubTab === 'restock_request' && staffRole === 'Admin' && (
                                <div className="flex items-center gap-1.5 flex-wrap mt-3">
                                    {[
                                        { key: 'all', label: 'All', count: notYetRequestedItems.length + pendingOrApprovedRestockRequests.length + fulfilledRestockRequests.length },
                                        { key: 'not_requested', label: 'Not Requested', count: notYetRequestedItems.length },
                                        { key: 'pending', label: 'Pending', count: pendingOrApprovedRestockRequests.length },
                                        { key: 'fulfilled', label: 'Fulfilled', count: fulfilledRestockRequests.length },
                                    ].map(f => (
                                        <button
                                            key={f.key}
                                            onClick={() => setRestockFilter(f.key)}
                                            className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors flex items-center gap-1 ${restockFilter === f.key ? 'bg-[#A0826D] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                                        >
                                            {f.label}
                                            <span className={`px-1.5 py-0.5 rounded-full text-[10px] font-bold ${restockFilter === f.key ? 'bg-white/25 text-white' : 'bg-white text-gray-500'}`}>
                                                {f.count}
                                            </span>
                                        </button>
                                    ))}
                                </div>
                            )}

                            {/* ── Sub-tabs (Barista only) ── */}
                            {staffRole === 'Barista' && (
                                <div className="flex gap-2 mt-4 overflow-x-auto pb-1">
                                    <button
                                        onClick={() => setInventorySubTab('stock_list')}
                                        className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors flex-shrink-0 whitespace-nowrap ${inventorySubTab === 'stock_list' ? 'bg-[#A0826D] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                            }`}
                                    >
                                        Stock List
                                    </button>
                                    <button
                                        onClick={() => setInventorySubTab('adjustment_requests')}
                                        className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors flex-shrink-0 whitespace-nowrap ${inventorySubTab === 'adjustment_requests' ? 'bg-[#A0826D] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                                    >
                                        My Adjustment Requests
                                        {adjustmentRequests.filter(r => r.status === 'pending' && (
                                            selectedBarista ? r.requested_by_barista_id === selectedBarista.barista_id : r.requested_by_staff_id === staffRecordId
                                        )).length > 0 && (
                                                <span className="ml-1.5 px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-red-500 text-white">
                                                    {adjustmentRequests.filter(r => r.status === 'pending' && (
                                                        selectedBarista ? r.requested_by_barista_id === selectedBarista.barista_id : r.requested_by_staff_id === staffRecordId
                                                    )).length}
                                                </span>
                                            )}
                                    </button>
                                </div>
                            )}
                        </div>

                        {/* ── Stock List sub-tab ── */}
                        {inventorySubTab === 'stock_list' && (
                            <div className="overflow-x-auto" ref={inventoryTableRef}>
                                <table className="w-full">
                                    <thead className="bg-gray-50 border-b border-gray-200">
                                        <tr>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ingredient</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                                            {staffRole !== 'Barista' && <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">On Hand</th>}
                                            {staffRole !== 'Barista' && <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reserved</th>}
                                            {staffRole !== 'Barista' && <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Available</th>}
                                            {staffRole !== 'Barista' && <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>}
                                            {staffRole !== 'Barista' && <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reorder Level</th>}
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Updated</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {filteredInventory.length === 0 ? (
                                            <tr>
                                                <td colSpan={staffRole === 'Barista' ? 4 : 9} className="px-6 py-8 text-center text-gray-500">
                                                    No branch inventory records found.
                                                </td>
                                            </tr>
                                        ) : (
                                            paginatedInventory.map(item => {
                                                const isOutOfStock = item.available_stock <= 0
                                                const isLowStock = item.available_stock > 0 && item.available_stock <= item.reorder_level

                                                return (
                                                    <tr key={item.id} className={`hover:bg-gray-50 ${isOutOfStock ? 'bg-red-50/40' : isLowStock ? 'bg-orange-50/40' : ''}`}>
                                                        <td className="px-6 py-3">
                                                            <p className="font-medium text-gray-800">{item.name}</p>
                                                            <p className="text-xs text-gray-500 mt-1">{item.unit}</p>
                                                            {/* Count type completion badges — Barista only, since only Baristas do BOD/EOD counts */}
                                                            {staffRole === 'Barista' && (
                                                                <div className="flex items-center gap-1 mt-1.5 flex-wrap">
                                                                    {['beginning', 'end_of_shift'].map(type => {
                                                                        const done = (todayCountTypes[item.ingredient_id] || new Set()).has(type)
                                                                        return (
                                                                            <span
                                                                                key={type}
                                                                                className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold ${done
                                                                                    ? 'bg-green-100 text-green-700'
                                                                                    : 'bg-gray-100 text-gray-400'
                                                                                    }`}
                                                                            >
                                                                                {done && (
                                                                                    <svg xmlns="http://www.w3.org/2000/svg" width="10" height="10" viewBox="0 0 24 24">
                                                                                        <path fill="currentColor" d="m9.55 18l-5.7-5.7l1.425-1.425l4.275 4.275l9.175-9.175L20.15 7.4z" />
                                                                                    </svg>
                                                                                )}
                                                                                {type === 'beginning' ? 'BOD' : 'EOD'}
                                                                            </span>
                                                                        )
                                                                    })}
                                                                </div>
                                                            )}
                                                        </td>
                                                        <td className="px-6 py-4 text-sm text-gray-600">{item.category}</td>
                                                        {staffRole !== 'Barista' && (
                                                            <td className="px-6 py-4 whitespace-nowrap">
                                                                <div className="inline-flex items-center px-3 py-1 rounded-full bg-gray-100 text-gray-800 text-sm font-medium">
                                                                    {item.current_stock} {item.unit}
                                                                </div>
                                                            </td>
                                                        )}
                                                        {staffRole !== 'Barista' && (
                                                            <td className="px-6 py-4 whitespace-nowrap">
                                                                <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${(item.reserved_stock || 0) > 0 ? 'bg-amber-100 text-amber-800' : 'bg-gray-100 text-gray-500'}`}>
                                                                    {item.reserved_stock || 0} {item.unit}
                                                                </div>
                                                            </td>
                                                        )}
                                                        {staffRole !== 'Barista' && (
                                                            <td className="px-6 py-4 whitespace-nowrap">
                                                                <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-semibold ${item.available_stock <= 0 ? 'bg-red-100 text-red-800' : item.available_stock <= item.reorder_level ? 'bg-orange-100 text-orange-800' : 'bg-green-100 text-green-800'}`}>
                                                                    {item.available_stock} {item.unit}
                                                                </div>
                                                            </td>
                                                        )}
                                                        {staffRole !== 'Barista' && (
                                                            <td className="px-6 py-4 whitespace-nowrap">
                                                                <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ${isOutOfStock ? 'bg-red-100 text-red-800' : isLowStock ? 'bg-orange-100 text-orange-800' : 'bg-green-100 text-green-800'}`}>
                                                                    {isOutOfStock ? 'Out of Stock' : isLowStock ? 'Low Stock' : 'In Stock'}
                                                                </span>
                                                            </td>
                                                        )}
                                                        {staffRole !== 'Barista' && (
                                                            <td className="px-6 py-4 whitespace-nowrap">
                                                                <div className="inline-flex items-center px-3 py-1 rounded-full bg-slate-100 text-slate-700 text-sm font-medium">
                                                                    {item.reorder_level} {item.unit}
                                                                </div>
                                                            </td>
                                                        )}
                                                        <td className="px-6 py-4 text-sm text-gray-600">{formatDateTime(item.last_updated)}</td>
                                                        <td className="px-6 py-4 text-sm">
                                                            <div className="flex items-center gap-2">
                                                                {staffRole === 'Barista' && (
                                                                    <button
                                                                        onClick={() => openStaffCountModal(item)}
                                                                        className="p-1 text-purple-600 hover:bg-purple-50 rounded"
                                                                        title="Staff Count"
                                                                    >
                                                                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24">
                                                                            <path fill="currentColor" d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2m-7 3c1.93 0 3.5 1.57 3.5 3.5S13.93 13 12 13s-3.5-1.57-3.5-3.5S10.07 6 12 6m7 13H5v-.23c0-.62.28-1.2.76-1.58C7.47 15.82 9.64 15 12 15s4.53.82 6.24 2.19c.48.38.76.97.76 1.58V19z" />
                                                                        </svg>
                                                                    </button>
                                                                )}
                                                                {staffRole !== 'Barista' && (
                                                                    <button
                                                                        onClick={() => openAdjustStockModal(item)}
                                                                        className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                                                                        title="Edit Stock"
                                                                    >
                                                                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24">
                                                                            <path fill="currentColor" d="m14.06 9l.94.94L5.92 19H5v-.92zm3.6-6c-.25 0-.51.1-.7.29l-1.83 1.83l3.75 3.75l1.83-1.83c.39-.39.39-1.04 0-1.41l-2.34-2.34c-.2-.2-.45-.29-.71-.29m-3.6 3.19L3 17.25V21h3.75L17.81 9.94z" />
                                                                        </svg>
                                                                    </button>
                                                                )}
                                                                {staffRole === 'Barista' && (
                                                                    <button
                                                                        onClick={() => {
                                                                            setAdjustmentRequestItem(item)
                                                                            setAdjustmentRequestForm({ adjustment_type: 'deduct', input_mode: 'manual', quantity: '', amount_per_package: '', package_count: '', reason: 'spillage', notes: '' })
                                                                            setEditingAdjustmentRequestId(null)
                                                                            setShowAdjustmentRequestModal(true)
                                                                        }}
                                                                        className="p-1 text-blue-600 hover:bg-orange-50 rounded"
                                                                        title="Request Stock Adjustment"
                                                                    >
                                                                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24">
                                                                            <path fill="currentColor" d="m14.06 9l.94.94L5.92 19H5v-.92zm3.6-6c-.25 0-.51.1-.7.29l-1.83 1.83l3.75 3.75l1.83-1.83c.39-.39.39-1.04 0-1.41l-2.34-2.34c-.2-.2-.45-.29-.71-.29m-3.6 3.19L3 17.25V21h3.75L17.81 9.94z" />
                                                                        </svg>
                                                                    </button>
                                                                )}
                                                                {staffRole === 'Admin' && (
                                                                    <button
                                                                        onClick={() => handleArchiveStockItem(item)}
                                                                        className="p-1 text-orange-600 hover:bg-orange-50 rounded"
                                                                        title="Archive Item"
                                                                    >
                                                                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24">
                                                                            <path fill="currentColor" d="M3 3h18a1 1 0 0 1 1 1v4a1 1 0 0 1-1 1h-1v11a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V9H3a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1m3 6v10h12V9zm2 2h8v2H8zm0 4h8v2H8zM5 5v2h14V5z" />
                                                                        </svg>
                                                                    </button>
                                                                )}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                )
                                            })
                                        )}
                                    </tbody>
                                </table>
                                <Pagination
                                    page={inventoryPage}
                                    totalItems={filteredInventory.length}
                                    pageSize={PAGE_SIZE}
                                    onPageChange={setInventoryPage}
                                />
                            </div>
                        )}

                        {/* ── My Adjustment Requests sub-tab (Barista only) ── */}
                        {inventorySubTab === 'adjustment_requests' && staffRole === 'Barista' && (
                            <div className="overflow-x-auto">
                                {adjustmentRequestsLoading ? (
                                    <div className="px-6 py-12 text-center text-gray-500">
                                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#A0826D] mx-auto mb-3" />
                                        <p className="text-sm">Loading your requests...</p>
                                    </div>
                                ) : (() => {
                                    const myRequests = adjustmentRequests.filter(r =>
                                        selectedBarista ? r.requested_by_barista_id === selectedBarista.barista_id : r.requested_by_staff_id === staffRecordId
                                    )
                                    return (
                                        <>
                                            <table className="w-full">
                                                <thead className="bg-gray-50 border-b border-gray-200">
                                                    <tr>
                                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ingredient</th>
                                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Quantity</th>
                                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reason</th>
                                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="bg-white divide-y divide-gray-200">
                                                    {myRequests.length === 0 ? (
                                                        <tr>
                                                            <td colSpan="7" className="px-6 py-8 text-center text-gray-500">
                                                                You haven't submitted any adjustment requests yet.
                                                            </td>
                                                        </tr>
                                                    ) : (
                                                        myRequests.slice((adjustmentRequestsPage - 1) * PAGE_SIZE, adjustmentRequestsPage * PAGE_SIZE).map(req => (
                                                            <tr key={req.id} className="hover:bg-gray-50">
                                                                <td className="px-6 py-4">
                                                                    <p className="font-medium text-gray-800 text-sm">{req.ingredient_name}</p>
                                                                    <p className="text-xs text-gray-500">{req.ingredient_unit}</p>
                                                                </td>
                                                                <td className="px-6 py-4">
                                                                    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${req.adjustment_type === 'add' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                                                        {req.adjustment_type === 'add' ? '+ Add' : '− Deduct'}
                                                                    </span>
                                                                </td>
                                                                <td className="px-6 py-4 text-sm font-semibold text-gray-800">
                                                                    {req.quantity} {req.ingredient_unit}
                                                                </td>
                                                                <td className="px-6 py-4">
                                                                    <p className="text-sm text-gray-700 capitalize">{req.reason}</p>
                                                                    {req.notes && <p className="text-xs text-gray-400 mt-0.5">{req.notes}</p>}
                                                                </td>
                                                                <td className="px-6 py-4">
                                                                    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${req.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                                                        req.status === 'approved' ? 'bg-green-100 text-green-800' :
                                                                            'bg-red-100 text-red-800'
                                                                        }`}>
                                                                        {req.status.charAt(0).toUpperCase() + req.status.slice(1)}
                                                                    </span>
                                                                </td>
                                                                <td className="px-6 py-4 text-sm text-gray-500">{formatDateTime(req.created_at)}</td>
                                                                <td className="px-6 py-4">
                                                                    {req.status === 'pending' && (
                                                                        <div className="flex items-center gap-2">
                                                                            <button
                                                                                onClick={() => handleEditAdjustmentRequest(req)}
                                                                                className="px-3 py-1.5 bg-blue-100 text-blue-700 rounded-lg text-sm font-medium hover:bg-blue-200"
                                                                            >
                                                                                Edit
                                                                            </button>
                                                                            <button
                                                                                onClick={() => handleCancelMyAdjustmentRequest(req)}
                                                                                className="px-3 py-1.5 bg-red-100 text-red-700 rounded-lg text-sm font-medium hover:bg-red-200"
                                                                            >
                                                                                Cancel
                                                                            </button>
                                                                        </div>
                                                                    )}
                                                                </td>
                                                            </tr>
                                                        ))
                                                    )}
                                                </tbody>
                                            </table>
                                            <Pagination
                                                page={adjustmentRequestsPage}
                                                totalItems={myRequests.length}
                                                pageSize={PAGE_SIZE}
                                                onPageChange={setAdjustmentRequestsPage}
                                            />
                                        </>
                                    )
                                })()}
                            </div>
                        )}

                        {/* ── Stock Count Review sub-tab (Admin only) ── */}
                        {inventorySubTab === 'stock_count_review' && staffRole === 'Admin' && (
                            <div>
                                <div className="overflow-x-auto">
                                    {stockCountReviewLoading ? (
                                        <div className="px-6 py-12 text-center text-gray-500">
                                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#A0826D] mx-auto mb-3" />
                                            <p className="text-sm">Loading reconciliation data...</p>
                                        </div>
                                    ) : (
                                        <table className="w-full">
                                            <thead className="bg-gray-50 border-b border-gray-200">
                                                <tr>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ingredient</th>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Beginning Stock</th>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Added</th>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Deducted</th>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">System Qty</th>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Count Records</th>
                                                </tr>
                                            </thead>
                                            <tbody className="bg-white divide-y divide-gray-200">
                                                {stockCountReview.length === 0 ? (
                                                    <tr>
                                                        <td colSpan="8" className="px-6 py-8 text-center text-gray-500">
                                                            No inventory data found for this date.
                                                        </td>
                                                    </tr>
                                                ) : (
                                                    paginatedStockCountReview.map(row => {
                                                        const hasCount = row.staff_count !== null
                                                        const isOver = hasCount && row.variance > 0
                                                        const isShort = hasCount && row.variance < 0
                                                        const isMatch = hasCount && row.variance === 0

                                                        return (
                                                            <tr key={row.ingredient_id} className="hover:bg-gray-50">
                                                                <td className="px-6 py-4">
                                                                    <p className="font-medium text-gray-800 text-sm">{row.name}</p>
                                                                    <p className="text-xs text-gray-500">{row.unit} • {row.category}</p>
                                                                </td>
                                                                <td className="px-6 py-4">
                                                                    <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-gray-100 text-gray-700 text-sm font-medium">
                                                                        {row.beginning_stock} {row.unit}
                                                                    </span>
                                                                </td>
                                                                <td className="px-6 py-4">
                                                                    <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-green-50 text-green-700 text-sm font-medium">
                                                                        +{row.added_stock} {row.unit}
                                                                    </span>
                                                                </td>
                                                                <td className="px-6 py-4">
                                                                    <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-orange-50 text-orange-700 text-sm font-medium">
                                                                        -{row.deducted_stock} {row.unit}
                                                                    </span>
                                                                </td>
                                                                <td className="px-6 py-4">
                                                                    <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-blue-50 text-blue-700 text-sm font-medium">
                                                                        {row.system_quantity} {row.unit}
                                                                    </span>
                                                                </td>
                                                                <td className="px-6 py-4">
                                                                    <button
                                                                        onClick={() => {
                                                                            setStockCountRecordsItem(row)
                                                                            setStockCountRecords([])
                                                                            fetchStockCountRecords(row.ingredient_id, stockCountReviewDate)
                                                                            setShowStockCountRecordsModal(true)
                                                                        }}
                                                                        className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg text-xs font-medium hover:bg-gray-200 flex items-center gap-1.5"
                                                                    >
                                                                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24"><g fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5"><path d="M15 12a3 3 0 1 1-6 0a3 3 0 0 1 6 0" /><path d="M2 12c1.6-4.097 5.336-7 10-7s8.4 2.903 10 7c-1.6 4.097-5.336 7-10 7s-8.4-2.903-10-7" /></g></svg>
                                                                        View Records
                                                                    </button>
                                                                </td>
                                                            </tr>
                                                        )
                                                    })
                                                )}
                                            </tbody>
                                        </table>
                                    )}
                                    <Pagination
                                        page={stockCountReviewPage}
                                        totalItems={stockCountReview.length}
                                        pageSize={PAGE_SIZE}
                                        onPageChange={setStockCountReviewPage}
                                    />
                                </div>
                            </div>
                        )}
                        {/* ── Restock Request sub-tab (Admin only) ── */}
                        {inventorySubTab === 'restock_request' && staffRole === 'Admin' && (
                            <div>
                                <div className="overflow-x-auto">
                                    {restockRequestsLoading ? (
                                        <div className="px-6 py-12 text-center text-gray-500">
                                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#A0826D] mx-auto mb-3" />
                                            <p className="text-sm">Loading restock requests...</p>
                                        </div>
                                    ) : (() => {
                                        const entriesToRender =
                                            restockFilter === 'all' ? allRestockEntries :
                                                restockFilter === 'not_requested' ? notYetRequestedItems.map(item => ({ type: 'not_requested', item })) :
                                                    restockFilter === 'pending' ? pendingOrApprovedRestockRequests.map(req => ({ type: 'active', req })) :
                                                        fulfilledRestockRequests.map(req => ({ type: 'fulfilled', req }))
                                        const emptyMessage =
                                            restockFilter === 'not_requested' ? 'No low stock or out-of-stock items found. All ingredients are sufficiently stocked.' :
                                                restockFilter === 'pending' ? 'No pending restock requests.' :
                                                    restockFilter === 'fulfilled' ? 'No fulfilled restock requests yet.' :
                                                        'No restock activity found for this branch.'

                                        const paginatedEntries = entriesToRender.slice(
                                            (restockRequestPage - 1) * PAGE_SIZE, restockRequestPage * PAGE_SIZE
                                        )

                                        return (
                                            <>
                                                <table className="w-full">
                                                    <thead className="bg-gray-50 border-b border-gray-200">
                                                        <tr>
                                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ingredient</th>
                                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Current Stock</th>
                                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reorder Level</th>
                                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Request Qty</th>
                                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody className="bg-white divide-y divide-gray-200">
                                                        {entriesToRender.length === 0 ? (
                                                            <tr>
                                                                <td colSpan="6" className="px-6 py-8 text-center text-gray-500">
                                                                    {emptyMessage}
                                                                </td>
                                                            </tr>
                                                        ) : (
                                                            paginatedEntries.map(entry => {
                                                                if (entry.type === 'not_requested') {
                                                                    const item = entry.item
                                                                    const isOutOfStock = item.available_stock <= 0
                                                                    const isSubmitting = submittingRestockId === item.ingredient_id
                                                                    const interactive = restockFilter === 'not_requested'

                                                                    return (
                                                                        <tr key={`notreq-${item.ingredient_id}`} className={`hover:bg-gray-50 ${isOutOfStock ? 'bg-red-50/40' : 'bg-orange-50/40'}`}>
                                                                            <td className="px-6 py-4">
                                                                                <p className="font-medium text-gray-800 text-sm">{item.name}</p>
                                                                                <p className="text-xs text-gray-500">{item.unit} • {item.category}</p>
                                                                            </td>
                                                                            <td className="px-6 py-4">
                                                                                <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-sm font-semibold ${isOutOfStock ? 'bg-red-100 text-red-800' : 'bg-orange-100 text-orange-800'}`}>
                                                                                    {item.available_stock} {item.unit}
                                                                                </span>
                                                                            </td>
                                                                            <td className="px-6 py-4">
                                                                                <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-slate-100 text-slate-700 text-sm font-medium">
                                                                                    {item.reorder_level} {item.unit}
                                                                                </span>
                                                                            </td>
                                                                            <td className="px-6 py-4">
                                                                                {interactive ? (
                                                                                    <input
                                                                                        type="number"
                                                                                        step="0.01"
                                                                                        min={item.reorder_level}
                                                                                        value={restockQuantityInputs[item.ingredient_id] || ''}
                                                                                        onChange={(e) => setRestockQuantityInputs(prev => ({
                                                                                            ...prev,
                                                                                            [item.ingredient_id]: e.target.value
                                                                                        }))}
                                                                                        placeholder={`Min ${item.reorder_level}`}
                                                                                        className="w-28 px-2 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#A0826D]"
                                                                                    />
                                                                                ) : (
                                                                                    <span className="text-xs text-gray-400 italic">—</span>
                                                                                )}
                                                                            </td>
                                                                            <td className="px-6 py-4">
                                                                                <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-500 italic">
                                                                                    Not yet requested
                                                                                </span>
                                                                            </td>
                                                                            <td className="px-6 py-4">
                                                                                {interactive ? (
                                                                                    <button onClick={() => handleSubmitRestockRequest(item)}
                                                                                        disabled={isSubmitting}
                                                                                        className="px-3 py-1.5 bg-[#A0826D] text-white rounded-lg text-sm font-medium hover:bg-[#8c705b] disabled:opacity-50 disabled:cursor-not-allowed"
                                                                                    >
                                                                                        {isSubmitting ? 'Submitting…' : 'Submit Request'}
                                                                                    </button>
                                                                                ) : (
                                                                                    <span className="text-xs text-gray-400 italic">No request made</span>
                                                                                )}
                                                                            </td>
                                                                        </tr>
                                                                    )
                                                                }

                                                                if (entry.type === 'active') {
                                                                    const req = entry.req
                                                                    const inventoryItem = branchInventory.find(i => i.ingredient_id === req.ingredient_id)
                                                                        || archivedInventory.find(i => i.ingredient_id === req.ingredient_id)
                                                                    return (
                                                                        <tr key={`active-${req.id}`} className="hover:bg-gray-50">
                                                                            <td className="px-6 py-4">
                                                                                <p className="font-medium text-gray-800 text-sm">{req.ingredient_name}</p>
                                                                                <p className="text-xs text-gray-500">{req.ingredient_unit}</p>
                                                                            </td>
                                                                            <td className="px-6 py-4">
                                                                                <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-slate-100 text-slate-700 text-sm font-medium">
                                                                                    {inventoryItem?.available_stock ?? req.current_stock} {req.ingredient_unit}
                                                                                </span>
                                                                            </td>
                                                                            <td className="px-6 py-4 text-sm text-gray-500">{req.reorder_level} {req.ingredient_unit}</td>
                                                                            <td className="px-6 py-4 text-sm font-semibold text-gray-800">{req.requested_quantity} {req.ingredient_unit}</td>
                                                                            <td className="px-6 py-4">
                                                                                <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${req.status === 'pending' ? 'bg-yellow-100 text-yellow-800' : 'bg-green-100 text-green-800'}`}>
                                                                                    {req.status === 'pending' ? 'Pending' : 'Approved — Awaiting Receipt'}
                                                                                </span>
                                                                            </td>
                                                                            <td className="px-6 py-4">
                                                                                <div className="flex items-center gap-2">
                                                                                    <button
                                                                                        onClick={() => handleMarkFulfilled(req.id, req.ingredient_name)}
                                                                                        className="px-3 py-1.5 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700"
                                                                                    >
                                                                                        Mark as Received
                                                                                    </button>
                                                                                    {req.status === 'pending' && (
                                                                                        <button
                                                                                            onClick={() => handleCancelRestockRequest(req.id, req.ingredient_name)}
                                                                                            className="px-3 py-1.5 bg-red-100 text-red-700 rounded-lg text-sm font-medium hover:bg-red-200"
                                                                                        >
                                                                                            Cancel
                                                                                        </button>
                                                                                    )}
                                                                                </div>
                                                                            </td>
                                                                        </tr>
                                                                    )
                                                                }

                                                                const req = entry.req
                                                                const inventoryItem = branchInventory.find(i => i.ingredient_id === req.ingredient_id)
                                                                    || archivedInventory.find(i => i.ingredient_id === req.ingredient_id)
                                                                return (
                                                                    <tr key={`fulfilled-${req.id}`} className="hover:bg-gray-50 bg-green-50/20">
                                                                        <td className="px-6 py-4">
                                                                            <p className="font-medium text-gray-800 text-sm">{req.ingredient_name}</p>
                                                                            <p className="text-xs text-gray-500">{req.ingredient_unit}</p>
                                                                        </td>
                                                                        <td className="px-6 py-4">
                                                                            <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-slate-100 text-slate-700 text-sm font-medium">
                                                                                {inventoryItem?.available_stock ?? '—'} {req.ingredient_unit}
                                                                            </span>
                                                                        </td>
                                                                        <td className="px-6 py-4 text-sm text-gray-500">{req.reorder_level} {req.ingredient_unit}</td>
                                                                        <td className="px-6 py-4 text-sm font-semibold text-gray-800">{req.requested_quantity} {req.ingredient_unit}</td>
                                                                        <td className="px-6 py-4">
                                                                            <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800">
                                                                                Fulfilled
                                                                            </span>
                                                                        </td>
                                                                        <td className="px-6 py-4 text-xs text-gray-400">
                                                                            {formatDateTime(req.updated_at || req.created_at)}
                                                                        </td>
                                                                    </tr>
                                                                )
                                                            })
                                                        )}
                                                    </tbody>
                                                </table>
                                                <Pagination
                                                    page={restockRequestPage}
                                                    totalItems={entriesToRender.length}
                                                    pageSize={PAGE_SIZE}
                                                    onPageChange={setRestockRequestPage}
                                                />
                                            </>
                                        )
                                    })()}
                                </div>
                            </div>
                        )}

                        {/* ── Adjustment Requests sub-tab (Admin only) ── */}
                        {inventorySubTab === 'adjustment_requests' && staffRole === 'Admin' && (
                            <div>
                                <div className="overflow-x-auto">
                                    {adjustmentRequestsLoading ? (
                                        <div className="px-6 py-12 text-center text-gray-500">
                                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#A0826D] mx-auto mb-3" />
                                            <p className="text-sm">Loading adjustment requests...</p>
                                        </div>
                                    ) : (
                                        <table className="w-full">
                                            <thead className="bg-gray-50 border-b border-gray-200">
                                                <tr>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ingredient</th>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Quantity</th>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reason</th>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Requested By</th>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Action</th>
                                                </tr>
                                            </thead>
                                            <tbody className="bg-white divide-y divide-gray-200">
                                                {adjustmentRequests.length === 0 ? (
                                                    <tr>
                                                        <td colSpan="8" className="px-6 py-8 text-center text-gray-500">
                                                            No adjustment requests found.
                                                        </td>
                                                    </tr>
                                                ) : (
                                                    paginatedAdjustmentRequests.map(req => (
                                                        <tr key={req.id} className="hover:bg-gray-50">
                                                            <td className="px-6 py-4">
                                                                <p className="font-medium text-gray-800 text-sm">{req.ingredient_name}</p>
                                                                <p className="text-xs text-gray-500">{req.ingredient_unit}</p>
                                                            </td>
                                                            <td className="px-6 py-4">
                                                                <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${req.adjustment_type === 'add' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                                                                    {req.adjustment_type === 'add' ? '+ Add' : '− Deduct'}
                                                                </span>
                                                            </td>
                                                            <td className="px-6 py-4 text-sm font-semibold text-gray-800">
                                                                {req.quantity} {req.ingredient_unit}
                                                            </td>
                                                            <td className="px-6 py-4">
                                                                <p className="text-sm text-gray-700 capitalize">{req.reason}</p>
                                                                {req.notes && <p className="text-xs text-gray-400 mt-0.5">{req.notes}</p>}
                                                            </td>
                                                            <td className="px-6 py-4 text-sm text-gray-600">{req.requested_by}</td>
                                                            <td className="px-6 py-4">
                                                                <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${req.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                                                                    req.status === 'approved' ? 'bg-green-100 text-green-800' :
                                                                        'bg-red-100 text-red-800'
                                                                    }`}>
                                                                    {req.status.charAt(0).toUpperCase() + req.status.slice(1)}
                                                                </span>
                                                            </td>
                                                            <td className="px-6 py-4 text-sm text-gray-500">{formatDateTime(req.created_at)}</td>
                                                            <td className="px-6 py-4">
                                                                {req.status === 'pending' && (
                                                                    <div className="flex items-center gap-2">
                                                                        <button
                                                                            onClick={() => handleApproveAdjustmentRequest(req)}
                                                                            className="px-3 py-1.5 bg-[#A0826D] text-white rounded-lg text-sm font-medium hover:bg-[#8c705b]"
                                                                        >
                                                                            Approve
                                                                        </button>
                                                                        <button
                                                                            onClick={() => handleRejectAdjustmentRequest(req)}
                                                                            className="px-3 py-1.5 bg-red-100 text-red-700 rounded-lg text-sm font-medium hover:bg-red-200"
                                                                        >
                                                                            Reject
                                                                        </button>
                                                                    </div>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    ))
                                                )}
                                            </tbody>
                                        </table>
                                    )}
                                    <Pagination
                                        page={adjustmentRequestsPage}
                                        totalItems={adjustmentRequests.length}
                                        pageSize={PAGE_SIZE}
                                        onPageChange={setAdjustmentRequestsPage}
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Recipes Tab */}
                {activeTab === 'recipes' && (
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                        <div className="px-6 py-4 border-b border-gray-200">
                            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                                <div>
                                    <h3 className="text-lg font-bold text-gray-800">Menu Item Recipe Reference</h3>
                                    <p className="text-sm text-gray-500 mt-1">View ingredient usage per branch-visible menu item</p>
                                </div>
                                <div className="relative w-full lg:w-72">
                                    <input
                                        type="text"
                                        placeholder="Search Menu items or recipes..."
                                        value={recipeSearch}
                                        onChange={(e) => setRecipeSearch(e.target.value)}
                                        className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#A0826D]"
                                    />
                                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24">
                                            <path fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10a7 7 0 1 0 14 0a7 7 0 1 0-14 0m18 11l-6-6" />
                                        </svg>
                                    </span>
                                </div>
                            </div>

                            {/* Category filter pills */}
                            <div className="flex gap-2 overflow-x-auto pb-1 mt-3">
                                {['All', ...new Set(recipeReference.map(i => i.category).filter(Boolean))]
                                    .sort((a, b) => a === 'All' ? -1 : b === 'All' ? 1 : a.localeCompare(b))
                                    .map(cat => (
                                        <button
                                            key={cat}
                                            onClick={() => {
                                                setRecipeCategoryFilter(cat)
                                                setRecipeClassificationFilter('All')
                                                if (cat === 'All') {
                                                    setAvailableRecipeClassifications([])
                                                } else {
                                                    const classifications = [
                                                        ...new Set(
                                                            recipeReference
                                                                .filter(item => item.category === cat)
                                                                .map(item => item.classification)
                                                                .filter(Boolean)
                                                        )
                                                    ].sort()
                                                    setAvailableRecipeClassifications(classifications)
                                                }
                                            }}
                                            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors flex-shrink-0 whitespace-nowrap ${recipeCategoryFilter === cat
                                                ? 'bg-[#A0826D] text-white'
                                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                                }`}
                                        >
                                            {cat}
                                            <span className={`ml-1.5 px-1.5 py-0.5 rounded-full text-[10px] font-bold ${recipeCategoryFilter === cat ? 'bg-white/30 text-white' : 'bg-gray-200 text-gray-500'
                                                }`}>
                                                {cat === 'All'
                                                    ? recipeReference.length
                                                    : recipeReference.filter(i => (i.category || '—') === cat).length}
                                            </span>
                                        </button>
                                    ))}
                            </div>

                            {/* Classification chips — only when a category is selected */}
                            {recipeCategoryFilter !== 'All' && availableRecipeClassifications.length > 0 && (
                                <div className="flex items-center gap-2 overflow-x-auto pb-1 mt-2">
                                    <span className="text-xs text-gray-400 font-medium shrink-0">
                                        {recipeCategoryFilter}:
                                    </span>
                                    {['All', ...availableRecipeClassifications].map(cls => (
                                        <button
                                            key={cls}
                                            onClick={() => setRecipeClassificationFilter(cls)}
                                            className={`px-3 py-1 rounded-full text-xs font-semibold border transition-all flex-shrink-0 whitespace-nowrap ${recipeClassificationFilter === cls
                                                ? 'bg-[#303030] border-[#303030] text-white'
                                                : 'bg-white border-gray-300 text-gray-600 hover:border-gray-500'
                                                }`}
                                        >
                                            {cls === 'All' ? `All ${recipeCategoryFilter}` : cls}
                                            {cls !== 'All' && (
                                                <span className={`ml-1 ${recipeClassificationFilter === cls ? 'opacity-70' : 'text-gray-400'}`}>
                                                    ({recipeReference.filter(i => i.category === recipeCategoryFilter && i.classification === cls).length})
                                                </span>
                                            )}
                                        </button>
                                    ))}
                                </div>
                            )}

                        </div>

                        <div className="divide-y divide-gray-200">
                            {filteredRecipes.length === 0 ? (
                                <div className="px-6 py-8 text-center text-gray-500">
                                    {recipeSearch ? 'No recipes match your search.' : 'No recipe reference data found.'}
                                </div>
                            ) : (
                                [...paginatedRecipes].sort((a, b) => a.name.localeCompare(b.name)).map(item => (
                                    <div key={item.item_id} className="px-6 py-5">
                                        <div className="flex items-center gap-3 mb-4">
                                            {item.item_img ? (
                                                <img src={item.item_img} alt={item.name} className="w-12 h-12 rounded-lg object-cover border border-gray-200" />
                                            ) : (
                                                <div className="w-12 h-12 rounded-lg bg-gray-100 border border-gray-200 flex items-center justify-center text-[10px] text-gray-400">N/A</div>
                                            )}
                                            <div>
                                                <p className="font-medium text-gray-800">{item.name}</p>
                                                <p className="text-sm text-gray-500">{item.category} • {item.classification}</p>
                                            </div>
                                        </div>
                                        {item.ingredients.length === 0 ? (
                                            <div className="text-sm text-gray-500 pl-[60px]">No recipe ingredients assigned yet.</div>
                                        ) : (
                                            <div className="pl-[60px]">
                                                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                                                    {[...item.ingredients].sort((a, b) => a.name.localeCompare(b.name)).map(ingredient => (
                                                        <div key={`${item.item_id}-${ingredient.ingredient_id}`} className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                                                            <p className="text-sm font-medium text-gray-800">{ingredient.name}</p>
                                                            <p className="text-xs text-gray-500 mt-1">{ingredient.quantity_used} {ingredient.unit}</p>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ))
                            )}
                        </div>
                        <Pagination
                            page={recipesPage}
                            totalItems={filteredRecipes.length}
                            pageSize={PAGE_SIZE}
                            onPageChange={setRecipesPage}
                        />
                    </div>
                )}

                {/* Transactions Tab */}
                {activeTab === 'transactions' && (
                    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                        <div className="px-6 py-4 border-b border-gray-200">
                            <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4">
                                <div>
                                    <h3 className="text-lg font-bold text-gray-800">Inventory Transactions</h3>
                                    <p className="text-sm text-gray-500 mt-1">Track stock movement for this branch</p>
                                </div>
                                <div className="flex flex-col sm:flex-row gap-3 xl:items-center xl:justify-end xl:flex-shrink-0">
                                    {/* Inventory Transactions date select */}
                                    <select
                                        value={inventoryDateFilter}
                                        onChange={(e) => {
                                            setInventoryDateFilter(e.target.value)
                                            if (e.target.value !== 'custom') {
                                                setInventoryCustomStart('')
                                                setInventoryCustomEnd('')
                                            }
                                        }}
                                        className="px-4 py-2 border border-gray-300 rounded-lg bg-white text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#A0826D]"
                                    >
                                        <option value="all">All Time</option>
                                        <option value="day">Today</option>
                                        <option value="week">This Week</option>
                                        <option value="month">This Month</option>
                                        <option value="year">This Year</option>
                                        <option value="custom">Custom Range</option>
                                    </select>

                                    {inventoryDateFilter === 'custom' && (
                                        <div className="flex items-center gap-2 flex-wrap">
                                            <span className="text-xs text-gray-400">From:</span>
                                            <input
                                                type="date"
                                                value={inventoryCustomStart}
                                                max={inventoryCustomEnd || new Date().toISOString().split('T')[0]}
                                                onChange={(e) => setInventoryCustomStart(e.target.value)}
                                                className="px-3 py-2 border rounded-lg bg-white text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#A0826D]"
                                                style={{ borderColor: inventoryCustomStart && inventoryCustomEnd ? '#A0826D' : '#D1D5DB' }}
                                            />
                                            <span className="text-xs text-gray-400">To</span>
                                            <input
                                                type="date"
                                                value={inventoryCustomEnd}
                                                min={inventoryCustomStart}
                                                max={new Date().toISOString().split('T')[0]}
                                                onChange={(e) => setInventoryCustomEnd(e.target.value)}
                                                className="px-3 py-2 border rounded-lg bg-white text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#A0826D]"
                                                style={{ borderColor: inventoryCustomStart && inventoryCustomEnd ? '#A0826D' : '#D1D5DB' }}
                                            />
                                            {inventoryCustomStart && inventoryCustomEnd && (
                                                <button
                                                    onClick={() => { setInventoryCustomStart(''); setInventoryCustomEnd('') }}
                                                    className="px-2.5 py-2 rounded-lg border border-gray-200 text-xs text-gray-500 hover:bg-gray-50"
                                                >
                                                    ✕ Clear
                                                </button>
                                            )}
                                        </div>
                                    )}
                                    <div className="relative w-full sm:w-72">
                                        <input
                                            type="text"
                                            placeholder="Search Inventory transactions..."
                                            value={transactionSearch}
                                            onChange={(e) => setTransactionSearch(e.target.value)}
                                            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#A0826D]"
                                        />
                                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                                            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24">
                                                <path fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10a7 7 0 1 0 14 0a7 7 0 1 0-14 0m18 11l-6-6" />
                                            </svg>
                                        </span>
                                    </div>
                                </div>
                            </div>

                        </div>
                        <div className="overflow-x-auto">
                            <table className="w-full">
                                <thead className="bg-gray-50 border-b border-gray-200">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ingredient</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Transaction Type</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Quantity</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reference Type</th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Remarks</th>
                                        {staffRole === 'Admin' && <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Adjusted By</th>}
                                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created At</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {filteredTransactions.length === 0 ? (
                                        <tr><td colSpan="6" className="px-6 py-8 text-center text-gray-500">No inventory transactions found.</td></tr>
                                    ) : (
                                        paginatedTransactions.map(transaction => (
                                            <tr key={transaction.inventory_transaction_id} className="hover:bg-gray-50">
                                                <td className="px-6 py-4 text-sm font-medium text-gray-800">{transaction.ingredient_name}</td>
                                                <td className="px-6 py-4 text-sm text-gray-600">{transaction.transaction_type}</td>
                                                <td className="px-6 py-4 text-sm font-semibold text-gray-800">{transaction.quantity} {transaction.unit}</td>
                                                <td className="px-6 py-4 text-sm text-gray-600">{transaction.reference_type}</td>
                                                <td className="px-6 py-4 text-sm text-gray-600">{transaction.notes || '—'}</td>
                                                {staffRole === 'Admin' && (
                                                    <td className="px-6 py-4 text-sm text-gray-600">
                                                        {transaction.adjusted_by || <span className="text-gray-400 italic">—</span>}
                                                    </td>
                                                )}
                                                <td className="px-6 py-4 text-sm text-gray-600">{formatDateTime(transaction.created_at)}</td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                            <Pagination
                                page={transactionsPage}
                                totalItems={filteredTransactions.length}
                                pageSize={PAGE_SIZE}
                                onPageChange={setTransactionsPage}
                            />
                        </div>
                    </div>
                )}

                {/* POS Transactions Tab */}
                {activeTab === 'pos_transactions' && (
                    <>
                        {/* Sub-tabs live outside both containers now, so they control which container renders */}
                        {staffRole === 'Admin' && (
                            <div className="flex gap-2 mb-4 overflow-x-auto pb-1">
                                <button
                                    onClick={() => setPosSubTab('history')}
                                    className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors flex-shrink-0 whitespace-nowrap ${posSubTab === 'history' ? 'bg-[#A0826D] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                                >
                                    POS Transaction History
                                </button>
                                <button
                                    onClick={() => setPosSubTab('cash_drawer')}
                                    className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors flex-shrink-0 whitespace-nowrap ${posSubTab === 'cash_drawer' ? 'bg-[#A0826D] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                                >
                                    Cash Drawer Log History
                                </button>
                            </div>
                        )}

                        {posSubTab === 'cash_drawer' && staffRole === 'Admin' ? (
                            <CashDrawerLogsCard branchId={branchId} baristaId={null} showFilters isAdmin />
                        ) : (
                            /* POS Transaction History — original container + filters, untouched */
                            <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                                <div className="px-6 py-4 border-b border-gray-200">
                                    <div className="flex flex-col xl:flex-row xl:items-center xl:justify-between gap-4">
                                        <div>
                                            <h3 className="text-lg font-bold text-gray-800">POS Transactions</h3>
                                            <p className="text-sm text-gray-500 mt-1">Track recorded POS payments for this branch</p>
                                        </div>
                                        <div className="flex flex-col sm:flex-row gap-3 xl:items-center xl:justify-end xl:flex-shrink-0">
                                            <select
                                                value={posDateFilter}
                                                onChange={(e) => {
                                                    setPosDateFilter(e.target.value)
                                                    if (e.target.value !== 'custom') {
                                                        setPosCustomStart('')
                                                        setPosCustomEnd('')
                                                    }
                                                }}
                                                className="px-4 py-2 border border-gray-300 rounded-lg bg-white text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#A0826D]"
                                            >
                                                <option value="all">All Time</option>
                                                <option value="day">Today</option>
                                                <option value="week">This Week</option>
                                                <option value="month">This Month</option>
                                                <option value="year">This Year</option>
                                                <option value="custom">Custom Range</option>
                                            </select>

                                            <select
                                                value={posOrderStatusFilter}
                                                onChange={(e) => setPosOrderStatusFilter(e.target.value)}
                                                className="px-4 py-2 border border-gray-300 rounded-lg bg-white text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#A0826D]"
                                            >
                                                <option value="all">All Order Statuses</option>
                                                <option value="completed">Completed</option>
                                                <option value="pending">Pending</option>
                                                <option value="cancelled">Cancelled</option>
                                            </select>

                                            {posDateFilter === 'custom' && (
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <span className="text-xs text-gray-400">From:</span>
                                                    <input
                                                        type="date"
                                                        value={posCustomStart}
                                                        max={posCustomEnd || new Date().toISOString().split('T')[0]}
                                                        onChange={(e) => setPosCustomStart(e.target.value)}
                                                        className="px-3 py-2 border rounded-lg bg-white text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#A0826D]"
                                                        style={{ borderColor: posCustomStart && posCustomEnd ? '#A0826D' : '#D1D5DB' }}
                                                    />
                                                    <span className="text-xs text-gray-400">To</span>
                                                    <input
                                                        type="date"
                                                        value={posCustomEnd}
                                                        min={posCustomStart}
                                                        max={new Date().toISOString().split('T')[0]}
                                                        onChange={(e) => setPosCustomEnd(e.target.value)}
                                                        className="px-3 py-2 border rounded-lg bg-white text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#A0826D]"
                                                        style={{ borderColor: posCustomStart && posCustomEnd ? '#A0826D' : '#D1D5DB' }}
                                                    />
                                                    {posCustomStart && posCustomEnd && (
                                                        <button
                                                            onClick={() => { setPosCustomStart(''); setPosCustomEnd('') }}
                                                            className="px-2.5 py-2 rounded-lg border border-gray-200 text-xs text-gray-500 hover:bg-gray-50"
                                                        >
                                                            ✕ Clear
                                                        </button>
                                                    )}
                                                </div>
                                            )}
                                            <div className="relative w-full sm:w-72">
                                                <input
                                                    type="text"
                                                    placeholder="Search POS transactions..."
                                                    value={posTransactionSearch}
                                                    onChange={(e) => setPosTransactionSearch(e.target.value)}
                                                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#A0826D]"
                                                />
                                                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
                                                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24">
                                                        <path fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10a7 7 0 1 0 14 0a7 7 0 1 0-14 0m18 11l-6-6" />
                                                    </svg>
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="overflow-x-auto">
                                    <table className="w-full">
                                        <thead className="bg-gray-50 border-b border-gray-200">
                                            <tr>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reference #</th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Payment Method</th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Amount</th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Payment Status</th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Order Status</th>
                                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Created At</th>
                                            </tr>
                                        </thead>
                                        <tbody className="bg-white divide-y divide-gray-200">
                                            {filteredPOSTransactions.length === 0 ? (
                                                <tr><td colSpan="6" className="px-6 py-8 text-center text-gray-500">No POS transactions found.</td></tr>
                                            ) : (
                                                paginatedPOS.map((transaction) => (
                                                    <tr key={transaction.order_transaction_id} className="hover:bg-gray-50">
                                                        <td className="px-6 py-4 text-sm font-medium text-gray-800">{transaction.reference_number}</td>
                                                        <td className="px-6 py-4 text-sm text-gray-600 uppercase">{transaction.payment_method}</td>
                                                        <td className="px-6 py-4 text-sm font-semibold text-gray-800">{formatCurrency(transaction.transaction_amount)}</td>
                                                        <td className="px-6 py-4 text-sm text-gray-600">{transaction.payment_status}</td>
                                                        <td className="px-6 py-4 text-sm text-gray-600">{transaction.order_status}</td>
                                                        <td className="px-6 py-4 text-sm text-gray-600">{formatDateTime(transaction.created_at)}</td>
                                                    </tr>
                                                ))
                                            )}
                                        </tbody>
                                    </table>
                                    <Pagination
                                        page={posPage}
                                        totalItems={filteredPOSTransactions.length}
                                        pageSize={PAGE_SIZE}
                                        onPageChange={setPosPage}
                                    />
                                </div>
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* ── Add Stock Modal ─────────────────────────────────────────────── */}
            {showAddStockModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                    <div
                        className="absolute inset-0 bg-black bg-opacity-50"
                        onClick={() => {
                            setShowAddStockModal(false)
                            setShowIngredientDropdown(false)
                            setIngredientSearchQuery('')
                            setInitialAddStockFormData(null)
                            setAddStockFormData({ ingredient_id: '', input_mode: 'manual', current_stock: '', package_count: '', reorder_level: '' })
                        }}
                    />
                    <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full">
                        <div className="border-b border-gray-200 px-6 py-4">
                            <h2 className="text-xl font-bold text-gray-800">Add Branch Stock</h2>
                            <p className="text-sm text-gray-500 mt-1">Add a new ingredient to this branch inventory</p>
                        </div>

                        <div className="px-6 py-6 space-y-4">
                            <div className="relative" ref={ingredientDropdownRef}>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Ingredient</label>
                                <input
                                    type="text"
                                    value={
                                        showIngredientDropdown
                                            ? ingredientSearchQuery
                                            : selectedAddIngredient
                                                ? `${selectedAddIngredient.name} (${selectedAddIngredient.unit || 'unit'})`
                                                : ingredientSearchQuery
                                    }
                                    onFocus={() => {
                                        setShowIngredientDropdown(true)
                                        setIngredientSearchQuery('')
                                    }}
                                    onChange={(e) => {
                                        setIngredientSearchQuery(e.target.value)
                                        setShowIngredientDropdown(true)
                                        setAddStockFormData({ ...addStockFormData, ingredient_id: '' })
                                    }}
                                    placeholder="Click to search ingredient..."
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#A0826D]"
                                />
                                {showIngredientDropdown && (
                                    <div className="absolute z-20 mt-1 w-full max-h-56 overflow-y-auto bg-white border border-gray-300 rounded-lg shadow-lg">
                                        {filteredAvailableIngredients.length === 0 ? (
                                            <div className="px-3 py-2 text-sm text-gray-500">No ingredients found.</div>
                                        ) : (
                                            filteredAvailableIngredients.map((ingredient) => (
                                                <button
                                                    key={ingredient.ingredient_id}
                                                    type="button"
                                                    onClick={() => {
                                                        setAddStockFormData({ ...addStockFormData, ingredient_id: ingredient.ingredient_id })
                                                        setIngredientSearchQuery(ingredient.name)
                                                        setShowIngredientDropdown(false)
                                                    }}
                                                    className="w-full px-3 py-2 text-left hover:bg-gray-50 border-b last:border-b-0 border-gray-100"
                                                >
                                                    <div className="text-sm font-medium text-gray-800">{ingredient.name}</div>
                                                    <div className="text-xs text-gray-500">{ingredient.unit || 'unit'}{ingredient.category ? ` • ${ingredient.category}` : ''}</div>
                                                </button>
                                            ))
                                        )}
                                    </div>
                                )}
                            </div>

                            {selectedAddIngredient && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">Input Method</label>
                                    <div className="inline-flex mb-3 rounded-lg bg-gray-100 p-1">
                                        <button
                                            type="button"
                                            onClick={() => setAddStockFormData({ ...addStockFormData, input_mode: 'manual', package_count: '' })}
                                            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${addStockFormData.input_mode === 'manual' ? 'bg-[#A0826D] text-white shadow-sm' : 'bg-transparent text-gray-600 hover:text-gray-800'}`}
                                        >
                                            Manual Quantity
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setAddStockFormData({ ...addStockFormData, input_mode: 'package', current_stock: '' })}
                                            disabled={!selectedAddIngredient.stock_per_package || selectedAddIngredient.stock_per_package <= 0}
                                            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${addStockFormData.input_mode === 'package' ? 'bg-[#A0826D] text-white shadow-sm' : 'bg-transparent text-gray-600 hover:text-gray-800'} ${!selectedAddIngredient.stock_per_package || selectedAddIngredient.stock_per_package <= 0 ? 'opacity-50 cursor-not-allowed' : ''}`}
                                        >
                                            {selectedAddIngredient.package_label ? `${selectedAddIngredient.package_label} Count` : 'Package Count'}
                                        </button>
                                    </div>

                                    {addStockFormData.input_mode === 'manual' && (
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Manual Quantity ({selectedAddIngredient.unit || 'unit'})
                                            </label>
                                            <input
                                                type="number" step="0.01" min="0"
                                                placeholder={`Enter quantity in ${selectedAddIngredient.unit || 'unit'}`}
                                                value={addStockFormData.current_stock}
                                                onChange={(e) => {
                                                    if (e.target.value.replace('.', '').replace('-', '').length <= 6) {
                                                        setAddStockFormData({ ...addStockFormData, current_stock: e.target.value })
                                                    }
                                                }}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#A0826D]"
                                            />
                                            <p className="text-xs text-gray-500 mt-1">
                                                {String(addStockFormData.current_stock || '').replace('.', '').replace('-', '').length} / 6 digits
                                            </p>
                                        </div>
                                    )}

                                    {addStockFormData.input_mode === 'package' && selectedAddIngredient.stock_per_package > 0 && (
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                {selectedAddIngredient.package_label || 'Package'} Count
                                            </label>
                                            <input
                                                type="number" step="1" min="0"
                                                placeholder={`Enter number of ${selectedAddIngredient.package_label || 'packages'}`}
                                                value={addStockFormData.package_count}
                                                onChange={(e) => {
                                                    if (e.target.value.replace('.', '').replace('-', '').length <= 6) {
                                                        setAddStockFormData({ ...addStockFormData, package_count: e.target.value })
                                                    }
                                                }}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#A0826D]"
                                            />
                                            <p className="text-xs text-gray-500 mt-1">
                                                {String(addStockFormData.package_count || '').replace('.', '').replace('-', '').length} / 6 digits
                                            </p>
                                            <p className="text-xs text-gray-500 mt-1">
                                                1 {selectedAddIngredient.package_label || 'package'} = {selectedAddIngredient.stock_per_package} {selectedAddIngredient.unit}
                                            </p>
                                        </div>
                                    )}
                                </div>
                            )}

                            {!selectedAddIngredient && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Current Stock</label>
                                    <div className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-gray-400 text-sm">
                                        Select an ingredient first
                                    </div>
                                </div>
                            )}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Reorder Level</label>
                                <input
                                    type="number" step="0.01" min="0"
                                    placeholder="Enter Reorder Level"
                                    value={addStockFormData.reorder_level}
                                    onChange={(e) => {
                                        if (e.target.value.replace('.', '').replace('-', '').length <= 6) {
                                            setAddStockFormData({ ...addStockFormData, reorder_level: e.target.value })
                                        }
                                    }}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#A0826D]"
                                />
                                <p className="text-xs text-gray-500 mt-1">
                                    {String(addStockFormData.reorder_level || '').replace('.', '').replace('-', '').length} / 6 digits • {selectedAddIngredient?.unit || ''}
                                </p>
                            </div>
                        </div>

                        <div className="border-t border-gray-200 px-6 py-4 flex justify-end gap-3">
                            <button
                                onClick={() => {
                                    setShowAddStockModal(false)
                                    setShowIngredientDropdown(false)
                                    setIngredientSearchQuery('')
                                    setInitialAddStockFormData(null)
                                    setAddStockFormData({ ingredient_id: '', input_mode: 'manual', current_stock: '', package_count: '', reorder_level: '' })
                                }}
                                className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleAddStockItem}
                                disabled={!hasAddStockChanges}
                                className={`px-6 py-2 rounded-lg font-medium ${!hasAddStockChanges ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-[#A0826D] text-white hover:bg-[#8c705b]'}`}
                            >
                                Add Ingredient
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Add Ingredient Modal */}
            {showAddIngredientModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                    <div
                        className="absolute inset-0 bg-black bg-opacity-50"
                        onClick={() => { setShowAddIngredientModal(false) }}
                    />
                    <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] flex flex-col">
                        <div className="border-b border-gray-200 px-6 py-4">
                            <h2 className="text-xl font-bold text-gray-800">Create New Ingredient</h2>
                            <p className="text-sm text-gray-500 mt-1">
                                Use only if this ingredient doesn't exist yet in the system. It will be created globally and tracked immediately for {branchName}.
                            </p>
                        </div>

                        <div className="px-6 py-6 space-y-4 overflow-y-auto">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Ingredient Name <span className="text-red-500">*</span></label>
                                <input type="text" placeholder="e.g. Oat Milk" value={newIngredientFormData.name}
                                    maxLength={NEW_INGREDIENT_FIELD_LIMITS.name}
                                    onChange={(e) => setNewIngredientFormData({ ...newIngredientFormData, name: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#A0826D]" />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Unit <span className="text-red-500">*</span></label>
                                <input type="text" placeholder="e.g. ml, grams, pcs" value={newIngredientFormData.unit}
                                    maxLength={NEW_INGREDIENT_FIELD_LIMITS.unit}
                                    onChange={(e) => setNewIngredientFormData({ ...newIngredientFormData, unit: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#A0826D]" />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                                <input type="text" placeholder="e.g. Dairy, Sweetener" value={newIngredientFormData.category}
                                    maxLength={NEW_INGREDIENT_FIELD_LIMITS.category}
                                    onChange={(e) => setNewIngredientFormData({ ...newIngredientFormData, category: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#A0826D]" />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Package Label</label>
                                <input type="text" placeholder="e.g. Bottle, Box" value={newIngredientFormData.package_label}
                                    maxLength={NEW_INGREDIENT_FIELD_LIMITS.package_label}
                                    onChange={(e) => setNewIngredientFormData({ ...newIngredientFormData, package_label: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#A0826D]" />
                            </div>

                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Qty per Package</label>
                                    <input type="number" step="0.01" min="0" value={newIngredientFormData.stock_per_package}
                                        onChange={(e) => setNewIngredientFormData({ ...newIngredientFormData, stock_per_package: e.target.value })}
                                        placeholder="e.g. 1000"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#A0826D]" />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Price per Package</label>
                                    <input type="number" step="0.01" min="0" value={newIngredientFormData.package_price}
                                        onChange={(e) => setNewIngredientFormData({ ...newIngredientFormData, package_price: e.target.value })}
                                        placeholder="e.g. 250.00"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#A0826D]" />
                                </div>
                            </div>

                            <div className="border-t border-gray-100 pt-4">
                                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Branch Starting Stock</p>
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Starting Stock <span className="text-red-500">*</span></label>
                                        <input type="number" step="0.01" min="0" value={newIngredientFormData.current_stock}
                                            onChange={(e) => setNewIngredientFormData({ ...newIngredientFormData, current_stock: e.target.value })}
                                            placeholder="0"
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#A0826D]" />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Reorder Level <span className="text-red-500">*</span></label>
                                        <input type="number" step="0.01" min="0" value={newIngredientFormData.reorder_level}
                                            onChange={(e) => setNewIngredientFormData({ ...newIngredientFormData, reorder_level: e.target.value })}
                                            placeholder="0"
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#A0826D]" />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="border-t border-gray-200 px-6 py-4 flex justify-end gap-3">
                            <button onClick={() => setShowAddIngredientModal(false)} className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300">
                                Cancel
                            </button>
                            <button onClick={handleAddNewIngredient} disabled={isAddingNewIngredient}
                                className="px-6 py-2 bg-[#A0826D] text-white rounded-lg font-medium hover:bg-[#8c705b] disabled:opacity-50">
                                {isAddingNewIngredient ? 'Creating…' : 'Create Ingredient'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Adjust Stock Modal ──────────────────────────────────────────── */}
            {showAdjustStockModal && selectedInventoryItem && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                    <div
                        className="absolute inset-0 bg-black bg-opacity-50"
                        onClick={() => {
                            setShowAdjustStockModal(false)
                            setSelectedInventoryItem(null)
                            setInitialStockFormData(null)
                            setStockFormData({ mode: 'add', input_mode: 'manual', quantity: '', amount_per_package: '', package_count: '', reorder_level: '' })
                        }}
                    />
                    <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full">
                        <div className="border-b border-gray-200 px-6 py-4">
                            <h2 className="text-xl font-bold text-gray-800">Adjust Stock</h2>
                            <p className="text-sm text-gray-500 mt-1">{selectedInventoryItem.name}</p>
                        </div>
                        <div className="px-6 py-6 space-y-4">
                            {staffRole !== 'Barista' && (
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Current Stock</label>
                                    <div className="w-full px-3 py-2 bg-gray-100 border border-gray-300 rounded-lg text-gray-800">
                                        {selectedInventoryItem.current_stock} {selectedInventoryItem.unit}
                                    </div>
                                </div>
                            )}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Adjustment Type</label>
                                <select
                                    value={stockFormData.mode}
                                    onChange={(e) => setStockFormData({ ...stockFormData, mode: e.target.value })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#A0826D]"
                                >
                                    <option value="add">Add Stock</option>
                                    <option value="deduct">Deduct Stock</option>
                                    {staffRole === 'Admin' && <option value="set">Set Exact Stock</option>}
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Input Method</label>
                                <div className="inline-flex mb-3 rounded-lg bg-gray-100 p-1">
                                    <button
                                        type="button"
                                        onClick={() => setStockFormData({ ...stockFormData, input_mode: 'manual', package_count: '' })}
                                        className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${stockFormData.input_mode === 'manual' ? 'bg-[#A0826D] text-white shadow-sm' : 'bg-transparent text-gray-600 hover:text-gray-800'}`}
                                    >
                                        Manual Quantity
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setStockFormData({ ...stockFormData, input_mode: 'package', quantity: '' })}
                                        className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${stockFormData.input_mode === 'package' ? 'bg-[#A0826D] text-white shadow-sm' : 'bg-transparent text-gray-600 hover:text-gray-800'}`}
                                    >
                                        {selectedInventoryItem.package_label ? `${selectedInventoryItem.package_label} Count` : 'Package Count'}
                                    </button>
                                </div>
                                {stockFormData.input_mode === 'manual' && (
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Manual Quantity ({selectedInventoryItem.unit})</label>
                                        <input
                                            type="number" step="0.01" min="0"
                                            value={stockFormData.quantity}
                                            onChange={(e) => {
                                                if (e.target.value.replace('.', '').replace('-', '').length <= 6) {
                                                    setStockFormData({ ...stockFormData, quantity: e.target.value })
                                                }
                                            }}
                                            placeholder={`Enter quantity in ${selectedInventoryItem.unit}`}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#A0826D]"
                                        />
                                        <p className="text-xs text-gray-500 mt-1">
                                            {String(stockFormData.quantity || '').replace('.', '').replace('-', '').length} / 6 digits
                                        </p>
                                    </div>
                                )}
                                {stockFormData.input_mode === 'package' && (
                                    <div className="space-y-3">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Amount per Package ({selectedInventoryItem.unit})
                                            </label>
                                            <input
                                                type="number" step="0.01" min="0"
                                                value={stockFormData.amount_per_package}
                                                onChange={(e) => {
                                                    if (e.target.value.replace('.', '').replace('-', '').length <= 6) {
                                                        setStockFormData({ ...stockFormData, amount_per_package: e.target.value })
                                                    }
                                                }}
                                                placeholder={`e.g. how many ${selectedInventoryItem.unit} in one package`}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#A0826D]"
                                            />
                                        </div>

                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                {selectedInventoryItem.package_label || 'Package'} Count
                                            </label>
                                            <input
                                                type="number" step="1" min="0"
                                                value={stockFormData.package_count}
                                                onChange={(e) => {
                                                    if (e.target.value.replace('.', '').replace('-', '').length <= 6) {
                                                        setStockFormData({ ...stockFormData, package_count: e.target.value })
                                                    }
                                                }}
                                                placeholder={`Enter number of ${selectedInventoryItem.package_label || 'packages'} that arrived`}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#A0826D]"
                                            />
                                        </div>

                                        {parseFloat(stockFormData.amount_per_package || 0) > 0 && parseFloat(stockFormData.package_count || 0) > 0 && (
                                            <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3">
                                                <p className="text-xs text-blue-600 mb-0.5">Computed Total</p>
                                                <p className="text-sm font-semibold text-blue-800">
                                                    {parseFloat(stockFormData.amount_per_package)} × {parseFloat(stockFormData.package_count)} = {(parseFloat(stockFormData.amount_per_package) * parseFloat(stockFormData.package_count)).toLocaleString()} {selectedInventoryItem.unit}
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Reorder Level</label>
                                <input
                                    type="number" step="0.01" min="0"
                                    value={stockFormData.reorder_level}
                                    onChange={(e) => {
                                        if (e.target.value.replace('.', '').replace('-', '').length <= 6) {
                                            setStockFormData({ ...stockFormData, reorder_level: e.target.value })
                                        }
                                    }}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#A0826D]"
                                />
                                <p className="text-xs text-gray-500 mt-1">
                                    {String(stockFormData.reorder_level || '').replace('.', '').replace('-', '').length} / 6 digits • {selectedInventoryItem.unit}
                                </p>
                            </div>
                        </div>
                        <div className="border-t border-gray-200 px-6 py-4 flex justify-end gap-3">
                            <button
                                onClick={() => {
                                    setShowAdjustStockModal(false)
                                    setSelectedInventoryItem(null)
                                    setInitialStockFormData(null)
                                    setStockFormData({ mode: 'add', input_mode: 'manual', quantity: '', amount_per_package: '', package_count: '', reorder_level: '' })
                                }}
                                className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleUpdateStock}
                                disabled={!hasAdjustStockChanges}
                                className={`px-6 py-2 rounded-lg font-medium ${!hasAdjustStockChanges ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-[#A0826D] text-white hover:bg-[#8c705b]'}`}
                            >
                                Save Changes
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Archived Stock Items Modal ──────────────────────────────────── */}
            {showArchivedModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                    <div
                        className="absolute inset-0 bg-black bg-opacity-50"
                        onClick={() => {
                            setShowArchivedModal(false)
                            setArchivedInventorySearch('')
                            setArchivedInventoryCategoryFilter('All')
                            setArchivedInventoryPage(1)
                        }}
                    />
                    <div className="relative bg-white rounded-lg shadow-xl w-full max-w-3xl max-h-[85vh] flex flex-col">
                        <div className="border-b border-gray-200 px-6 py-4 space-y-3">
                            <div className="flex items-center justify-between gap-4 flex-wrap">
                                <div>
                                    <h2 className="text-xl font-bold text-gray-800">Archived Stock Items</h2>
                                    <p className="text-sm text-gray-500 mt-1">
                                        Reinstate archived ingredients back to the active inventory
                                    </p>
                                </div>
                                <span className="px-3 py-1 bg-gray-100 text-gray-600 text-sm font-medium rounded-full flex-shrink-0">
                                    {archivedInventory.length} archived
                                </span>
                            </div>

                            <div className="relative">
                                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24">
                                        <path fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 10a7 7 0 1 0 14 0a7 7 0 1 0-14 0m18 11l-6-6" />
                                    </svg>
                                </span>
                                <input
                                    type="text"
                                    placeholder="Search archived stock items..."
                                    value={archivedInventorySearch}
                                    onChange={(e) => setArchivedInventorySearch(e.target.value)}
                                    className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#A0826D]"
                                />
                            </div>

                            <div className="flex flex-wrap gap-2">
                                {['All', ...new Set(archivedInventory.map(i => i.category || '—').filter(Boolean))].map(cat => (
                                    <button
                                        key={cat}
                                        onClick={() => setArchivedInventoryCategoryFilter(cat)}
                                        className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors ${archivedInventoryCategoryFilter === cat
                                                ? 'bg-[#A0826D] text-white'
                                                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                                            }`}
                                    >
                                        {cat}
                                        <span className={`ml-1.5 px-1.5 py-0.5 rounded-full text-[10px] font-bold ${archivedInventoryCategoryFilter === cat
                                                ? 'bg-white/30 text-white'
                                                : 'bg-gray-200 text-gray-500'
                                            }`}>
                                            {cat === 'All'
                                                ? archivedInventory.length
                                                : archivedInventory.filter(i => (i.category || '—') === cat).length}
                                        </span>
                                    </button>
                                ))}
                            </div>
                        </div>

                        {/* Archive Modal */}
                        <div className="overflow-y-auto flex-1">
                            {(() => {
                                const filteredArchivedInventory = archivedInventory.filter(item => {
                                    const query = archivedInventorySearch.toLowerCase()
                                    const matchesSearch =
                                        String(item.name || '').toLowerCase().includes(query) ||
                                        String(item.unit || '').toLowerCase().includes(query) ||
                                        String(item.category || '').toLowerCase().includes(query)
                                    const matchesCategory =
                                        archivedInventoryCategoryFilter === 'All' || (item.category || '—') === archivedInventoryCategoryFilter
                                    return matchesSearch && matchesCategory
                                })

                                const pagedArchivedInventory = filteredArchivedInventory.slice(
                                    (archivedInventoryPage - 1) * PAGE_SIZE,
                                    archivedInventoryPage * PAGE_SIZE
                                )

                                return (
                                    <>
                                        <table className="w-full">
                                            <thead className="bg-gray-50 border-b border-gray-200 sticky top-0">
                                                <tr>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ingredient</th>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Category</th>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Stock at Archive</th>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Reorder Level</th>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Updated</th>
                                                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-200">
                                                {filteredArchivedInventory.length === 0 ? (
                                                    <tr>
                                                        <td colSpan="6" className="px-6 py-8 text-center text-gray-500">
                                                            {archivedInventorySearch || archivedInventoryCategoryFilter !== 'All'
                                                                ? 'No archived stock items match your filters.'
                                                                : 'No archived stock items found.'}
                                                        </td>
                                                    </tr>
                                                ) : (
                                                    pagedArchivedInventory.map((item) => (
                                                        <tr key={item.id} className="hover:bg-gray-50">
                                                            <td className="px-6 py-4">
                                                                <p className="text-sm font-medium text-gray-800">{item.name}</p>
                                                                <p className="text-xs text-gray-500 mt-0.5">{item.unit}</p>
                                                            </td>
                                                            <td className="px-6 py-4 text-sm text-gray-600">{item.category}</td>
                                                            <td className="px-6 py-4 text-sm text-gray-600">
                                                                {item.current_stock} {item.unit}
                                                            </td>
                                                            <td className="px-6 py-4 text-sm text-gray-600">
                                                                {item.reorder_level} {item.unit}
                                                            </td>
                                                            <td className="px-6 py-4 text-sm text-gray-600">
                                                                {formatDateTime(item.last_updated)}
                                                            </td>
                                                            <td className="px-6 py-4">
                                                                <div className="flex items-center gap-2">
                                                                    <button
                                                                        onClick={() => handleReinstateStockItem(item)}
                                                                        className="px-3 py-1.5 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 transition-colors"
                                                                    >
                                                                        Reinstate
                                                                    </button>
                                                                    <button
                                                                        onClick={() => handleRemoveStockItem(item)}
                                                                        className="px-3 py-1.5 bg-red-100 text-red-700 rounded-lg text-sm font-medium hover:bg-red-200 transition-colors"
                                                                    >
                                                                        Remove Permanently
                                                                    </button>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    ))
                                                )}
                                            </tbody>
                                        </table>
                                        <Pagination
                                            page={archivedInventoryPage}
                                            totalItems={filteredArchivedInventory.length}
                                            pageSize={PAGE_SIZE}
                                            onPageChange={setArchivedInventoryPage}
                                        />
                                    </>
                                )
                            })()}
                        </div>

                        <div className="border-t border-gray-200 px-6 py-4 flex justify-end">
                            <button
                                onClick={() => {
                                    setShowArchivedModal(false)
                                    setArchivedInventorySearch('')
                                    setArchivedInventoryCategoryFilter('All')
                                    setArchivedInventoryPage(1)
                                }}
                                className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Staff Count Modal ──────────────────────────────────────────── */}
            {showStaffCountModal && staffCountItem && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                    <div
                        className="absolute inset-0 bg-black bg-opacity-50"
                        onClick={() => { setShowStaffCountModal(false); setStaffCountItem(null); setStaffCountResult(null) }}
                    />
                    <div className="relative bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
                        <div className="border-b border-gray-200 px-6 py-4">
                            <h2 className="text-xl font-bold text-gray-800">Stock Count</h2>
                            <p className="text-sm text-gray-500 mt-1">
                                Count <span className="font-medium text-gray-700">{staffCountItem.name}</span> and enter the quantity you physically counted
                            </p>
                        </div>

                        <div className="px-6 py-6 space-y-5">
                            {/* System record — Admin only (blind count for Baristas) */}
                            {staffRole !== 'Barista' && (
                                <div className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded-lg px-4 py-3">
                                    <span className="text-sm text-gray-600">System Record (Current Stock)</span>
                                    <span className="text-sm font-bold text-gray-800">
                                        {staffCountItem.current_stock} {staffCountItem.unit}
                                    </span>
                                </div>
                            )}

                            {/* Count type selector */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Count Type</label>
                                <div className="inline-flex rounded-lg bg-gray-100 p-1 w-full">
                                    {[
                                        { value: 'beginning', label: 'Beginning of Shift' },
                                        { value: 'end_of_shift', label: 'End of Shift' },
                                        { value: 'general', label: 'General' },
                                    ].map(opt => {
                                        const alreadySubmitted =
                                            opt.value !== 'general' &&
                                            (todayCountTypes[staffCountItem?.ingredient_id] || new Set()).has(opt.value)

                                        return (
                                            <button
                                                key={opt.value}
                                                type="button"
                                                onClick={() => !alreadySubmitted && setStaffCountType(opt.value)}
                                                disabled={alreadySubmitted}
                                                title={alreadySubmitted ? `${opt.label} already submitted today` : ''}
                                                className={`flex-1 px-3 py-2 rounded-md text-xs font-medium transition-colors relative ${alreadySubmitted
                                                    ? 'bg-gray-50 text-gray-300 cursor-not-allowed'
                                                    : staffCountType === opt.value
                                                        ? 'bg-white text-gray-800 shadow-sm'
                                                        : 'bg-transparent text-gray-600 hover:text-gray-800'
                                                    }`}
                                            >
                                                {opt.label}
                                                {alreadySubmitted && (
                                                    <span className="ml-1 text-[10px] text-gray-300">✓</span>
                                                )}
                                            </button>
                                        )
                                    })}
                                </div>
                                {(todayCountTypes[staffCountItem?.ingredient_id] || new Set()).has('beginning') &&
                                    (todayCountTypes[staffCountItem?.ingredient_id] || new Set()).has('end_of_shift') && (
                                        <p className="text-xs text-amber-600 mt-1.5">
                                            Beginning and end of shift counts already submitted today. You can still submit a general count.
                                        </p>
                                    )}
                            </div>

                            {/* Count input — unchanged */}
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Your Physical Count ({staffCountItem.unit}) <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    value={staffCountInput}
                                    onChange={(e) => { setStaffCountInput(e.target.value); setStaffCountResult(null) }}
                                    placeholder={`Enter quantity in ${staffCountItem.unit}`}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-400"
                                />
                            </div>

                            {/* Result banner */}
                            {staffCountResult && (
                                <div className={`rounded-lg px-4 py-4 border ${staffCountResult.match ? 'bg-green-50 border-green-200' : 'bg-orange-50 border-orange-200'}`}>
                                    <div className="flex items-center gap-2 mb-2">
                                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" className="text-green-600 flex-shrink-0">
                                            <path fill="currentColor" d="m9.55 18l-5.7-5.7l1.425-1.425l4.275 4.275l9.175-9.175L20.15 7.4z" />
                                        </svg>
                                        <p className="text-sm font-semibold text-green-700">
                                            Count submitted successfully!
                                        </p>
                                    </div>

                                    {/* Only Admins see the variance breakdown */}
                                    {staffRole !== 'Barista' && (
                                        <div className="grid grid-cols-3 gap-3 text-sm mt-2">
                                            <div>
                                                <p className="text-gray-500 text-xs">Your Count</p>
                                                <p className="font-semibold text-gray-800">{staffCountResult.counted} {staffCountResult.unit}</p>
                                            </div>
                                            <div>
                                                <p className="text-gray-500 text-xs">System Record</p>
                                                <p className="font-semibold text-gray-800">{staffCountResult.actual} {staffCountResult.unit}</p>
                                            </div>
                                            <div>
                                                <p className="text-gray-500 text-xs">Variance</p>
                                                <p className={`font-semibold ${staffCountResult.discrepancy === 0 ? 'text-green-700' : staffCountResult.discrepancy > 0 ? 'text-blue-700' : 'text-red-700'}`}>
                                                    {staffCountResult.discrepancy > 0 ? '+' : ''}{staffCountResult.discrepancy} {staffCountResult.unit}
                                                </p>
                                            </div>
                                        </div>
                                    )}

                                    <p className="text-xs text-gray-400 mt-2">
                                        {staffRole === 'Barista'
                                            ? 'Your count has been recorded. Thank you!'
                                            : 'Contact your Admin if there is a discrepancy.'}
                                    </p>
                                </div>
                            )}

                            {/* History */}
                            {/* {staffCountHistory.length > 0 && (
                                <div>
                                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Recent Count History</p>
                                    <div className="space-y-2 max-h-48 overflow-y-auto">
                                        {staffCountHistory.map(record => {
                                            const diff = Number(record.discrepancy || 0)
                                            return (
                                                <div key={record.staff_count_id} className="flex items-center justify-between bg-gray-50 border border-gray-100 rounded-lg px-3 py-2">
                                                    <div>
                                                        <p className="text-xs text-gray-500">
                                                            {new Date(record.created_at).toLocaleString('en-PH', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}
                                                        </p>
                                                        <p className="text-xs text-gray-600 mt-0.5">
                                                            {staffRole === 'Barista' ? 'Count submitted' : record.notes}
                                                        </p>
                                                    </div>
                                                    <div className="text-right">
                                                        <p className="text-xs text-gray-500">
                                                            Counted: <span className="font-medium text-gray-700">{record.counted_quantity}</span>
                                                        </p>
                                                        {staffRole !== 'Barista' && (
                                                            <p className={`text-xs font-semibold ${diff === 0 ? 'text-green-600' : diff > 0 ? 'text-blue-600' : 'text-red-600'}`}>
                                                                {diff > 0 ? '+' : ''}{diff}
                                                            </p>
                                                        )}
                                                    </div>
                                                </div>
                                            )
                                        })}
                                    </div>
                                </div>
                            )} */}
                        </div>

                        <div className="border-t border-gray-200 px-6 py-4 flex justify-end gap-3">
                            <button
                                onClick={() => { setShowStaffCountModal(false); setStaffCountItem(null); setStaffCountResult(null) }}
                                className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300"
                            >
                                Close
                            </button>
                            <button
                                onClick={handleSubmitStaffCount}
                                disabled={isSubmittingCount || staffCountInput === '' || !!staffCountResult}
                                className="px-6 py-2 bg-[#A0826D] text-white rounded-lg font-medium hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {isSubmittingCount ? 'Saving…' : staffCountResult ? 'Count Submitted' : 'Submit Count'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Stock Count Records Modal ── */}
            {showStockCountRecordsModal && stockCountRecordsItem && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                    <div
                        className="absolute inset-0 bg-black bg-opacity-50"
                        onClick={() => { setShowStockCountRecordsModal(false); setStockCountRecordsItem(null) }}
                    />
                    <div className="relative bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[85vh] flex flex-col">
                        <div className="border-b border-gray-200 px-6 py-4">
                            <h2 className="text-xl font-bold text-gray-800">Count Records</h2>
                            <p className="text-sm text-gray-500 mt-1">
                                <span className="font-medium text-gray-700">{stockCountRecordsItem.name}</span>
                                {' · '}{stockCountReviewDate}
                            </p>
                        </div>

                        <div className="overflow-y-auto flex-1">
                            {stockCountRecordsLoading ? (
                                <div className="flex items-center justify-center py-12">
                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-400 mb-3" />
                                    <p className="text-sm text-gray-500 ml-3">Loading records...</p>
                                </div>
                            ) : stockCountRecords.length === 0 ? (
                                <div className="px-6 py-12 text-center text-gray-500 text-sm">
                                    No count records found for this ingredient on this date.
                                </div>
                            ) : (
                                <table className="w-full">
                                    <thead className="bg-gray-50 border-b border-gray-200 sticky top-0">
                                        <tr>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Time</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Count Type</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Counted By</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Counted Qty</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">System Qty</th>
                                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Variance</th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-100 bg-white">
                                        {stockCountRecords.map(record => {
                                            const variance = Number(record.discrepancy || 0)
                                            const isMatch = variance === 0
                                            const isOver = variance > 0

                                            const countTypeLabel =
                                                record.count_type === 'beginning' ? 'Beginning of Shift' :
                                                    record.count_type === 'end_of_shift' ? 'End of Shift' : 'General'

                                            const countTypeBadge =
                                                record.count_type === 'beginning' ? 'bg-blue-100 text-blue-700' :
                                                    record.count_type === 'end_of_shift' ? 'bg-orange-100 text-orange-700' :
                                                        'bg-gray-100 text-gray-600'

                                            return (
                                                <tr key={record.staff_count_id} className="hover:bg-gray-50">
                                                    <td className="px-6 py-3 text-sm text-gray-600 whitespace-nowrap">
                                                        {new Date(record.created_at).toLocaleString('en-PH', {
                                                            hour: 'numeric', minute: '2-digit'
                                                        })}
                                                    </td>
                                                    <td className="px-6 py-3">
                                                        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${countTypeBadge}`}>
                                                            {countTypeLabel}
                                                        </span>
                                                    </td>
                                                    <td className="px-6 py-3 text-sm text-gray-700">{record.counted_by}</td>
                                                    <td className="px-6 py-3">
                                                        <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-purple-50 text-purple-700 text-sm font-medium">
                                                            {record.counted_quantity} {stockCountRecordsItem.unit}
                                                        </span>
                                                    </td>
                                                    <td className="px-6 py-3">
                                                        <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-blue-50 text-blue-700 text-sm font-medium">
                                                            {record.actual_quantity} {stockCountRecordsItem.unit}
                                                        </span>
                                                    </td>
                                                    <td className="px-6 py-3">
                                                        <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-sm font-semibold ${isMatch ? 'bg-green-100 text-green-800' :
                                                            isOver ? 'bg-blue-100 text-blue-800' :
                                                                'bg-red-100 text-red-800'
                                                            }`}>
                                                            {variance > 0 ? '+' : ''}{variance} {stockCountRecordsItem.unit}
                                                        </span>
                                                    </td>
                                                </tr>
                                            )
                                        })}
                                    </tbody>
                                </table>
                            )}
                        </div>

                        <div className="border-t border-gray-200 px-6 py-4 flex justify-end">
                            <button
                                onClick={() => { setShowStockCountRecordsModal(false); setStockCountRecordsItem(null) }}
                                className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Adjustment Request Modal (Barista) ── */}
            {showAdjustmentRequestModal && adjustmentRequestItem && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                    <div
                        className="absolute inset-0 bg-black bg-opacity-50"
                        onClick={() => { setShowAdjustmentRequestModal(false); setAdjustmentRequestItem(null); setEditingAdjustmentRequestId(null) }}
                    />
                    <div className="relative bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] flex flex-col">
                        <div className="border-b border-gray-200 px-6 py-4 flex-shrink-0">
                            <h2 className="text-xl font-bold text-gray-800">
                                {editingAdjustmentRequestId ? 'Edit Stock Adjustment Request' : 'Request Stock Adjustment'}
                            </h2>
                            <p className="text-sm text-gray-500 mt-1">
                                <span className="font-medium text-gray-700">{adjustmentRequestItem.name}</span>
                                {editingAdjustmentRequestId ? ' — editing your pending request' : ' — submit for admin approval'}
                            </p>
                        </div>

                        <div className="px-6 py-6 space-y-4 overflow-y-auto flex-1">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Adjustment Type</label>
                                <div className="inline-flex rounded-lg bg-gray-100 p-1 w-full">
                                    {[{ value: 'deduct', label: '− Deduct Stock' }, { value: 'add', label: '+ Add Stock' }].map(opt => (
                                        <button
                                            key={opt.value}
                                            type="button"
                                            onClick={() => setAdjustmentRequestForm(prev => ({
                                                ...prev,
                                                adjustment_type: opt.value,
                                                reason: opt.value === 'deduct' ? 'spillage' : 'recount'  // ← reset reason on type change
                                            }))}
                                            className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-colors ${adjustmentRequestForm.adjustment_type === opt.value ? 'bg-[#A0826D] text-white shadow-sm' : 'bg-transparent text-gray-600 hover:text-gray-800'}`}
                                        >
                                            {opt.label}
                                        </button>
                                    ))}
                                </div>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">Input Method</label>
                                <div className="inline-flex mb-3 rounded-lg bg-gray-100 p-1 w-full">
                                    <button
                                        type="button"
                                        onClick={() => setAdjustmentRequestForm(prev => ({ ...prev, input_mode: 'manual', amount_per_package: '', package_count: '' }))}
                                        className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-colors ${adjustmentRequestForm.input_mode === 'manual' ? 'bg-[#A0826D] text-white shadow-sm' : 'bg-transparent text-gray-600 hover:text-gray-800'}`}
                                    >
                                        Manual Quantity
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setAdjustmentRequestForm(prev => ({ ...prev, input_mode: 'package', quantity: '' }))}
                                        className={`flex-1 px-4 py-2 rounded-md text-sm font-medium transition-colors ${adjustmentRequestForm.input_mode === 'package' ? 'bg-[#A0826D] text-white shadow-sm' : 'bg-transparent text-gray-600 hover:text-gray-800'}`}
                                    >
                                        Package Count
                                    </button>
                                </div>

                                {adjustmentRequestForm.input_mode === 'manual' && (
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Quantity ({adjustmentRequestItem.unit}) <span className="text-red-500">*</span>
                                        </label>
                                        <input
                                            type="number" step="0.01" min="0.01"
                                            value={adjustmentRequestForm.quantity}
                                            onChange={(e) => setAdjustmentRequestForm(prev => ({ ...prev, quantity: e.target.value }))}
                                            placeholder={`Enter quantity in ${adjustmentRequestItem.unit}`}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#A0826D]"
                                        />
                                    </div>
                                )}

                                {adjustmentRequestForm.input_mode === 'package' && (
                                    <div className="space-y-3">
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Amount per Package ({adjustmentRequestItem.unit}) <span className="text-red-500">*</span>
                                            </label>
                                            <input
                                                type="number" step="0.01" min="0"
                                                value={adjustmentRequestForm.amount_per_package}
                                                onChange={(e) => setAdjustmentRequestForm(prev => ({ ...prev, amount_per_package: e.target.value }))}
                                                placeholder={`e.g. how many ${adjustmentRequestItem.unit} in one package`}
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#A0826D]"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                                Package Count <span className="text-red-500">*</span>
                                            </label>
                                            <input
                                                type="number" step="1" min="0"
                                                value={adjustmentRequestForm.package_count}
                                                onChange={(e) => setAdjustmentRequestForm(prev => ({ ...prev, package_count: e.target.value }))}
                                                placeholder="Enter number of packages"
                                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#A0826D]"
                                            />
                                        </div>
                                        {parseFloat(adjustmentRequestForm.amount_per_package || 0) > 0 && parseFloat(adjustmentRequestForm.package_count || 0) > 0 && (
                                            <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3">
                                                <p className="text-xs text-blue-600 mb-0.5">Computed Total</p>
                                                <p className="text-sm font-semibold text-blue-800">
                                                    {parseFloat(adjustmentRequestForm.amount_per_package)} × {parseFloat(adjustmentRequestForm.package_count)} = {(parseFloat(adjustmentRequestForm.amount_per_package) * parseFloat(adjustmentRequestForm.package_count)).toLocaleString()} {adjustmentRequestItem.unit}
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Reason <span className="text-red-500">*</span></label>
                                <select
                                    value={adjustmentRequestForm.reason}
                                    onChange={(e) => setAdjustmentRequestForm(prev => ({ ...prev, reason: e.target.value }))}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#A0826D]"
                                >
                                    {adjustmentRequestForm.adjustment_type === 'deduct' ? (
                                        <>
                                            <option value="spillage">Spillage</option>
                                            <option value="spoilage">Spoilage</option>
                                            <option value="damaged">Damaged</option>
                                            <option value="theft">Theft</option>
                                            <option value="recount">Recount / Correction</option>
                                            <option value="other">Other</option>
                                        </>
                                    ) : (
                                        <>
                                            <option value="recount">Recount / Correction</option>
                                            <option value="restock">Restock / Delivery</option>
                                            <option value="transfer">Branch Transfer</option>
                                            <option value="other">Other</option>
                                        </>
                                    )}
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Additional Notes</label>
                                <textarea
                                    value={adjustmentRequestForm.notes}
                                    onChange={(e) => {
                                        if (e.target.value.length <= 300) {
                                            setAdjustmentRequestForm(prev => ({ ...prev, notes: e.target.value }))
                                        }
                                    }}
                                    placeholder="Optional details..."
                                    rows={3}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#A0826D] resize-none"
                                />
                                <p className={`text-xs mt-1 text-right ${(adjustmentRequestForm.notes?.length || 0) >= 280 ? 'text-orange-500' : 'text-gray-400'}`}>
                                    {adjustmentRequestForm.notes?.length || 0} / 300
                                </p>
                            </div>
                        </div>

                        <div className="border-t border-gray-200 px-6 py-4 flex justify-end gap-3 flex-shrink-0">
                            <button
                                onClick={() => { setShowAdjustmentRequestModal(false); setAdjustmentRequestItem(null); setEditingAdjustmentRequestId(null) }}
                                className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300"
                            >
                                Cancel
                            </button>
                            {(() => {
                                const isPackageInvalid = adjustmentRequestForm.input_mode === 'package' &&
                                    !(parseFloat(adjustmentRequestForm.amount_per_package || 0) > 0 && parseFloat(adjustmentRequestForm.package_count || 0) > 0)
                                const isManualInvalid = adjustmentRequestForm.input_mode === 'manual' && !adjustmentRequestForm.quantity
                                const isDisabled = isSubmittingAdjustmentRequest || isPackageInvalid || isManualInvalid

                                return (
                                    <button
                                        onClick={handleSubmitAdjustmentRequest}
                                        disabled={isDisabled}
                                        className={`px-6 py-2 rounded-lg font-medium ${isDisabled ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-[#A0826D] text-white hover:bg-[#8c705b]'}`}
                                    >
                                        {isSubmittingAdjustmentRequest
                                            ? (editingAdjustmentRequestId ? 'Updating…' : 'Submitting…')
                                            : (editingAdjustmentRequestId ? 'Update Request' : 'Submit Request')}
                                    </button>
                                )
                            })()}
                        </div>
                    </div>
                </div>
            )}

            {/* ── EOD Report Schedule Modal ──────────────────────────────────── */}
            {showEODModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
                    <div
                        className="absolute inset-0 bg-black bg-opacity-50"
                        onClick={() => setShowEODModal(false)}
                    />
                    <div className="relative bg-white rounded-lg shadow-xl max-w-sm w-full">
                        <div className="border-b border-gray-200 px-6 py-4">
                            <h2 className="text-xl font-bold text-gray-800">Schedule EOD Report</h2>
                            <p className="text-sm text-gray-500 mt-1">
                                Choose what time to receive the daily inventory summary email for <span className="font-medium text-gray-700">{branchName}</span>
                            </p>
                        </div>
                        <div className="px-6 py-6 space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                    Send Time <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="time"
                                    value={eodTime}
                                    onChange={(e) => setEodTime(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#A0826D] text-gray-800"
                                />
                                <p className="text-xs text-gray-500 mt-1">
                                    The report will be sent daily at this time to your registered email.
                                </p>
                            </div>
                            {eodTime && (
                                <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-800">
                                    Report will be sent every day at <span className="font-semibold">{eodTime}</span>
                                </div>
                            )}
                        </div>
                        <div className="border-t border-gray-200 px-6 py-4 flex justify-end gap-3">
                            <button
                                onClick={() => setShowEODModal(false)}
                                className="px-6 py-2 bg-gray-200 text-gray-700 rounded-lg font-medium hover:bg-gray-300"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleSaveEODTime}
                                disabled={!eodTime || isSavingEOD}
                                className={`px-6 py-2 rounded-lg font-medium ${!eodTime || isSavingEOD ? 'bg-gray-300 text-gray-500 cursor-not-allowed' : 'bg-[#A0826D] text-white hover:bg-[#8c705b]'}`}
                            >
                                {isSavingEOD ? 'Saving…' : 'Save Schedule'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

        </div>
    )
}